import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Eye, ChevronDown, ChevronRight, Edit2, Save, X, Send, User, Lock, Trash2, History, FileCheck, CheckCircle, Calendar, ArrowRight, ArrowDown, FilePlus, UserCheck, PenTool, Printer, CornerUpLeft, BookOpen, Upload, Paperclip, ChevronUp, ArrowLeft, Download, ChevronLeft, Clock, Play, Search, UserPlus, AlertTriangle, RefreshCw, Sparkles } from 'lucide-react';
import { numberToWordsPLN } from '../utils/numberToWords';
import {
  fetchUnsignedAttachmentNames,
  fetchBlockingAttachments,
  unsignedAttachmentsToast,
  showUnsignedAttachmentBlockingToast,
  allowsForwardingWithUnsignedAttachments,
  requiresAcceptBeforeAttachmentWaiver,
  isAttachmentSigned,
  canSignAttachment,
  isAttachmentSigningRequired,
  isAttachmentSigningRequiredForUser,
  filterActiveSignatures,
} from '../utils/attachmentSigning';
import api from '../services/api';
import { promisePool } from '../utils/promisePool';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import DocumentSignModal from '../components/DocumentSignModal';
import AttachmentReplaceModal from '../components/AttachmentReplaceModal';
import DocumentSignatureInfo from '../components/DocumentSignatureInfo';
import DictionaryManager from '../components/DictionaryManager';
import ConfirmModal from '../components/ConfirmModal';
import DotacjaDisplay from '../components/DotacjaDisplay';
import InvoiceStatusCell from '../components/InvoiceStatusCell';
import AttachmentSignWaiverModal from '../components/AttachmentSignWaiverModal';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, UI_THEMES } from '../contexts/ThemeContext';

const InvoicesByStatus = () => {
  const { status } = useParams();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isModern = theme === UI_THEMES.MODERN;
  
  // Debug: Sprawdź uprawnienia użytkownika
  useEffect(() => {
    console.log('🔍 [DEBUG] InvoicesByStatus - useEffect user:', {
      user: user,
      hasUser: !!user,
      canUseCostCenter: user?.canUseCostCenter,
      userId: user?.id,
      userEmail: user?.email
    });
    
    if (user) {
      console.log('👤 [DEBUG] Użytkownik w kontekście InvoicesByStatus:', {
        id: user.id,
        email: user.email,
        canUseCostCenter: user.canUseCostCenter,
        allPermissions: {
          canUseCostCenter: user.canUseCostCenter,
          canCheckMerit: user.canCheckMerit,
          canCheckFormalAccounting: user.canCheckFormalAccounting,
          canDoPreliminaryCheck: user.canDoPreliminaryCheck
        },
        fullUserObject: user
      });
      
      if (!user.canUseCostCenter) {
        console.warn('⚠️ [DEBUG] Użytkownik NIE MA uprawnienia canUseCostCenter! Wyloguj się i zaloguj ponownie, aby odświeżyć uprawnienia.');
      } else {
        console.log('✅ [DEBUG] Użytkownik MA uprawnienie canUseCostCenter');
      }
    } else {
      console.warn('⚠️ [DEBUG] Brak użytkownika w kontekście InvoicesByStatus!');
    }
  }, [user]);
  const [allInvoices, setAllInvoices] = useState([]); // Wszystkie faktury dla danego statusu (bez paginacji)
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const PAGE_SIZE = 20; // 20 faktur na stronę – paginacja serwerowa (dla statusów: in-progress, pending, done, signed)
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  // Paginacja serwerowa dla wszystkich statusów – pobieranie "wszystkiego" (size=10000) powoduje time-outy i muli UI.
  const useServerPagination = true;
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());
  const [invoiceHistory, setInvoiceHistory] = useState({});
  const [expandedHistory, setExpandedHistory] = useState(new Set()); // Stan do śledzenia rozwiniętych historii
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [editForm, setEditForm] = useState({
    description: '',
    signedByPosition: '',
    signatureImage: null,
    metricalChecked: false,
    preliminaryCheckDone: false,
    formalnoRachunkowoChecked: false,
    zatwierdzonoDo: '',
    kwota: '',
    costCenter: '',
    invoicePaymentType: 'TRANSFER', // CASH=gotówkowa, TRANSFER=przelew (wymagane przy canCheckMerit) - domyślnie Przelew
    isDotacja: false,
    srodkiPowiat: '',
    srodkiSpZoz: ''
  });
  const [isDescriptionCollapsed, setIsDescriptionCollapsed] = useState(false);
  const [isSignaturesCollapsed, setIsSignaturesCollapsed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [substitutionInfo, setSubstitutionInfo] = useState(null);
  const [activeSubstitutions, setActiveSubstitutions] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [invoiceSignatures, setInvoiceSignatures] = useState({});
  const [signingInvoiceId, setSigningInvoiceId] = useState(null);
  const [signingAttachmentId, setSigningAttachmentId] = useState(null);
  // Wskazanie osoby podpisującej załącznik przy przekazywaniu faktury dalej
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [waiverAttachments, setWaiverAttachments] = useState([]);
  const [waiverInvoice, setWaiverInvoice] = useState(null);
  const [invoiceDescriptions, setInvoiceDescriptions] = useState({});
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showDictionaryManager, setShowDictionaryManager] = useState(false);
  const [showDescriptionsModal, setShowDescriptionsModal] = useState(false);
  const [modalInvoiceId, setModalInvoiceId] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [showA4PreviewModal, setShowA4PreviewModal] = useState(false);
  const [a4PreviewInvoiceId, setA4PreviewInvoiceId] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertInvoice, setRevertInvoice] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', confirmLabel: 'Usuń', pendingAction: null });
  const [returnReason, setReturnReason] = useState('');
  const [returnInvoice, setReturnInvoice] = useState(null);
  const [returnToUserId, setReturnToUserId] = useState('');
  const [returnUserSearchQuery, setReturnUserSearchQuery] = useState('');
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceAttachment, setReplaceAttachment] = useState(null);
  const [replaceInvoiceId, setReplaceInvoiceId] = useState(null);
  const [replaceDefaultSignerId, setReplaceDefaultSignerId] = useState(null);
  const [invoicePreviewHtml, setInvoicePreviewHtml] = useState('');
  const [invoicePreviewUrl, setInvoicePreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [invoiceAttachments, setInvoiceAttachments] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [selectedAttachmentPreview, setSelectedAttachmentPreview] = useState(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState(null);
  const [loadingAttachmentPreview, setLoadingAttachmentPreview] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [isDraggingAttachment, setIsDraggingAttachment] = useState(false);
  const [editingDueDate, setEditingDueDate] = useState(null);
  const [editingDueDateValue, setEditingDueDateValue] = useState('');
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  // Wielokrotny wybór we wszystkich statusach (checkbox + klik wiersza)
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [selectingAllInvoices, setSelectingAllInvoices] = useState(false);

  const [assignBatchIds, setAssignBatchIds] = useState([]); // id faktur do przekazania w trybie zbiorczym
  const [batchSignQueue, setBatchSignQueue] = useState([]); // kolejka do masowego podpisywania
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc'); // desc = najnowsze na górze, asc = najstarsze na górze

  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  };
  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  };
  const [dateFromFilter, setDateFromFilter] = useState(getFirstDayOfMonth());
  const [dateToFilter, setDateToFilter] = useState(getLastDayOfMonth());
  
  const statusLabels = {
    'in-progress': 'W realizacji',
    'pending': 'Oczekujące',
    'assigned': 'Przekazane',
    'completed': 'Zakończone',
    'done': 'Zaksięgowane',
    'signed': 'Podpisane'
  };

  const statusMap = {
    'in-progress': 'IN_PROGRESS',
    'pending': 'PENDING',
    'assigned': 'ASSIGNED',
    'completed': 'IN_PROGRESS', // API: completedByMe = udział w obiegu (historia / opis / przekazanie+przyjęcie)
    'done': 'DONE',
    'signed': 'SIGNED'
  };

  // Debounce wyszukiwania – 400ms po ostatnim znaku
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(0);
    setSearchQuery('');
    setSelectedInvoiceIds([]);
    setDateFromFilter(getFirstDayOfMonth());
    setDateToFilter(getLastDayOfMonth());
    if (status === 'in-progress') {
      fetchUsers();
    }
    const interval = setInterval(() => fetchInvoicesSilent(), 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const prevDebouncedSearchRef = React.useRef('');
  // Refy z aktualnymi wartościami – unikamy stale closure w interwale fetchInvoicesSilent (co 10s)
  const currentPageRef = useRef(currentPage);
  const debouncedSearchQueryRef = useRef(debouncedSearchQuery);
  const sortFieldRef = useRef(sortField);
  const sortDirectionRef = useRef(sortDirection);
  const dateFromFilterRef = useRef(dateFromFilter);
  const dateToFilterRef = useRef(dateToFilter);
  currentPageRef.current = currentPage;
  debouncedSearchQueryRef.current = debouncedSearchQuery;
  sortFieldRef.current = sortField;
  sortDirectionRef.current = sortDirection;
  dateFromFilterRef.current = dateFromFilter;
  dateToFilterRef.current = dateToFilter;

  useEffect(() => {
    if (!useServerPagination || !status) return;
    const searchChanged = prevDebouncedSearchRef.current !== debouncedSearchQuery;
    if (searchChanged) {
      prevDebouncedSearchRef.current = debouncedSearchQuery;
      setCurrentPage(0);
    }
    fetchInvoices(searchChanged ? 0 : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useServerPagination, status, currentPage, debouncedSearchQuery, sortField, sortDirection, dateFromFilter, dateToFilter]);

  // Dla assigned/completed (paginacja kliencka) – pobieranie z API z filtrami dat (jak Lista faktur)
  useEffect(() => {
    if (useServerPagination || !status) return;
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, dateFromFilter, dateToFilter, sortField, sortDirection]);

  // Aktualizuj paginację gdy zmieni się wyszukiwanie lub faktury (tylko dla assigned/completed – paginacja kliencka)
  useEffect(() => {
    if (useServerPagination) return; // totalPages/totalElements ustawiane w fetchInvoices
    const filtered = allInvoices.filter(invoice => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase().trim();
      return (
        (invoice.supplierNip && invoice.supplierNip.toLowerCase().includes(query)) ||
        (invoice.supplierName && invoice.supplierName.toLowerCase().includes(query)) ||
        (invoice.amountGross && invoice.amountGross.toString().includes(query)) ||
        (invoice.amountNet && invoice.amountNet.toString().includes(query)) ||
        (invoice.number && invoice.number.toLowerCase().includes(query)) ||
        (invoice.internalNumber && invoice.internalNumber.toLowerCase().includes(query))
      );
    });
    const totalFiltered = filtered.length;
    const totalPagesFiltered = Math.ceil(totalFiltered / PAGE_SIZE);
    setTotalPages(totalPagesFiltered);
    setTotalElements(totalFiltered);
    if (currentPage >= totalPagesFiltered && totalPagesFiltered > 0) {
      setCurrentPage(0);
    }
  }, [allInvoices, searchQuery, currentPage, useServerPagination]);

  // Pobierz podpisy dla faktur (tylko wyświetlanych – przy paginacji serwerowej = strona, przy klienckiej = slice)
  useEffect(() => {
    let cancelled = false;
    const toFetch = useServerPagination
      ? allInvoices
      : allInvoices
          .filter(inv => {
            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase().trim();
            return (
              (inv.supplierNip && inv.supplierNip.toLowerCase().includes(q)) ||
              (inv.supplierName && inv.supplierName.toLowerCase().includes(q)) ||
              (inv.amountGross && inv.amountGross.toString().includes(q)) ||
              (inv.amountNet && inv.amountNet.toString().includes(q)) ||
              (inv.number && inv.number.toLowerCase().includes(q)) ||
              (inv.internalNumber && inv.internalNumber.toLowerCase().includes(q))
            );
          })
          .slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);
    const fetchSignatures = async () => {
      const SIGNATURES_TIMEOUT = 10000; // 10 s – fail fast, nie blokuj UI
      const results = await promisePool(
        toFetch,
        async (invoice) => {
          try {
            const r = await api.get(`/documents/invoices/${invoice.id}/signatures`, { timeout: SIGNATURES_TIMEOUT });
            return { ok: true, invoiceId: invoice.id, data: r?.data };
          } catch (e) {
            return { ok: false, invoiceId: invoice.id, error: e };
          }
        },
        4
      );
      if (cancelled) return;
      const updates = {};
      results.forEach((res) => {
        const invoiceId = res?.invoiceId;
        if (!invoiceId) return;
        if (res.ok && res?.data) {
          updates[invoiceId] = Array.isArray(res.data) ? res.data : [];
        } else {
          if (!res.ok) {
            const msg = res.error?.message || res.error;
            const isAborted = res.error?.code === 'ECONNABORTED' || msg === 'Request aborted' || msg === 'canceled';
            if (!isAborted) {
              console.warn(`⚠️ Błąd pobierania podpisów dla faktury ${invoiceId}:`, msg);
            }
          }
          updates[invoiceId] = [];
        }
      });
      if (!cancelled) setInvoiceSignatures(prev => ({ ...prev, ...updates }));
    };
    if (toFetch.length > 0) {
      fetchSignatures();
    }
    return () => { cancelled = true; };
  }, [allInvoices, useServerPagination, currentPage, searchQuery]);

  // Pobierz historię dla faktur w statusie IN_PROGRESS (tylko wyświetlanych)
  useEffect(() => {
    const displayed = useServerPagination
      ? allInvoices
      : allInvoices.filter(inv => {
          if (!searchQuery) return true;
          const q = searchQuery.toLowerCase().trim();
          return (
            (inv.supplierNip && inv.supplierNip.toLowerCase().includes(q)) ||
            (inv.supplierName && inv.supplierName.toLowerCase().includes(q)) ||
            (inv.amountGross && inv.amountGross.toString().includes(q)) ||
            (inv.amountNet && inv.amountNet.toString().includes(q)) ||
            (inv.number && inv.number.toLowerCase().includes(q)) ||
            (inv.internalNumber && inv.internalNumber.toLowerCase().includes(q))
          );
        }).slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);
    const fetchHistoriesForInProgress = async () => {
      const inProgressInvoices = displayed.filter(inv => inv.status === 'IN_PROGRESS');
      console.log('Pobieranie historii dla faktur w realizacji:', inProgressInvoices.length);
      
      const toLoad = inProgressInvoices.filter(invoice => !invoiceHistory[invoice.id]);
      await promisePool(
        toLoad,
        async (invoice) => {
          console.log('Pobieranie historii dla faktury:', invoice.id, invoice.number);
          try {
            await fetchInvoiceHistory(invoice.id);
          } catch (error) {
            const isAborted = error?.code === 'ECONNABORTED' || error?.message === 'Request aborted' || error?.message === 'canceled';
            if (!isAborted) console.log('Nie udało się pobrać historii dla faktury:', invoice.id, error);
          }
        },
        4
      );
      console.log('Zakończono pobieranie historii dla faktur w realizacji');
    };
    
    if (displayed.length > 0 && status === 'in-progress') {
      fetchHistoriesForInProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allInvoices, status, useServerPagination, currentPage, searchQuery]);

  // Pobierz opisy (tylko dla wyświetlanych faktur)
  useEffect(() => {
    const toFetch = useServerPagination
      ? allInvoices
      : allInvoices.filter(inv => {
          if (!searchQuery) return true;
          const q = searchQuery.toLowerCase().trim();
          return (
            (inv.supplierNip && inv.supplierNip.toLowerCase().includes(q)) ||
            (inv.supplierName && inv.supplierName.toLowerCase().includes(q)) ||
            (inv.amountGross && inv.amountGross.toString().includes(q)) ||
            (inv.amountNet && inv.amountNet.toString().includes(q)) ||
            (inv.number && inv.number.toLowerCase().includes(q)) ||
            (inv.internalNumber && inv.internalNumber.toLowerCase().includes(q))
          );
        }).slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);
    const fetchDescriptions = async () => {
      for (const invoice of toFetch) {
        if (!invoiceDescriptions[invoice.id]) {
          try {
            const response = await api.get(`/invoices/${invoice.id}/descriptions`);
            if (response.data && Array.isArray(response.data)) {
              // Zawsze zapisz opisy, nawet jeśli tablica jest pusta (aby pokazać przycisk jeśli są opisy)
              setInvoiceDescriptions(prev => ({
                ...prev,
                [invoice.id]: response.data
              }));
            } else {
              // Ustaw pustą tablicę, żeby nie próbować ponownie
              setInvoiceDescriptions(prev => ({
                ...prev,
                [invoice.id]: []
              }));
            }
          } catch (error) {
            const isAborted = error?.code === 'ECONNABORTED' || error?.message === 'Request aborted' || error?.message === 'canceled';
            if (isAborted) {
              // normalne przy przełączaniu zakładek / unmount
              continue;
            }
            // Ignoruj błędy 404 i 403 - faktura może nie mieć opisów lub użytkownik nie ma dostępu
            if (error.response?.status === 404 || error.response?.status === 403) {
              // Ustaw pustą tablicę, żeby nie próbować ponownie
              setInvoiceDescriptions(prev => ({
                ...prev,
                [invoice.id]: []
              }));
            } else {
              // Dla innych błędów loguj, ale nie blokuj
              console.log('Błąd podczas pobierania opisów dla faktury:', invoice.id, error.response?.status);
            }
          }
        }
      }
    };
    
    if (toFetch.length > 0) {
      fetchDescriptions();
    }
  }, [allInvoices, useServerPagination, currentPage, searchQuery]);

  // Pobierz załączniki – tylko dla wyświetlanych faktur (przy paginacji serwerowej = strona, przy klienckiej = slice)
  useEffect(() => {
    const toFetch = useServerPagination
      ? allInvoices
      : allInvoices
          .filter(inv => {
            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase().trim();
            return (
              (inv.supplierNip && inv.supplierNip.toLowerCase().includes(q)) ||
              (inv.supplierName && inv.supplierName.toLowerCase().includes(q)) ||
              (inv.amountGross && inv.amountGross.toString().includes(q)) ||
              (inv.amountNet && inv.amountNet.toString().includes(q)) ||
              (inv.number && inv.number.toLowerCase().includes(q)) ||
              (inv.internalNumber && inv.internalNumber.toLowerCase().includes(q))
            );
          })
          .slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);
    if (!toFetch.length) return;
    const fetchAttachments = async () => {
      const results = await promisePool(
        toFetch,
        async (inv) => {
          try {
            const r = await api.get(`/invoices/${inv.id}/attachments`, { timeout: 10000 });
            return { invoiceId: inv.id, attachments: r.data || [] };
          } catch (e) {
            const isAborted = e?.code === 'ECONNABORTED' || e?.message === 'Request aborted' || e?.message === 'canceled';
            if (!isAborted && e.response?.status !== 404 && e.response?.status !== 403) {
              console.error('Error fetching attachments for invoice', inv.id, e);
            }
            return { invoiceId: inv.id, attachments: [] };
          }
        },
        4
      );
      const map = {};
      results.forEach(({ invoiceId, attachments }) => { map[invoiceId] = attachments; });
      setInvoiceAttachments(prev => ({ ...prev, ...map }));
    };
    fetchAttachments();
  }, [allInvoices, useServerPagination, currentPage, searchQuery]);

  // Odśwież załączniki gdy modal się otwiera
  useEffect(() => {
    if (showDescriptionModal && selectedInvoice) {
      const fetchAttachments = async () => {
        try {
          const attachmentsResponse = await api.get(`/invoices/${selectedInvoice.id}/attachments`);
          const allAttachments = attachmentsResponse.data || [];
          console.log('🔄 [InvoicesByStatus useEffect] Odświeżono załączniki dla faktury:', selectedInvoice.id, 'Liczba:', allAttachments.length, allAttachments);
          setAttachments(allAttachments);
          setInvoiceAttachments(prev => ({
            ...prev,
            [selectedInvoice.id]: allAttachments
          }));
        } catch (error) {
          console.error('❌ [InvoicesByStatus useEffect] Błąd podczas odświeżania załączników:', error);
        }
      };
      fetchAttachments();
    }
  }, [showDescriptionModal, selectedInvoice?.id]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/assignment/list');
      setUsers(response.data || []);
    } catch (error) {
      const isAborted = error?.code === 'ECONNABORTED' || error?.message === 'Request aborted' || error?.message === 'canceled';
      if (!isAborted) console.error('Error fetching users:', error);
    }
  };

  const fetchInvoices = async (pageOverride = null) => {
    try {
      setLoading(true);
      const apiStatus = statusMap[status];
      const pageToUse = pageOverride !== null ? pageOverride : currentPage;

      const params = {
        status: apiStatus,
        sort: sortField,
        direction: sortDirection
      };

      params.page = pageToUse;
      params.size = PAGE_SIZE;
      if (debouncedSearchQuery?.trim()) params.q = debouncedSearchQuery.trim();

      if (dateFromFilter) params.dateFrom = dateFromFilter;
      if (dateToFilter) params.dateTo = dateToFilter;

      if (status === 'assigned') {
        params.assignedByMe = true;
      } else if (status === 'completed') {
        params.completedByMe = true;
      }

      const response = await api.get('/invoices', { params });
      const invoicesData = response.data.content || response.data;
      setAllInvoices(invoicesData);

      if (response.data.totalElements !== undefined) {
        setTotalElements(response.data.totalElements);
      }
      if (response.data.totalPages !== undefined) {
        setTotalPages(response.data.totalPages);
      } else if (useServerPagination) {
        setTotalPages(Math.ceil((response.data.totalElements || 0) / PAGE_SIZE));
      }

      if (pageOverride !== null) {
        setCurrentPage(pageOverride);
      }
    } catch (error) {
      toast.error('Błąd podczas pobierania faktur');
      const isAborted = error?.code === 'ECONNABORTED' || error?.message === 'Request aborted' || error?.message === 'canceled';
      if (!isAborted) console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Ciche odświeżanie bez migania ekranu (używa refów – aktualna strona i wyszukiwanie)
  const fetchInvoicesSilent = async () => {
    try {
      const apiStatus = statusMap[status];
      const params = {
        status: apiStatus,
        sort: sortFieldRef.current,
        direction: sortDirectionRef.current
      };
      params.page = currentPageRef.current;
      params.size = PAGE_SIZE;
      if (debouncedSearchQueryRef.current?.trim()) params.q = debouncedSearchQueryRef.current.trim();
      if (dateFromFilterRef.current) params.dateFrom = dateFromFilterRef.current;
      if (dateToFilterRef.current) params.dateTo = dateToFilterRef.current;
      if (status === 'assigned') params.assignedByMe = true;
      else if (status === 'completed') params.completedByMe = true;

      const response = await api.get('/invoices', {
        params,
        timeout: 10000
      });
      const newInvoices = response.data.content || response.data;
      
      if (response.data.totalElements !== undefined) {
        setTotalElements(response.data.totalElements);
      }
      if (useServerPagination && response.data.totalPages !== undefined) {
        setTotalPages(response.data.totalPages);
      }
      
      // Aktualizuj tylko jeśli dane się zmieniły (porównaj po ID i statusie)
      setAllInvoices(prevInvoices => {
        const prevIds = new Set(prevInvoices.map(inv => inv.id));
        const newIds = new Set(newInvoices.map(inv => inv.id));
        
        // Jeśli lista się zmieniła, zaktualizuj
        if (prevIds.size !== newIds.size || 
            ![...prevIds].every(id => newIds.has(id)) ||
            ![...newIds].every(id => prevIds.has(id))) {
          return newInvoices;
        }
        
        // Sprawdź czy statusy lub przypisania się zmieniły
        const hasChanges = prevInvoices.some(prevInv => {
          const newInv = newInvoices.find(ni => ni.id === prevInv.id);
          return !newInv || 
                 newInv.status !== prevInv.status ||
                 newInv.assignedUserId !== prevInv.assignedUserId;
        });
        
        return hasChanges ? newInvoices : prevInvoices;
      });
    } catch (error) {
      // Nie loguj błędów "Request aborted" - to normalne gdy komponent się unmountuje
      const isAborted = error.code === 'ECONNABORTED' || error.message === 'Request aborted' || error.message === 'canceled';
      if (!isAborted) {
        // Ciche błędy - nie pokazuj toast
        console.error('Error silently fetching invoices:', error);
      }
    }
  };

  const handleViewPdf = async (invoice) => {
    // W każdej zakładce: KSeF, PDF, skany – zawsze podgląd w nowym oknie z linkiem publicznym
    if (!invoice.id) return;
    if (!invoice.filePath && !invoice.ksefReference) {
      toast.error('Brak załącznika dla tej faktury');
      return;
    }
    const previewUrl = `/app/invoices/${invoice.id}/preview`;
    window.open(previewUrl, '_blank', 'width=1200,height=800');
  };

  const handleViewDescription = (invoice) => {
    // Pobierz opisy użytkowników
    const descriptions = invoiceDescriptions[invoice.id] || [];

    // Generuj HTML dla opisu faktury
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="pl">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Opis faktury - ${invoice.number}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            padding: 40px;
            background: #f5f5f5;
            color: #333;
            line-height: 1.6;
          }
          
          .container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            padding: 40px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            border-radius: 8px;
          }
          
          .header {
            border-bottom: 2px solid #e5e7eb;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          
          .header h1 {
            font-size: 24px;
            font-weight: bold;
            color: #111827;
            margin-bottom: 10px;
          }
          
          .invoice-info {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
            margin-bottom: 30px;
            padding: 20px;
            background: #f9fafb;
            border-radius: 6px;
          }
          
          .info-item {
            font-size: 14px;
          }
          
          .info-label {
            color: #6b7280;
            font-weight: 500;
            margin-bottom: 4px;
          }
          
          .info-value {
            color: #111827;
            font-weight: 600;
          }
          
          .section {
            margin-bottom: 30px;
          }
          
          .section-title {
            font-size: 18px;
            font-weight: 600;
            color: #111827;
            margin-bottom: 15px;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          .section-content {
            font-size: 15px;
            color: #374151;
            line-height: 1.8;
            white-space: pre-wrap;
          }
          
          .signature-container {
            margin-top: 20px;
            text-align: center;
          }
          
          .signature-image {
            max-width: 400px;
            border: 1px solid #d1d5db;
            border-radius: 4px;
            padding: 10px;
            background: white;
          }
          
          .position-info {
            margin-top: 15px;
            padding: 15px;
            background: #f9fafb;
            border-radius: 6px;
            font-size: 14px;
          }
          
          .position-label {
            color: #6b7280;
            font-weight: 500;
            margin-bottom: 5px;
          }
          
          .position-value {
            color: #111827;
            font-weight: 600;
          }
          
          @media print {
            @page {
              size: A4;
              margin: 10mm;
            }
            body {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
            .print\\:break-inside-avoid {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            body {
              background: white;
              padding: 0;
            }
            
            .container {
              box-shadow: none;
              padding: 20px;
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Opis faktury</h1>
            <p style="color: #6b7280; font-size: 14px;">Numer faktury: <strong>${invoice.number || '-'}</strong></p>
          </div>
          
          <div class="invoice-info">
            <div class="info-item">
              <div class="info-label">Dostawca</div>
              <div class="info-value">${invoice.supplierName || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">NIP</div>
              <div class="info-value">${invoice.supplierNip || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Data wystawienia</div>
              <div class="info-value">${formatDate(invoice.issueDate)}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Termin płatności</div>
              <div class="info-value">${formatDate(invoice.dueDate)}</div>
            </div>
          </div>
          
          ${descriptions.length > 0 ? descriptions.map((desc, idx) => `
            <div class="section" style="border-top: ${idx > 0 ? '2px solid #e5e7eb; padding-top: 30px; margin-top: 30px;' : 'none'}">
              <div class="section-title" style="color: #1f2937; font-size: 16px; margin-bottom: 10px;">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="display: inline-block; margin-right: 8px;">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                Opis i podpis - ${desc.userName || desc.userEmail}
              </div>
              ${desc.description ? `
                <div class="section-content" style="margin-bottom: 15px;">${desc.description}</div>
              ` : ''}
              ${desc.signedByPosition ? `
                <div class="position-info">
                  <div class="position-label">Stanowisko</div>
                  <div class="position-value">${desc.signedByPosition}</div>
                </div>
              ` : ''}
              ${desc.signatureImage ? `
                <div style="margin-top: 15px;">
                  <div class="section-title" style="font-size: 14px;">Podpis</div>
                  <div class="signature-container">
                    <img src="${desc.signatureImage}" alt="Podpis" class="signature-image" />
                  </div>
                </div>
              ` : ''}
              ${desc.documentSignatureId ? `
                <div style="margin-top: 15px; padding: 10px; background: #f0fdf4; border: 1px solid #86efac; border-radius: 6px; font-size: 13px; color: #166534;">
                  ✓ Podpis elektroniczny zweryfikowany
                </div>
              ` : ''}
              ${desc.invoicePaymentType === 'CASH' || desc.invoicePaymentType === 'TRANSFER' ? `
                <div class="position-info" style="margin-top: 15px;">
                  <div class="position-label">Typ płatności</div>
                  <div class="position-value">${desc.invoicePaymentType === 'CASH' ? 'Faktura gotówkowa' : 'Przelew'}</div>
                </div>
              ` : ''}
              <div style="margin-top: 10px; font-size: 12px; color: #6b7280;">
                Data: ${formatHistoryDate(desc.createdAt)}
              </div>
            </div>
          `).join('') : `
            <div class="section">
              <div class="section-content" style="color: #6b7280; font-style: italic;">
                Brak opisów i podpisów dla tej faktury.
              </div>
            </div>
          `}
        </div>
      </body>
      </html>
    `;

    // Otwórz w nowym oknie
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const toggleInvoiceExpansion = async (invoiceId) => {
    const newExpanded = new Set(expandedInvoices);
    if (newExpanded.has(invoiceId)) {
      newExpanded.delete(invoiceId);
    } else {
      newExpanded.add(invoiceId);
      if (!invoiceHistory[invoiceId]) {
        await fetchInvoiceHistory(invoiceId);
      }
      // Pobierz załączniki gdy faktura jest rozwinięta
      if (!invoiceAttachments[invoiceId]) {
        try {
          const attachmentsResponse = await api.get(`/invoices/${invoiceId}/attachments`, {
            timeout: 10000
          });
          setInvoiceAttachments(prev => ({
            ...prev,
            [invoiceId]: attachmentsResponse.data || []
          }));
        } catch (error) {
          console.error('Error fetching attachments for invoice:', invoiceId, error);
          setInvoiceAttachments(prev => ({
            ...prev,
            [invoiceId]: []
          }));
        }
      }
    }
    setExpandedInvoices(newExpanded);
  };

  const fetchInvoiceHistory = async (invoiceId) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/history`);
      const history = response.data || [];
      console.log(`Historia faktury ${invoiceId}:`, history.length, 'wpisów, RETURNED:', history.some(item => item.actionType === 'RETURNED'));
      setInvoiceHistory(prev => ({
        ...prev,
        [invoiceId]: history
      }));
    } catch (error) {
      const isAborted = error?.code === 'ECONNABORTED' || error?.message === 'Request aborted' || error?.message === 'canceled';
      if (!isAborted) console.error('Error fetching invoice history:', error);
    }
  };

  // Sprawdź czy faktura była zwrócona (ma w historii akcję RETURNED)
  const wasInvoiceReturned = (invoiceId) => {
    if (!invoiceId) return false;
    const history = invoiceHistory[invoiceId] || [];
    const hasReturned = history.some(item => item.actionType === 'RETURNED');
    // Debug: loguj dla faktury 30
    if (invoiceId === 30) {
      console.log('Faktura 30 - historia:', history.length, 'zwrócona:', hasReturned, 'historia:', history);
    }
    return hasReturned;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
  };

  const formatCurrency = (amount, currency = 'PLN') => {
    if (!amount) return '-';
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatHistoryDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'NEW': 'Nowa',
      'IN_PROGRESS': 'W realizacji',
      'PENDING': 'Oczekująca',
      'ASSIGNED': 'Przekazana',
      'RETURNED': 'Zwrócona',
      'DONE': 'Zaksięgowana',
      'SIGNED': 'Podpisana'
    };
    return statusLabels[status] || status;
  };

  const getStatusColor = (status) => {
    if (isModern) {
      // Nowoczesny wygląd – miękkie, pastelowe pigułki statusów
      const modernColors = {
        'NEW': 'bg-blue-50 text-blue-700 border border-blue-100',
        'IN_PROGRESS': 'bg-amber-50 text-amber-700 border border-amber-100',
        'PENDING': 'bg-rose-50 text-rose-700 border border-rose-100',
        'ASSIGNED': 'bg-violet-50 text-violet-700 border border-violet-100',
        'RETURNED': 'bg-orange-50 text-orange-700 border border-orange-100',
        'DONE': 'bg-emerald-50 text-emerald-700 border border-emerald-100',
        'SIGNED': 'bg-sky-50 text-sky-700 border border-sky-100'
      };
      return modernColors[status] || 'bg-gray-50 text-gray-600 border border-gray-100';
    }
    const colors = {
      'NEW': 'bg-green-600 text-white border border-green-700', // Zielony dla "nowe" - bardziej czytelny
      'IN_PROGRESS': 'bg-orange-500 text-white border border-orange-600', // Pomarańczowy dla "w realizacji"
      'PENDING': 'bg-amber-500 text-white border border-amber-600', // Bursztynowy dla "oczekujące"
      'ASSIGNED': 'bg-yellow-500 text-white border border-yellow-600', // Żółty dla "przekazane"
      'RETURNED': 'bg-orange-50 text-orange-700 border border-orange-200',
      'DONE': 'bg-green-50 text-green-700 border border-green-200',
      'SIGNED': 'bg-blue-500 text-white border border-blue-600'
    };
    return colors[status] || 'bg-gray-50 text-gray-700 border border-gray-200';
  };

  const handleEditClick = async (invoice) => {
    setSelectedInvoice(invoice);
    setEditingInvoice(invoice.id);
    
    // Pobierz historię faktury SYNCHRONICZNIE jeśli nie jest jeszcze pobrana (potrzebne do wykrycia faktur ze zwrotu)
    if (!invoiceHistory[invoice.id]) {
      try {
        console.log('Pobieranie historii faktury SYNCHRONICZNIE:', invoice.id);
        const response = await api.get(`/invoices/${invoice.id}/history`);
        const history = response.data || [];
        console.log('Historia faktury pobrana:', invoice.id, 'zwrotów:', history.filter(h => h.actionType === 'RETURNED').length);
        setInvoiceHistory(prev => ({
          ...prev,
          [invoice.id]: history
        }));
      } catch (error) {
        console.error('Błąd podczas pobierania historii faktury:', error);
      }
    }
    
    // Pobierz opis użytkownika jeśli istnieje
    const userDescription = invoiceDescriptions[invoice.id]?.find(d => d.userId === user?.id);
    
    setEditForm({
      description: userDescription?.description || '',
      signedByPosition: userDescription?.signedByPosition || (user?.position || ''),
      signatureImage: null, // Usunięte - używamy podpisu elektronicznego
      metricalChecked: userDescription?.metricalChecked || false,
      preliminaryCheckDone: userDescription?.preliminaryCheckDone || false,
      formalnoRachunkowoChecked: userDescription?.formalnoRachunkowoChecked || false,
      zatwierdzonoDo: userDescription?.zatwierdzonoDo || '',
      kwota: userDescription?.kwota ? userDescription.kwota.toString() : '',
      costCenter: userDescription?.costCenter || '',
      invoicePaymentType: userDescription?.invoicePaymentType || 'TRANSFER',
      isDotacja: userDescription?.isDotacja || false,
      srodkiPowiat: userDescription?.srodkiPowiat != null ? userDescription.srodkiPowiat.toString() : '',
      srodkiSpZoz: userDescription?.srodkiSpZoz != null ? userDescription.srodkiSpZoz.toString() : ''
    });
    
    // Pobierz aktualne podpisy dla faktury
    try {
      console.log('Pobieranie podpisów dla faktury:', invoice.id);
      const response = await api.get(`/documents/invoices/${invoice.id}/signatures`);
      const signatures = response.data || [];
      console.log('Pobrano podpisy:', signatures.length, 'podpisów dla faktury', invoice.id, signatures);
      setInvoiceSignatures(prev => ({
        ...prev,
        [invoice.id]: signatures
      }));
    } catch (error) {
      console.error('Błąd podczas pobierania podpisów:', error);
      // Ustaw pustą tablicę jeśli nie ma podpisów
      setInvoiceSignatures(prev => ({
        ...prev,
        [invoice.id]: []
      }));
    }
    
    // Pobierz opisy faktury jeśli nie są jeszcze pobrane
    if (!invoiceDescriptions[invoice.id]) {
      try {
        console.log('Pobieranie opisów dla faktury:', invoice.id);
        const descResponse = await api.get(`/invoices/${invoice.id}/descriptions`);
        const descriptions = descResponse.data || [];
        console.log('Pobrano opisy:', descriptions.length, 'opisów dla faktury', invoice.id);
        setInvoiceDescriptions(prev => ({
          ...prev,
          [invoice.id]: descriptions
        }));
      } catch (error) {
        console.error('Błąd podczas pobierania opisów:', error);
        setInvoiceDescriptions(prev => ({
          ...prev,
          [invoice.id]: []
        }));
      }
    }
    
    // Pobierz załączniki dla faktury - zawsze pobierz świeże dane
    try {
      const attachmentsResponse = await api.get(`/invoices/${invoice.id}/attachments`);
      const allAttachments = attachmentsResponse.data || [];
      console.log('🔍 [InvoicesByStatus] Pobrano załączniki dla faktury:', invoice.id, 'Liczba:', allAttachments.length, allAttachments);
      setAttachments(allAttachments);
      // Zapisz też do invoiceAttachments dla użycia w modalu
      setInvoiceAttachments(prev => ({
        ...prev,
        [invoice.id]: allAttachments
      }));
    } catch (error) {
      console.error('❌ [InvoicesByStatus] Błąd podczas pobierania załączników:', error);
      // Jeśli błąd, spróbuj użyć już załadowanych załączników
      const existingAttachments = invoiceAttachments[invoice.id] || [];
      console.log('📦 [InvoicesByStatus] Używam istniejących załączników:', existingAttachments.length);
      setAttachments(existingAttachments);
    }
    
    setShowDescriptionModal(true);
    
    // Pobierz podgląd faktury przez API (z tokenem)
    if (invoice.filePath || invoice.ksefReference) {
      setLoadingPreview(true);
      setInvoicePreviewHtml('');
      setInvoicePreviewUrl(null);
      
      try {
        // Sprawdź czy to plik PDF - sprawdź zarówno filePath jak i file_path (różne formaty z API)
        // Najpierw pobierz pełne dane faktury z API jeśli filePath nie jest dostępne
        let filePath = invoice.filePath || invoice.file_path || '';
        
        // Jeśli nie mamy filePath, pobierz pełne dane faktury z API
        if (!filePath && invoice.id) {
          try {
            console.log('Brak filePath w obiekcie, pobieranie pełnych danych faktury z API:', invoice.id);
            const invoiceResponse = await api.get(`/invoices/${invoice.id}`);
            filePath = invoiceResponse.data?.filePath || invoiceResponse.data?.file_path || '';
            console.log('Pobrano filePath z API:', filePath);
          } catch (err) {
            console.error('Błąd podczas pobierania danych faktury:', err);
          }
        }
        
        const isPdf = filePath && filePath.toLowerCase().endsWith('.pdf');
        const isXml = filePath && filePath.toLowerCase().endsWith('.xml');
        
        console.log('Pobieranie podglądu faktury:', invoice.id, 'filePath:', filePath, 'isPdf:', isPdf, 'isXml:', isXml, 'invoice object keys:', Object.keys(invoice));
        
        // Jeśli nie ma filePath ale jest ksefReference, użyj /preview (KSeF zwraca XML)
        if (!filePath && invoice.ksefReference) {
          console.log('Używanie /preview dla faktury KSeF (bez filePath):', invoice.id);
          const previewResponse = await api.get(`/invoices/${invoice.id}/preview`, {
            responseType: 'text'
          });
          setInvoicePreviewHtml(previewResponse.data);
        } else if (isPdf) {
          // Dla PDF użyj endpointu /file i utwórz blob URL
          console.log('Używanie /file dla PDF faktury:', invoice.id);
          const response = await api.get(`/invoices/${invoice.id}/file`, {
            responseType: 'blob'
          });
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          setInvoicePreviewUrl(url);
        } else if (isXml) {
          // Dla XML użyj endpointu /preview
          console.log('Używanie /preview dla XML faktury:', invoice.id);
          const previewResponse = await api.get(`/invoices/${invoice.id}/preview`, {
            responseType: 'text'
          });
          setInvoicePreviewHtml(previewResponse.data);
        } else if (filePath) {
          // Jeśli mamy filePath ale nie wiemy typ, spróbuj najpierw /file (prawdopodobnie PDF)
          console.log('Nieznany typ pliku z filePath, próba /file:', invoice.id, filePath);
          try {
            const fileResponse = await api.get(`/invoices/${invoice.id}/file`, {
              responseType: 'blob'
            });
            const blob = new Blob([fileResponse.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            setInvoicePreviewUrl(url);
          } catch (fileError) {
            // Jeśli /file nie działa, spróbuj /preview
            console.log('Próba użycia /preview zamiast /file dla faktury:', invoice.id);
            const previewResponse = await api.get(`/invoices/${invoice.id}/preview`, {
              responseType: 'text'
            });
            setInvoicePreviewHtml(previewResponse.data);
          }
        } else {
          // Jeśli nie mamy filePath, użyj /preview (może być KSeF)
          console.log('Brak filePath, próba /preview:', invoice.id);
          try {
            const previewResponse = await api.get(`/invoices/${invoice.id}/preview`, {
              responseType: 'text'
            });
            setInvoicePreviewHtml(previewResponse.data);
          } catch (previewError) {
            // Jeśli /preview nie działa, spróbuj /file jako ostatnia deska ratunku
            if (previewError.response?.status === 400) {
              console.log('Próba użycia /file zamiast /preview dla faktury:', invoice.id);
              const fileResponse = await api.get(`/invoices/${invoice.id}/file`, {
                responseType: 'blob'
              });
              const blob = new Blob([fileResponse.data], { type: 'application/pdf' });
              const url = window.URL.createObjectURL(blob);
              setInvoicePreviewUrl(url);
            } else {
              throw previewError;
            }
          }
        }
      } catch (error) {
        console.error('Błąd podczas pobierania podglądu faktury:', error);
        setInvoicePreviewHtml('');
        setInvoicePreviewUrl(null);
      } finally {
        setLoadingPreview(false);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingInvoice(null);
    setSelectedInvoice(null);
    setShowDescriptionModal(false);
    setAttachments([]);
    // Zwolnij blob URL jeśli istnieje
    if (invoicePreviewUrl && invoicePreviewUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(invoicePreviewUrl);
    }
    // Zwolnij blob URL załącznika jeśli istnieje
    if (attachmentPreviewUrl && attachmentPreviewUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(attachmentPreviewUrl);
    }
    setInvoicePreviewHtml('');
    setInvoicePreviewUrl(null);
    setSelectedAttachmentPreview(null);
    setAttachmentPreviewUrl(null);
    setLoadingAttachmentPreview(false);
    setLoadingPreview(false);
    setEditForm({
      description: '',
      signedByPosition: '',
      signatureImage: null,
      metricalChecked: false,
      preliminaryCheckDone: false,
      formalnoRachunkowoChecked: false,
      zatwierdzonoDo: '',
      kwota: '',
      costCenter: '',
      invoicePaymentType: 'TRANSFER',
      isDotacja: false,
      srodkiPowiat: '',
      srodkiSpZoz: ''
    });
    setIsDescriptionCollapsed(false);
  };

  const handleSaveDraft = async () => {
    if (!selectedInvoice) return;

    try {
      setSaving(true);
      const requestData = {
        description: editForm.description,
        signedByPosition: editForm.signedByPosition,
        isDraft: true,
        metricalChecked: editForm.metricalChecked || false,
        preliminaryCheckDone: editForm.preliminaryCheckDone || false
      };

      if (user?.canCheckMerit && editForm.invoicePaymentType) {
        requestData.invoicePaymentType = editForm.invoicePaymentType;
      }

      if (user?.canCheckFormalAccounting) {
        requestData.formalnoRachunkowoChecked = editForm.formalnoRachunkowoChecked || false;
        if (editForm.zatwierdzonoDo) {
          requestData.zatwierdzonoDo = editForm.zatwierdzonoDo;
        }
        if (editForm.kwota) {
          requestData.kwota = editForm.kwota.replace(',', '.');
        }
      }

      if (user?.canUseCostCenter) {
        requestData.costCenter = editForm.costCenter ? editForm.costCenter.trim() : null;
      }

      requestData.isDotacja = editForm.isDotacja || false;
      if (editForm.isDotacja) {
        requestData.srodkiPowiat = editForm.srodkiPowiat ? editForm.srodkiPowiat.toString().replace(',', '.') : null;
        requestData.srodkiSpZoz = editForm.srodkiSpZoz ? editForm.srodkiSpZoz.toString().replace(',', '.') : null;
      }

      await api.patch(`/invoices/${selectedInvoice.id}/describe`, requestData);
      toast.success('Zapisano roboczo – możesz wrócić w późniejszym terminie');

      try {
        const response = await api.get(`/invoices/${selectedInvoice.id}/descriptions`);
        setInvoiceDescriptions(prev => ({
          ...prev,
          [selectedInvoice.id]: response.data
        }));
      } catch (error) {
        console.error('Error fetching descriptions:', error);
      }

      try {
        const attachmentsResponse = await api.get(`/invoices/${selectedInvoice.id}/attachments`);
        const allAttachments = attachmentsResponse.data || [];
        setAttachments(allAttachments);
        setInvoiceAttachments(prev => ({
          ...prev,
          [selectedInvoice.id]: allAttachments
        }));
      } catch (error) {
        console.error('Error fetching attachments:', error);
      }

      fetchInvoices();
      if (expandedInvoices.has(selectedInvoice.id)) {
        await fetchInvoiceHistory(selectedInvoice.id);
      }
    } catch (error) {
      toast.error('Błąd podczas zapisywania roboczego');
      console.error('Error saving draft:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDescription = async (invoiceId) => {
    try {
      setSaving(true);
      if (user?.canCheckFormalAccounting && editForm.formalnoRachunkowoChecked) {
        const kwotaStr = (editForm.kwota || '').trim().replace(',', '.');
        if (!kwotaStr || isNaN(parseFloat(kwotaStr))) {
          toast.error('Przy zaznaczeniu "Sprawdzono pod względem formalno-rachunkowym" pole KWOTĘ jest obowiązkowe.');
          setSaving(false);
          return;
        }
      }
      // Wysyłamy isDraft: true jeśli nie ma podpisu elektronicznego
      const hasSignature = invoiceSignatures[invoiceId]?.some(sig => 
        sig.user?.id === user?.id && sig.signatureType === 'INTERNAL'
      );
      
      const requestData = {
        description: editForm.description,
        signedByPosition: editForm.signedByPosition,
        signatureImage: null, // Usunięte - używamy podpisu elektronicznego
        isDraft: !hasSignature, // Zapisz jako draft jeśli nie ma podpisu
      };
      
      // Dodaj pole merytoryczne i typ płatności jeśli użytkownik ma uprawnienia
      if (user?.canCheckMerit) {
        requestData.metricalChecked = editForm.metricalChecked || false;
        if (editForm.invoicePaymentType) {
          requestData.invoicePaymentType = editForm.invoicePaymentType;
        }
      }
      
      // Dodaj pole wstępnej kontroli jeśli użytkownik ma uprawnienia
      if (user?.canDoPreliminaryCheck) {
        requestData.preliminaryCheckDone = editForm.preliminaryCheckDone || false;
      }
      
      // Dodaj pola księgowości jeśli użytkownik ma uprawnienie
      if (user?.canCheckFormalAccounting) {
        requestData.formalnoRachunkowoChecked = editForm.formalnoRachunkowoChecked || false;
        if (editForm.zatwierdzonoDo) {
          requestData.zatwierdzonoDo = editForm.zatwierdzonoDo;
        }
        if (editForm.kwota) {
          requestData.kwota = editForm.kwota.replace(',', '.');
        }
      }
      
      // Dodaj pole ośrodka kosztów jeśli użytkownik ma uprawnienie - zawsze wysyłaj, nawet jeśli puste
      console.log('🔍 [DEBUG handleSaveDescription InvoicesByStatus] Sprawdzanie uprawnienia canUseCostCenter:', {
        user: user,
        canUseCostCenter: user?.canUseCostCenter,
        userId: user?.id,
        editFormCostCenter: editForm.costCenter
      });
      
      if (user?.canUseCostCenter) {
        requestData.costCenter = editForm.costCenter ? editForm.costCenter.trim() : null;
        console.log('💰 [DEBUG handleSaveDescription InvoicesByStatus] Wysyłanie costCenter:', requestData.costCenter);
      } else {
        console.warn('⚠️ [DEBUG handleSaveDescription InvoicesByStatus] Użytkownik nie ma uprawnienia canUseCostCenter:', user?.canUseCostCenter);
      }

      // DOTACJA (dotyczy księgowości) - widoczne dla wszystkich
      requestData.isDotacja = editForm.isDotacja || false;
      if (editForm.isDotacja) {
        requestData.srodkiPowiat = editForm.srodkiPowiat ? editForm.srodkiPowiat.toString().replace(',', '.') : null;
        requestData.srodkiSpZoz = editForm.srodkiSpZoz ? editForm.srodkiSpZoz.toString().replace(',', '.') : null;
      }
      
      console.log('📤 [DEBUG handleSaveDescription InvoicesByStatus] Wysyłane dane:', requestData);
      await api.patch(`/invoices/${invoiceId}/describe`, requestData);
      toast.success('Zapisano opis i podpis');
      setEditingInvoice(null);
      setSelectedInvoice(null);
      setShowDescriptionModal(false);
      
      // Odśwież opisy dla tej faktury
      try {
        const response = await api.get(`/invoices/${invoiceId}/descriptions`);
        setInvoiceDescriptions(prev => ({
          ...prev,
          [invoiceId]: response.data
        }));
      } catch (error) {
        console.error('Error fetching descriptions:', error);
      }
      
      fetchInvoices();
      // Odśwież historię jeśli faktura jest rozwinięta
      if (expandedInvoices.has(invoiceId)) {
        await fetchInvoiceHistory(invoiceId);
      }
    } catch (error) {
      toast.error('Błąd podczas zapisywania');
      console.error('Error saving description:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSignatureClick = (invoiceId, signatureId) => {
    setConfirmModal({
      show: true,
      title: 'Usuń podpis elektroniczny',
      message: 'Czy na pewno chcesz usunąć ten podpis elektroniczny? Po usunięciu będziesz mógł ponownie dodać opis i podpis.',
      confirmLabel: 'Usuń',
      pendingAction: { type: 'signature', invoiceId, signatureId }
    });
  };

  const handleConfirmModalConfirm = async () => {
    const { pendingAction } = confirmModal;
    if (!pendingAction) return;
    setConfirmModal(prev => ({ ...prev, show: false, pendingAction: null }));
    if (pendingAction.type === 'attachment') {
      await handleDeleteAttachment(pendingAction.attachmentId, pendingAction.invoiceId);
    } else if (pendingAction.type === 'description') {
      await handleDeleteDescription(pendingAction.invoiceId, pendingAction.descriptionId);
    } else if (pendingAction.type === 'signature') {
      await handleDeleteSignature(pendingAction.invoiceId, pendingAction.signatureId);
    } else if (pendingAction.type === 'invoice') {
      await handleDeleteInvoice(pendingAction.invoiceId);
    }
  };

  const handleDeleteSignature = async (invoiceId, signatureId) => {
    try {
      await api.delete(`/invoices/${invoiceId}/signatures/${signatureId}`);
      toast.success('Podpis został usunięty. Możesz teraz ponownie dodać opis i podpis.');
      
      // Odśwież podpisy dla tej faktury
      try {
        const response = await api.get(`/documents/invoices/${invoiceId}/signatures`);
        setInvoiceSignatures(prev => ({
          ...prev,
          [invoiceId]: response.data || []
        }));
      } catch (error) {
        console.error('Error fetching signatures:', error);
        // Jeśli błąd, usuń z lokalnego stanu
        setInvoiceSignatures(prev => {
          const updated = { ...prev };
          if (updated[invoiceId]) {
            updated[invoiceId] = updated[invoiceId].filter(s => s.id !== signatureId);
          }
          return updated;
        });
      }
      
      // Odśwież opisy dla tej faktury (może być powiązany opis)
      try {
        const response = await api.get(`/invoices/${invoiceId}/descriptions`);
        setInvoiceDescriptions(prev => ({
          ...prev,
          [invoiceId]: response.data || []
        }));
      } catch (error) {
        console.error('Error fetching descriptions:', error);
      }
      
      fetchInvoices();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Błąd podczas usuwania podpisu';
      toast.error(errorMessage);
      console.error('Error deleting signature:', error);
    }
  };

  const handleDeleteDescriptionClick = (invoiceId, descriptionId) => {
    setConfirmModal({
      show: true,
      title: 'Usuń opis',
      message: 'Czy na pewno chcesz usunąć ten opis?',
      confirmLabel: 'Usuń',
      pendingAction: { type: 'description', invoiceId, descriptionId }
    });
  };

  const handleDeleteDescription = async (invoiceId, descriptionId) => {
    try {
      await api.delete(`/invoices/${invoiceId}/descriptions/${descriptionId}`);
      toast.success('Opis został usunięty');
      
      // Odśwież opisy dla tej faktury
      try {
        const response = await api.get(`/invoices/${invoiceId}/descriptions`);
        setInvoiceDescriptions(prev => ({
          ...prev,
          [invoiceId]: response.data || []
        }));
      } catch (error) {
        console.error('Error fetching descriptions:', error);
        // Jeśli błąd, usuń z lokalnego stanu
        setInvoiceDescriptions(prev => {
          const updated = { ...prev };
          if (updated[invoiceId]) {
            updated[invoiceId] = updated[invoiceId].filter(d => d.id !== descriptionId);
          }
          return updated;
        });
      }
      
      fetchInvoices();
    } catch (error) {
      toast.error('Błąd podczas usuwania opisu');
      console.error('Error deleting description:', error);
    }
  };

  const toggleSelection = (id) => {
    setSelectedInvoiceIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const selectAllOnPage = (invoices) => {
    if (!invoices.length) return;
    const ids = invoices.map(i => i.id);
    setSelectedInvoiceIds(prev => {
      const set = new Set(prev);
      const allSelected = ids.every(id => set.has(id));
      if (allSelected) ids.forEach(id => set.delete(id));
      else ids.forEach(id => set.add(id));
      return Array.from(set);
    });
  };

  /** Zaznacz/odznacz wszystkie faktury (uwzględnia paginację – pobiera wszystkie ID z API) */
  const selectAllInvoices = async () => {
    if (totalElements === 0) return;
    if (selectedInvoiceIds.length === totalElements) {
      clearSelection();
      return;
    }
    setSelectingAllInvoices(true);
    try {
      const apiStatus = statusMap[status];
      const params = {
        status: apiStatus,
        sort: sortField,
        direction: sortDirection
      };
      if (useServerPagination) {
        params.page = 0;
        params.size = Math.min(totalElements, 10000);
        if (debouncedSearchQuery?.trim()) params.q = debouncedSearchQuery.trim();
      } else {
        params.page = 0;
        params.size = 10000;
      }
      if (dateFromFilter) params.dateFrom = dateFromFilter;
      if (dateToFilter) params.dateTo = dateToFilter;
      if (status === 'assigned') params.assignedByMe = true;
      else if (status === 'completed') params.completedByMe = true;
      const response = await api.get('/invoices', { params });
      const invoicesData = response.data.content || response.data;
      let ids = (invoicesData || []).map(i => i.id);
      if (!useServerPagination && searchQuery?.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const filtered = (invoicesData || []).filter(inv => (
          (inv.supplierNip && inv.supplierNip.toLowerCase().includes(query)) ||
          (inv.supplierName && inv.supplierName.toLowerCase().includes(query)) ||
          (inv.amountGross && inv.amountGross.toString().includes(query)) ||
          (inv.amountNet && inv.amountNet.toString().includes(query)) ||
          (inv.number && inv.number.toLowerCase().includes(query)) ||
          (inv.internalNumber && inv.internalNumber.toLowerCase().includes(query))
        ));
        ids = filtered.map(i => i.id);
      }
      setSelectedInvoiceIds(ids);
      if (ids.length > 0) toast.success(`Zaznaczono wszystkie ${ids.length} faktur`);
    } catch (error) {
      toast.error('Błąd podczas pobierania listy faktur');
      console.error('Error fetching all invoices for selection:', error);
    } finally {
      setSelectingAllInvoices(false);
    }
  };

  const clearSelection = () => {
    setSelectedInvoiceIds([]);
    setBatchSignQueue([]);
  };

  const handleBatchSignClick = () => {
    if (selectedInvoiceIds.length === 0) return;
    const queue = [...selectedInvoiceIds];
    setBatchSignQueue(queue);
    setSigningInvoiceId(queue[0]);
    setSigningAttachmentId(null);
    setShowSignModal(true);
  };

  const handleToggleSuspicious = async (invoice) => {
    const markAsSuspicious = !invoice.suspicious;
    try {
      const response = await api.patch(`/invoices/${invoice.id}/suspicious`, {
        suspicious: markAsSuspicious,
      });
      const updated = response.data;
      setAllInvoices((prev) =>
        prev.map((inv) => (inv.id === invoice.id ? { ...inv, ...updated } : inv))
      );
      toast.success(
        markAsSuspicious
          ? 'Faktura oznaczona jako podejrzana (do wyjaśnienia)'
          : 'Usunięto oznaczenie podejrzanej faktury'
      );
      if (expandedInvoices.has(invoice.id)) {
        await fetchInvoiceHistory(invoice.id);
      }
    } catch (error) {
      const msg = error?.response?.data?.error
        || (markAsSuspicious
          ? 'Błąd podczas oznaczania faktury jako podejrzanej'
          : 'Błąd podczas usuwania oznaczenia podejrzanej faktury');
      toast.error(msg);
      console.error('Error toggling suspicious invoice:', error);
    }
  };

  const openAssignModalFor = async (invoice) => {
    setSelectedInvoice(invoice);
    setAssignBatchIds([]);
    setSelectedUserId(invoice.assignedUserId || '');
    setSubstitutionInfo(null);
    setUserSearchQuery('');

    await fetchUsers();
    try {
      const response = await api.get('/substitutions/active');
      setActiveSubstitutions(response.data || []);
    } catch (error) {
      console.error('Error fetching active substitutions:', error);
      setActiveSubstitutions([]);
    }
    setShowAssignModal(true);
  };

  const handleAssignClick = async (invoice) => {
    let blocking = [];
    try {
      blocking = await fetchBlockingAttachments(api, invoice.id, user);
    } catch (e) {
      console.error('Błąd weryfikacji podpisów załączników:', e);
      toast.error('Nie udało się zweryfikować podpisów załączników. Spróbuj ponownie.');
      return;
    }
    // NOWA z sekretariatu: pierwszy odbiorca może przekazać dalej bez podpisu załącznika.
    if (blocking.length > 0 && !allowsForwardingWithUnsignedAttachments(invoice)) {
      // Fakturę przekazaną od kogoś innego trzeba najpierw przyjąć do realizacji.
      if (requiresAcceptBeforeAttachmentWaiver(invoice)) {
        toast.error('Najpierw przyjmij fakturę do realizacji, a potem wskaż osobę do podpisu załącznika.');
        return;
      }
      await fetchUsers();
      setWaiverInvoice(invoice);
      setWaiverAttachments(blocking);
      setShowWaiverModal(true);
      return;
    }
    await openAssignModalFor(invoice);
  };

  const goToInvoiceOnList = async (invoice) => {
    if (!invoice?.id) return;
    setSelectedInvoiceIds([invoice.id]);
    if (!expandedInvoices.has(invoice.id)) {
      await toggleInvoiceExpansion(invoice.id);
    }
    const tryScroll = () => {
      const row = document.querySelector(`[data-invoice-id="${invoice.id}"]`);
      if (!row) return false;
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      row.style.boxShadow = 'inset 0 0 0 2px #f59e0b';
      setTimeout(() => {
        if (row) row.style.boxShadow = '';
      }, 5000);
      return true;
    };
    if (tryScroll()) return;
    const query = invoice.internalNumber || invoice.number || '';
    if (query) {
      setSearchQuery(query);
      setTimeout(tryScroll, 700);
    }
  };

  const handleAssignBatchClick = async () => {
    if (selectedInvoiceIds.length === 0) return;
    if (selectedInvoiceIds.length === 1) {
      const single = allInvoices.find((inv) => inv.id === selectedInvoiceIds[0]);
      if (single) {
        await handleAssignClick(single);
        return;
      }
    }
    try {
      for (const id of selectedInvoiceIds) {
        const invoice = allInvoices.find((inv) => inv.id === id);
        if (allowsForwardingWithUnsignedAttachments(invoice)) {
          continue;
        }
        const unsignedNames = await fetchUnsignedAttachmentNames(api, id, user);
        if (unsignedNames.length > 0) {
          const blockingInvoice = invoice || { id };
          showUnsignedAttachmentBlockingToast({
            invoice: blockingInvoice,
            names: unsignedNames,
            onGoTo: () => goToInvoiceOnList(blockingInvoice),
          });
          return;
        }
      }
    } catch (e) {
      console.error('Błąd weryfikacji podpisów załączników:', e);
      toast.error('Nie udało się zweryfikować podpisów załączników. Spróbuj ponownie.');
      return;
    }
    setSelectedInvoice(null);
    setAssignBatchIds([...selectedInvoiceIds]);
    setSelectedUserId('');
    setSubstitutionInfo(null);
    setUserSearchQuery('');
    await fetchUsers();
    try {
      const response = await api.get('/substitutions/active');
      setActiveSubstitutions(response.data || []);
    } catch (error) {
      console.error('Error fetching active substitutions:', error);
      setActiveSubstitutions([]);
    }
    setShowAssignModal(true);
  };

  const handleUserSelectChange = async (userId) => {
    setSelectedUserId(userId);
    if (userId) {
      try {
        // Sprawdź czy wybrany użytkownik ma aktywne zastępstwo
        const substitution = activeSubstitutions.find(s => s.managerId === parseInt(userId));
        
        if (substitution) {
          setSubstitutionInfo({
            managerName: substitution.managerName,
            substituteName: substitution.substituteName,
            substituteId: substitution.substituteId
          });
          // Automatycznie zmień wybór na zastępcę
          setSelectedUserId(substitution.substituteId.toString());
        } else {
          setSubstitutionInfo(null);
        }
      } catch (error) {
        console.error('Error checking substitution:', error);
        setSubstitutionInfo(null);
      }
    } else {
      setSubstitutionInfo(null);
    }
  };

  const handleAssignSubmit = async () => {
    if (!selectedUserId) {
      toast.error('Wybierz użytkownika');
      return;
    }

    if (isAssigning) return;

    const idsToCheck = assignBatchIds.length > 0
      ? assignBatchIds
      : (selectedInvoice ? [selectedInvoice.id] : []);
    try {
      for (const checkId of idsToCheck) {
        const invoiceForCheck = selectedInvoice?.id === checkId
          ? selectedInvoice
          : allInvoices.find((inv) => inv.id === checkId);
        if (allowsForwardingWithUnsignedAttachments(invoiceForCheck)) {
          continue;
        }
        const unsignedNames = await fetchUnsignedAttachmentNames(api, checkId, user);
        if (unsignedNames.length > 0) {
          const blockingInvoice = invoiceForCheck || { id: checkId };
          showUnsignedAttachmentBlockingToast({
            invoice: blockingInvoice,
            names: unsignedNames,
            onGoTo: () => goToInvoiceOnList(blockingInvoice),
          });
          return;
        }
      }
    } catch (e) {
      console.error('Błąd weryfikacji podpisów załączników:', e);
      toast.error('Nie udało się zweryfikować podpisów załączników. Spróbuj ponownie.');
      return;
    }

    setIsAssigning(true);
    try {
      const targetUserId = parseInt(selectedUserId);
      const isBatch = assignBatchIds.length > 0;

      if (isBatch) {
        let ok = 0;
        let err = 0;
        for (const id of assignBatchIds) {
          try {
            await api.put(`/invoices/${id}`, { status: 'ASSIGNED', assignedUserId: targetUserId });
            ok++;
          } catch (e) {
            err++;
            console.error('Error assigning invoice', id, e);
          }
        }
        setShowAssignModal(false);
        setAssignBatchIds([]);
        setSelectedInvoiceIds([]);
        setSelectedUserId('');
        setSubstitutionInfo(null);
        setUserSearchQuery('');
        await fetchInvoices();
        if (err > 0) toast.error(`Przekazano ${ok} faktur. Błąd: ${err}.`);
        else if (substitutionInfo) toast.success(`${ok} faktur przekazanych do ${substitutionInfo.substituteName} (zastępstwo)`);
        else toast.success(`Przekazano ${ok} ${ok === 1 ? 'fakturę' : ok < 5 ? 'faktury' : 'faktur'} do użytkownika`);
      } else {
        const newStatus = selectedInvoice.status === 'SIGNED' ? 'ASSIGNED' : 'ASSIGNED';
        await api.put(`/invoices/${selectedInvoice.id}`, {
          status: newStatus,
          assignedUserId: targetUserId
        });
        if (substitutionInfo) {
          toast.success(`Faktura przekazana do ${substitutionInfo.substituteName} w zastępstwie za ${substitutionInfo.managerName}`);
        } else {
          toast.success('Faktura przekazana do użytkownika');
        }
        setShowAssignModal(false);
        const invoiceId = selectedInvoice.id;
        setSelectedInvoice(null);
        setSelectedUserId('');
        setSubstitutionInfo(null);
        setUserSearchQuery('');
        await fetchInvoices();
        if (expandedInvoices.has(invoiceId)) await fetchInvoiceHistory(invoiceId);
      }
    } catch (error) {
      const data = error?.response?.data;
      if (data?.unsignedAttachments?.length > 0) {
        const blockingInvoice = selectedInvoice
          || allInvoices.find((inv) => inv.id === assignBatchIds[0]);
        showUnsignedAttachmentBlockingToast({
          invoice: blockingInvoice,
          names: data.unsignedAttachments,
          onGoTo: blockingInvoice ? () => goToInvoiceOnList(blockingInvoice) : undefined,
        });
      } else if (data?.error) {
        toast.error(data.error);
      } else {
        toast.error('Błąd podczas przekazywania faktury');
      }
      console.error('Error assigning invoice:', error);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleAcceptToSelf = async (invoice) => {
    if (!user?.id) return;
    try {
      await api.put(`/invoices/${invoice.id}`, {
        status: 'IN_PROGRESS',
        assignedUserId: user.id
      });
      toast.success('Faktura została przyjęta do realizacji');
      await fetchInvoices();
      if (expandedInvoices.has(invoice.id)) await fetchInvoiceHistory(invoice.id);
    } catch (error) {
      const data = error?.response?.data;
      if (data?.unsignedAttachments?.length > 0) {
        showUnsignedAttachmentBlockingToast({
          invoice,
          names: data.unsignedAttachments,
          onGoTo: () => goToInvoiceOnList(invoice),
        });
      } else if (data?.error) {
        toast.error(data.error);
      } else {
        toast.error('Błąd podczas przyjmowania faktury');
      }
      console.error('Error accepting invoice to self:', error);
    }
  };

  const handleAccountInvoice = async (invoice) => {
    try {
      await api.patch(`/invoices/${invoice.id}/finish`);
      toast.success('Faktura została zaksięgowana');
      
      // Odśwież listę faktur
      await fetchInvoices();
      
      // Odśwież historię jeśli faktura jest rozwinięta
      if (expandedInvoices.has(invoice.id)) {
        await fetchInvoiceHistory(invoice.id);
      }
    } catch (error) {
      toast.error('Błąd podczas księgowania faktury');
      console.error('Error accounting invoice:', error);
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    if (user?.role !== 'SUPERADMIN') return;

    const invoice = allInvoices.find((inv) => inv.id === invoiceId);
    if (!invoice || invoice.status === newStatus) return;

    try {
      setStatusUpdatingId(invoiceId);
      await api.patch(`/invoices/${invoiceId}/status`, { status: newStatus });
      toast.success('Status faktury został zaktualizowany');
      setAllInvoices((prev) =>
        prev.map((inv) => (inv.id === invoiceId ? { ...inv, status: newStatus } : inv))
      );
      if (expandedInvoices.has(invoiceId)) {
        await fetchInvoiceHistory(invoiceId);
      }
      await fetchInvoices();
    } catch (error) {
      const msg = error?.response?.data?.error || 'Błąd podczas zmiany statusu faktury';
      toast.error(msg);
      console.error('Error updating invoice status:', error);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleRevertClick = (invoice) => {
    setRevertInvoice(invoice);
    setShowRevertModal(true);
  };

  const handleRevertConfirm = async () => {
    if (!revertInvoice) return;

    try {
      await api.patch(`/invoices/${revertInvoice.id}/revert`);
      toast.success('Faktura została cofnięta do listy faktur');
      setShowRevertModal(false);
      setRevertInvoice(null);

      await fetchInvoices();
      if (expandedInvoices.has(revertInvoice.id)) {
        await fetchInvoiceHistory(revertInvoice.id);
      }
    } catch (error) {
      toast.error('Błąd podczas cofania faktury');
      console.error('Error reverting invoice:', error);
    }
  };

  const handleMoveToPending = async (invoice) => {
    try {
      await api.patch(`/invoices/${invoice.id}/move-to-pending`);
      toast.success('Faktura została przeniesiona do oczekujących');
      await fetchInvoices();
      if (expandedInvoices.has(invoice.id)) {
        await fetchInvoiceHistory(invoice.id);
      }
    } catch (error) {
      toast.error('Błąd podczas przenoszenia do oczekujących');
      console.error('Error moving to pending:', error);
    }
  };

  const handleMoveToInProgress = async (invoice) => {
    try {
      await api.patch(`/invoices/${invoice.id}/move-to-in-progress`);
      toast.success('Faktura została przeniesiona do realizacji');
      await fetchInvoices();
      if (expandedInvoices.has(invoice.id)) {
        await fetchInvoiceHistory(invoice.id);
      }
    } catch (error) {
      toast.error('Błąd podczas przenoszenia do realizacji');
      console.error('Error moving to in progress:', error);
    }
  };

  const handleReturnClick = async (invoice) => {
    setReturnInvoice(invoice);
    setReturnReason('');
    setReturnToUserId('');
    setReturnUserSearchQuery('');
    setShowReturnModal(true);
    
    // Pobierz listę użytkowników jeśli jeszcze nie została pobrana
    if (users.length === 0) {
      try {
        const response = await api.get('/users');
        setUsers(response.data || []);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    }
  };

  // Funkcja do dodawania załącznika bezpośrednio z listy faktur (bez modala)
  const handleAttachmentUploadFromList = async (e, invoiceId) => {
    const file = e.target.files[0];
    if (!file || !invoiceId) return;
    
    // Walidacja pliku
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error('Dozwolone formaty: PDF, PNG, JPG');
      e.target.value = '';
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Maksymalny rozmiar pliku: 10MB');
      e.target.value = '';
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/invoices/${invoiceId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Załącznik dodany');
      
      // Odśwież załączniki dla tej faktury
      const attachmentsResponse = await api.get(`/invoices/${invoiceId}/attachments`, {
        timeout: 10000 // 10 sekund timeout
      });
      const updatedAttachments = attachmentsResponse.data || [];
      setInvoiceAttachments(prev => ({
        ...prev,
        [invoiceId]: updatedAttachments
      }));

      const uploaded = updatedAttachments.find((a) => a.originalFileName === file.name) || updatedAttachments[updatedAttachments.length - 1];
      if (uploaded?.id && isAttachmentSigningRequired(uploaded, user)) {
        toast('Podpis załącznika jest obowiązkowy — użyj ikony długopisu przy pliku.', { icon: '✍️', duration: 6000 });
        setSigningInvoiceId(invoiceId);
        setSigningAttachmentId(uploaded.id);
        setShowSignModal(true);
      }
      
      e.target.value = '';
    } catch (error) {
      toast.error('Błąd podczas dodawania załącznika');
      console.error('Error uploading attachment:', error);
      e.target.value = '';
    }
  };

  const uploadSingleAttachmentToInvoice = async (file) => {
    if (!file || !selectedInvoice) return false;

    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error(`Plik "${file.name}" - dozwolone formaty: PDF, PNG, JPG`);
      return false;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(`Plik "${file.name}" przekracza 10MB`);
      return false;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post(`/invoices/${selectedInvoice.id}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return true;
    } catch (error) {
      toast.error(`Błąd podczas dodawania pliku: ${file.name}`);
      console.error('Error uploading attachment:', error);
      return false;
    }
  };

  const refreshSelectedInvoiceAttachments = async () => {
    if (!selectedInvoice) return;
    try {
      const attachmentsResponse = await api.get(`/invoices/${selectedInvoice.id}/attachments`, {
        timeout: 10000
      });
      const allAttachments = attachmentsResponse.data || [];
      setAttachments(allAttachments);
      setInvoiceAttachments(prev => ({
        ...prev,
        [selectedInvoice.id]: allAttachments
      }));
    } catch (error) {
      console.error('Error refreshing attachments:', error);
    }
  };

  const handleAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !selectedInvoice) return;

    try {
      setUploadingAttachment(true);
      let successCount = 0;
      for (const file of files) {
        const ok = await uploadSingleAttachmentToInvoice(file);
        if (ok) successCount += 1;
      }
      if (successCount > 0) {
        toast.success(successCount === 1 ? 'Załącznik dodany' : `Dodano ${successCount} załączników`);
        await refreshSelectedInvoiceAttachments();
        if (isAttachmentSigningRequiredForUser(user)) {
          const attRes = await api.get(`/invoices/${selectedInvoice.id}/attachments`, { timeout: 10000 });
          const firstUnsigned = (attRes.data || []).find(
            (a) => isAttachmentSigningRequired(a, user) && a.signed === false
          );
          if (firstUnsigned?.id) {
            toast('Podpis załącznika jest obowiązkowy — użyj ikony długopisu przy pliku.', { icon: '✍️', duration: 6000 });
            setSigningInvoiceId(selectedInvoice.id);
            setSigningAttachmentId(firstUnsigned.id);
            setShowSignModal(true);
          }
        }
      }
      e.target.value = '';
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleAttachmentDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAttachment(false);

    if (!selectedInvoice) {
      toast.error('Brak wybranej faktury');
      return;
    }

    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;

    try {
      setUploadingAttachment(true);
      let successCount = 0;
      for (const file of files) {
        const ok = await uploadSingleAttachmentToInvoice(file);
        if (ok) successCount += 1;
      }
      if (successCount > 0) {
        toast.success(successCount === 1 ? 'Załącznik dodany' : `Dodano ${successCount} załączników`);
        await refreshSelectedInvoiceAttachments();
        if (isAttachmentSigningRequiredForUser(user)) {
          const attRes = await api.get(`/invoices/${selectedInvoice.id}/attachments`, { timeout: 10000 });
          const firstUnsigned = (attRes.data || []).find(
            (a) => isAttachmentSigningRequired(a, user) && a.signed === false
          );
          if (firstUnsigned?.id) {
            toast('Podpis załącznika jest obowiązkowy — użyj ikony długopisu przy pliku.', { icon: '✍️', duration: 6000 });
            setSigningInvoiceId(selectedInvoice.id);
            setSigningAttachmentId(firstUnsigned.id);
            setShowSignModal(true);
          }
        }
      }
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleDeleteAttachmentClick = (attachmentId, invoiceId) => {
    setConfirmModal({
      show: true,
      title: 'Usuń załącznik',
      message: 'Czy na pewno chcesz usunąć ten załącznik?',
      confirmLabel: 'Usuń',
      pendingAction: { type: 'attachment', attachmentId, invoiceId }
    });
  };

  const handleDeleteAttachment = async (attachmentId, invoiceId) => {
    try {
      await api.delete(`/invoices/attachments/${attachmentId}`);
      toast.success('Załącznik został usunięty');

      const attachmentsResponse = await api.get(`/invoices/${invoiceId}/attachments`, {
        timeout: 10000
      });
      const allAttachments = attachmentsResponse.data || [];
      setInvoiceAttachments(prev => ({
        ...prev,
        [invoiceId]: allAttachments
      }));
      if (selectedInvoice && selectedInvoice.id === invoiceId) {
        setAttachments(allAttachments);
      }
    } catch (error) {
      toast.error('Błąd podczas usuwania załącznika');
      console.error('Error deleting attachment:', error);
    }
  };

  const canReplaceAttachment = user?.role === 'ADMIN'
    || user?.role === 'SUPERADMIN'
    || user?.canDeleteDescriptionsAndSignatures === true;

  const handleOpenReplaceModal = async (attachment, invoiceId, invoiceSigs) => {
    if (users.length === 0) {
      try {
        const response = await api.get('/users');
        setUsers(response.data || []);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    }
    const attachmentSig = (invoiceSigs || []).find(
      (sig) => sig.crossedOut !== true && sig.attachmentId != null && Number(sig.attachmentId) === Number(attachment.id)
    );
    setReplaceAttachment(attachment);
    setReplaceInvoiceId(invoiceId);
    setReplaceDefaultSignerId(attachmentSig?.userId || attachmentSig?.user?.id || null);
    setShowReplaceModal(true);
  };

  const handleAttachmentReplaceSuccess = async (invoiceId) => {
    try {
      const [attachmentsResponse, sigResponse] = await Promise.all([
        api.get(`/invoices/${invoiceId}/attachments`),
        api.get(`/documents/invoices/${invoiceId}/signatures`),
      ]);
      setInvoiceAttachments((prev) => ({
        ...prev,
        [invoiceId]: attachmentsResponse.data || [],
      }));
      setInvoiceSignatures((prev) => ({
        ...prev,
        [invoiceId]: sigResponse.data || [],
      }));
      if (selectedInvoice?.id === invoiceId) {
        setAttachments(attachmentsResponse.data || []);
      }
      await fetchInvoiceHistory(invoiceId);
    } catch (error) {
      console.error('Error refreshing after attachment replace:', error);
    }
  };

  const handleReturnSubmit = async () => {
    if (!returnInvoice) return;
    if (!returnReason.trim()) {
      toast.error('Podaj powód zwrotu');
      return;
    }
    if (!returnToUserId) {
      toast.error('Wybierz użytkownika, do którego chcesz zwrócić fakturę');
      return;
    }

    try {
      await api.patch(`/invoices/${returnInvoice.id}/return`, {
        reason: returnReason.trim(),
        returnToUserId: parseInt(returnToUserId)
      });

      const returnToUser = users.find(u => u.id === parseInt(returnToUserId));
      const userName = returnToUser ? `${returnToUser.firstName} ${returnToUser.lastName}` : 'użytkownika';
      toast.success(`Faktura została zwrócona do ${userName}`);
      const invoiceId = returnInvoice.id;
      setShowReturnModal(false);
      setReturnInvoice(null);
      setReturnReason('');
      setReturnToUserId('');
      setReturnUserSearchQuery('');

      await fetchInvoices();
      if (expandedInvoices.has(invoiceId)) {
        await fetchInvoiceHistory(invoiceId);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Błąd podczas zwracania faktury';
      toast.error(errorMessage);
      console.error('Error returning invoice:', error);
    }
  };

  const handleDeleteInvoiceClick = (invoiceId) => {
    setConfirmModal({
      show: true,
      title: 'Usuń fakturę',
      message: 'Czy na pewno chcesz usunąć tę fakturę? Ta operacja jest nieodwracalna.',
      confirmLabel: 'Usuń',
      pendingAction: { type: 'invoice', invoiceId }
    });
  };

  const handleDeleteInvoice = async (invoiceId) => {
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success('Faktura została usunięta');
      await fetchInvoices();
    } catch (error) {
      toast.error('Błąd podczas usuwania faktury');
      console.error('Error deleting invoice:', error);
    }
  };

  return (
    <div className={`card w-full ${isModern ? 'crm-modern-invoices' : ''}`}>
      <div className={`card-header ${isModern ? 'bg-white' : 'bg-gradient-to-r from-gray-50 to-white'}`}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Faktury - {statusLabels[status] || status}</h2>
              {(() => {
                const filteredCount = allInvoices.filter(invoice => {
                  if (!searchQuery) return true;
                  const query = searchQuery.toLowerCase().trim();
                  return (
                    (invoice.supplierNip && invoice.supplierNip.toLowerCase().includes(query)) ||
                    (invoice.supplierName && invoice.supplierName.toLowerCase().includes(query)) ||
                    (invoice.amountGross && invoice.amountGross.toString().includes(query)) ||
                    (invoice.amountNet && invoice.amountNet.toString().includes(query)) ||
                    (invoice.number && invoice.number.toLowerCase().includes(query)) ||
                    (invoice.internalNumber && invoice.internalNumber.toLowerCase().includes(query))
                  );
                }).length;
                return (
                  <p className="mt-1 text-xs sm:text-sm text-gray-600">
                    {searchQuery ? (
                      <>Znaleziono {filteredCount} {filteredCount === 1 ? 'fakturę' : filteredCount < 5 ? 'faktury' : 'faktur'} (wyszukiwanie)</>
                    ) : (
                      <>Znaleziono {totalElements} {totalElements === 1 ? 'fakturę' : totalElements < 5 ? 'faktury' : 'faktur'}</>
                    )}
                  </p>
                );
              })()}
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full lg:w-auto items-stretch sm:items-center">
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-xs sm:text-sm text-gray-700 whitespace-nowrap hidden sm:inline">Data dodania:</label>
                <div className="relative">
                  <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    type="date"
                    value={dateFromFilter}
                    onChange={(e) => {
                      setDateFromFilter(e.target.value);
                      if (useServerPagination) setCurrentPage(0);
                    }}
                    className="pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-auto min-w-0"
                  />
                </div>
                <span className="text-gray-500 self-center">–</span>
                <div className="relative">
                  <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    type="date"
                    value={dateToFilter}
                    onChange={(e) => {
                      setDateToFilter(e.target.value);
                      if (useServerPagination) setCurrentPage(0);
                    }}
                    className="pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-auto min-w-0"
                  />
                </div>
              </div>
              <div className="relative w-full sm:min-w-[280px] sm:flex-1 lg:max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Wyszukaj po numerze, dostawcy, NIP..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card-body p-0">
        {/* Akcja masowa – nad tabelą, widoczna po zaznaczeniu faktur we wszystkich statusach */}
        {selectedInvoiceIds.length > 0 && (
          <div className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4 border-b ${isModern ? 'bg-slate-50 border-slate-200' : 'bg-teal-600 text-white border-teal-700 shadow-md'}`}>
            <span className={`text-sm sm:text-base font-semibold ${isModern ? 'text-slate-700' : ''}`}>
              {isModern && (
                <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 mr-2 rounded-full bg-teal-100 text-teal-700 text-xs font-bold align-middle">
                  {selectedInvoiceIds.length}
                </span>
              )}
              {isModern
                ? `${selectedInvoiceIds.length === 1 ? 'faktura zaznaczona' : selectedInvoiceIds.length < 5 ? 'faktury zaznaczone' : 'faktur zaznaczonych'}`
                : `Zaznaczono ${selectedInvoiceIds.length} ${selectedInvoiceIds.length === 1 ? 'fakturę' : selectedInvoiceIds.length < 5 ? 'faktury' : 'faktur'}`}
            </span>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={clearSelection}
                className={isModern
                  ? 'px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors'
                  : 'px-3 py-1.5 text-sm font-medium text-teal-800 bg-white/90 rounded-lg hover:bg-white transition-colors'}
              >
                Wyczyść zaznaczenie
              </button>
              <button
                type="button"
                onClick={handleBatchSignClick}
                className={isModern
                  ? 'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors'
                  : 'inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-teal-900 bg-white rounded-lg hover:bg-teal-50 transition-colors shadow-sm'}
              >
                <PenTool className="h-4 w-4" />
                Podpisz wszystkie
              </button>
              <button
                type="button"
                onClick={handleAssignBatchClick}
                className={isModern
                  ? 'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors'
                  : 'inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-teal-900 bg-white rounded-lg hover:bg-teal-50 transition-colors shadow-sm'}
              >
                <Send className="h-4 w-4" />
                Wyślij do
              </button>
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-12 min-h-[200px]">
            <LoadingSpinner size="lg" />
          </div>
        ) : (() => {
          const paginatedInvoices = useServerPagination
            ? allInvoices
            : (() => {
                const filtered = allInvoices.filter(invoice => {
                  if (!searchQuery) return true;
                  const query = searchQuery.toLowerCase().trim();
                  return (
                    (invoice.supplierNip && invoice.supplierNip.toLowerCase().includes(query)) ||
                    (invoice.supplierName && invoice.supplierName.toLowerCase().includes(query)) ||
                    (invoice.amountGross && invoice.amountGross.toString().includes(query)) ||
                    (invoice.amountNet && invoice.amountNet.toString().includes(query)) ||
                    (invoice.number && invoice.number.toLowerCase().includes(query)) ||
                    (invoice.internalNumber && invoice.internalNumber.toLowerCase().includes(query))
                  );
                });
                const startIndex = currentPage * PAGE_SIZE;
                return filtered.slice(startIndex, startIndex + PAGE_SIZE);
              })();
          
          if (paginatedInvoices.length === 0) {
            return (
              <div className="text-center py-16 px-6">
                <FileText className="mx-auto h-16 w-16 text-gray-300" />
                <h3 className="mt-4 text-lg font-semibold text-gray-900">Brak faktur</h3>
                <p className="mt-2 text-sm text-gray-500">
                  {searchQuery
                    ? 'Nie znaleziono faktur pasujących do wyszukiwania.'
                    : (dateFromFilter || dateToFilter)
                      ? `Brak faktur w wybranym zakresie dat dodania (${dateFromFilter || '…'} – ${dateToFilter || '…'}). Poszerz zakres, aby zobaczyć starsze wpisy.`
                      : 'Nie ma faktur w tej kategorii.'}
                </p>
              </div>
            );
          }
          
          return (
          <div className="w-full overflow-x-auto">
            <table className="min-w-[1280px] w-full divide-y divide-gray-200">
              <colgroup>
                <col style={{ width: 36 }} />
              </colgroup>
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-0.5 py-2 text-center" style={{ width: 36 }}>
                    {isModern ? (
                      <input
                        type="checkbox"
                        onChange={selectAllInvoices}
                        checked={selectedInvoiceIds.length === totalElements && totalElements > 0}
                        disabled={selectingAllInvoices || totalElements === 0}
                        className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed align-middle"
                        title={selectedInvoiceIds.length === totalElements && totalElements > 0 ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={selectAllInvoices}
                        disabled={selectingAllInvoices || totalElements === 0}
                        className="text-[10px] text-teal-600 hover:text-teal-700 font-medium leading-tight disabled:opacity-50 disabled:cursor-not-allowed"
                        title={selectedInvoiceIds.length === totalElements && totalElements > 0 ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
                      >
                        {selectingAllInvoices ? '...' : (selectedInvoiceIds.length === totalElements && totalElements > 0 ? 'Odznacz' : 'Zaznacz')}
                      </button>
                    )}
                  </th>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[90px] whitespace-nowrap">
                    Numer faktury
                  </th>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell min-w-[100px] whitespace-nowrap">
                    Dostawca
                  </th>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider hidden lg:table-cell min-w-[90px] whitespace-nowrap">
                    NIP
                  </th>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider hidden xl:table-cell min-w-[100px] whitespace-nowrap">
                    Data wystawienia
                  </th>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider hidden xl:table-cell min-w-[100px] whitespace-nowrap bg-rose-50/70 border-x border-rose-100">
                    Termin płatności
                  </th>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider hidden lg:table-cell min-w-[110px] whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => {
                        const next = sortDirection === 'desc' ? 'asc' : 'desc';
                        setSortDirection(next);
                        setSortField('createdAt');
                        if (useServerPagination) setCurrentPage(0);
                      }}
                      className="inline-flex items-center gap-1 hover:text-teal-600 transition-colors group"
                      title={sortDirection === 'desc' ? 'Najnowsze na górze – kliknij, aby sortować od najstarszych' : 'Najstarsze na górze – kliknij, aby sortować od najnowszych'}
                    >
                      Data dodania
                      {sortDirection === 'desc' ? (
                        <ChevronDown className="h-3.5 w-3.5 text-gray-500 group-hover:text-teal-600 flex-shrink-0" />
                      ) : (
                        <ChevronUp className="h-3.5 w-3.5 text-gray-500 group-hover:text-teal-600 flex-shrink-0" />
                      )}
                    </button>
                  </th>
                  <th className="px-2 py-2 text-right text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider hidden sm:table-cell min-w-[70px] whitespace-nowrap">
                    Netto
                  </th>
                  <th className="px-2 py-2 text-right text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[70px] whitespace-nowrap">
                    Brutto
                  </th>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[80px] whitespace-nowrap">
                    Status
                  </th>
                  <th className="px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider hidden lg:table-cell min-w-[90px] whitespace-nowrap">
                    Użytkownik
                  </th>
                  <th className="px-1 sm:px-1.5 py-2 text-center text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[140px] w-[7%] sticky right-0 bg-gray-50 z-10">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {paginatedInvoices.map((invoice) => {
                  // Sprawdź czy faktura jest importowana (z KSeF lub z pliku) i nie jest zakończona/przekazana
                  // Faktury zakończone (DONE) i przekazane (ASSIGNED) NIE powinny mieć koloru tła
                  const status = invoice.status ? String(invoice.status).toUpperCase().trim() : '';
                  const isFinishedOrAssigned = status === 'DONE' || status === 'ASSIGNED';
                  const isInProgress = status === 'IN_PROGRESS';
                  const isPending = status === 'PENDING';
                  
                  // Tylko faktury NOWE (NEW) mają zielone tło całej komórki
                  const isNew = status === 'NEW';
                  const isImported = isNew && (invoice.ksefReference || invoice.filePath);
                  
                  // Sprawdź czy faktura jest usunięta
                  const isDeleted = invoice.deleted === true;
                  const isSuspicious = invoice.suspicious === true && !isDeleted;
                  
                  // Dla statusu "W realizacji", "Oczekujące", "Przekazana" komórka ma być biała
                  // Dla statusu "Nowa" cała komórka ma być zielona
                  // Dla usuniętych faktur - czerwone tło
                  // Dla podejrzanych faktur - pomarańczowe tło całego wiersza
                  const rowStyle = isSuspicious
                    ? { backgroundColor: '#ffedd5', background: '#ffedd5' }
                    : (isModern
                      ? (isDeleted ? { backgroundColor: '#fef2f2', background: '#fef2f2' } : {})
                      : (isDeleted
                          ? { backgroundColor: '#fee2e2', background: '#fee2e2' } // bg-red-200
                          : (isFinishedOrAssigned || isInProgress || isPending)
                            ? { backgroundColor: '#ffffff', background: '#ffffff' } 
                            : (isImported ? { backgroundColor: '#dcfce7', background: '#dcfce7' } : {}))); // bg-green-100 = #dcfce7
                  
                  // Usuwamy klasę hover dla faktur zakończonych/przekazanych/w realizacji/oczekujących żeby nie było koloru
                  // Dla usuniętych faktur - czerwone tło bez hover
                  const rowClassName = isSuspicious
                    ? 'border-b border-orange-200'
                    : (isModern
                      ? (isDeleted
                          ? 'border-b border-gray-100 opacity-75'
                          : 'hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100')
                      : (isDeleted
                          ? 'border-b border-red-300 opacity-75'
                          : (isFinishedOrAssigned || isInProgress || isPending)
                            ? 'border-b border-gray-100' 
                            : 'hover:bg-blue-50/30 transition-colors duration-150 border-b border-gray-100'));
                  const isRowSelected = selectedInvoiceIds.includes(invoice.id);
                  const canEditDueDate =
                    user?.role === 'SEKRETARIAT' ||
                    user?.role === 'SUPERADMIN' ||
                    user?.role === 'ADMIN' ||
                    user?.canEditPaymentDueDate === true ||
                    user?.canEditPaymentDueDate === 'true' ||
                    user?.canEditInvoices === true ||
                    user?.canEditInvoices === 'true' ||
                    user?.canAccountInvoice === true ||
                    user?.canAccountInvoice === 'true';
                  return (
                  <React.Fragment key={invoice.id}>
                    <tr
                      className={rowClassName}
                      style={{
                        ...rowStyle,
                        ...(isRowSelected && !isSuspicious ? { backgroundColor: '#f0fdfa', background: '#f0fdfa' } : {}),
                        ...(isRowSelected && isSuspicious ? { backgroundColor: '#fed7aa', background: '#fed7aa' } : {})
                      }}
                      data-invoice-id={invoice.id}
                      data-deleted={isDeleted}
                      data-suspicious={isSuspicious}
                    >
                      <td className="px-0.5 py-1.5 text-center align-middle" style={{ width: 36 }}>
                        {isModern ? (
                          <input
                            type="checkbox"
                            checked={isRowSelected}
                            onChange={() => toggleSelection(invoice.id)}
                            className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer align-middle"
                            aria-label={isRowSelected ? 'Odznacz fakturę' : 'Zaznacz fakturę'}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => toggleSelection(invoice.id)}
                            className="inline-flex items-center justify-center w-3 h-3 rounded-full border transition-all focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-0.5"
                            style={isRowSelected ? { backgroundColor: '#14b8a6', borderColor: '#14b8a6', borderWidth: '1px' } : { backgroundColor: '#fff', borderColor: '#cbd5e1', borderWidth: '1px' }}
                            aria-label={isRowSelected ? 'Odznacz fakturę' : 'Zaznacz fakturę'}
                          />
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <div className="text-xs sm:text-sm font-medium text-gray-900 flex items-center gap-1 min-w-0">
                          <button
                            onClick={() => toggleInvoiceExpansion(invoice.id)}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-0.5 rounded hover:bg-gray-100 flex-shrink-0"
                            aria-label={expandedInvoices.has(invoice.id) ? 'Zwiń' : 'Rozwiń'}
                          >
                            {expandedInvoices.has(invoice.id) ? (
                              <ChevronDown className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            ) : (
                              <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            )}
                          </button>
                          <FileText className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 flex-shrink-0" />
                          <div className="flex flex-col min-w-0">
                            <div className="flex items-center gap-1">
                              {wasInvoiceReturned(invoice.id) && (
                                <span className="text-red-600 font-bold text-xs sm:text-sm" title="Faktura ze zwrotu">Z.</span>
                              )}
                              <span className={`font-semibold break-words min-w-0 text-xs sm:text-sm ${invoice.deleted ? 'line-through text-red-600' : ''}`}>
                                {invoice.number}
                              </span>
                              {invoice.newFromSecretariat && !invoice.deleted && (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] sm:text-[10px] font-bold text-blue-700 bg-blue-100 ring-1 ring-inset ring-blue-200 rounded-full"
                                  title="Nowa faktura przekazana bezpośrednio z sekretariatu"
                                >
                                  <Sparkles className="h-2.5 w-2.5" aria-hidden="true" />
                                  NOWA
                                </span>
                              )}
                              {isSuspicious && (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] sm:text-[10px] font-bold text-orange-800 bg-orange-200 ring-1 ring-inset ring-orange-300 rounded-full"
                                  title={`Podejrzana faktura (do wyjaśnienia)${invoice.suspiciousMarkedByUserName ? ` – oznaczył: ${invoice.suspiciousMarkedByUserName}` : ''}`}
                                >
                                  <AlertTriangle className="h-2.5 w-2.5" aria-hidden="true" />
                                  PODEJRZANA
                                </span>
                              )}
                              {invoice.deleted && (
                                <span className="px-1 py-0.5 text-[9px] sm:text-[10px] font-semibold text-red-700 bg-red-200 rounded" title="Faktura została usunięta">
                                  USUNIĘTA
                                </span>
                              )}
                              {invoice.ksefReference && !invoice.deleted && (
                                <span className="px-1 py-0.5 text-[9px] sm:text-[10px] font-semibold text-green-700 bg-green-100 rounded" title={`Faktura z KSeF: ${invoice.ksefReference}`}>
                                  KSeF
                                </span>
                              )}
                              {(Number(invoice.amountGross) < 0 || Number(invoice.amountNet) < 0) && !invoice.deleted && (
                                <span className="px-1 py-0.5 text-[9px] sm:text-[10px] font-semibold text-amber-800 bg-amber-100 rounded" title="Faktura korekty (ujemna kwota)">
                                  Korekta
                                </span>
                              )}
                              {((invoice.attachmentCount ?? 0) > 0) && (
                                <span className="flex items-center text-gray-500" title={`Załączniki (${invoice.attachmentCount ?? invoiceAttachments[invoice.id]?.length ?? 0})`}>
                                  <Paperclip className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-green-600" />
                                  <span className="ml-0.5 text-[9px] sm:text-[10px] font-medium text-green-700">{invoice.attachmentCount ?? invoiceAttachments[invoice.id]?.length ?? 0}</span>
                                </span>
                              )}
                            </div>
                            {invoice.internalNumber && (
                              isModern ? (
                                <div className={`text-[10px] sm:text-xs mt-0.5 truncate ${isDeleted ? 'line-through text-red-500' : 'text-gray-500'}`} title={`Nr wewnętrzny: ${invoice.internalNumber}`}>
                                  Nr wew.: <span className="font-medium">{invoice.internalNumber}</span>
                                </div>
                              ) : (
                              <div className={`text-[10px] sm:text-xs mt-0.5 ${invoice.deleted ? 'line-through text-red-500' : 'text-gray-500'}`}>
                                <div>Nr wewnetrzny:</div>
                                <div className="font-medium">{invoice.internalNumber}</div>
                              </div>
                              )
                            )}
                          </div>
                        </div>
                        {/* Dodatkowe informacje na małych ekranach */}
                        <div className="md:hidden mt-1 space-y-0.5 text-xs text-gray-600">
                          {invoice.supplierName && (
                            <div className="break-words" title={invoice.supplierName}>
                              {invoice.supplierName}
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            {formatDate(invoice.dueDate) && (
                              <span>Termin: {formatDate(invoice.dueDate)}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2 hidden md:table-cell">
                        <div className={`text-xs sm:text-sm break-words line-clamp-2 ${invoice.deleted ? 'line-through text-red-600' : 'text-gray-900'}`} title={invoice.supplierName}>
                          {invoice.supplierName || <span className="text-gray-400">-</span>}
                        </div>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap hidden lg:table-cell">
                        <div className="text-xs sm:text-sm text-gray-700 font-mono">
                          {invoice.supplierNip || <span className="text-gray-400">-</span>}
                        </div>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap hidden xl:table-cell">
                        <div className="text-xs sm:text-sm text-gray-700">
                          {formatDate(invoice.issueDate)}
                        </div>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap hidden xl:table-cell border-x border-rose-100">
                        {editingDueDate === invoice.id && canEditDueDate ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="date"
                              value={editingDueDateValue}
                              onChange={(e) => setEditingDueDateValue(e.target.value)}
                              className="text-xs sm:text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              onBlur={async () => {
                                try {
                                  try {
                                    await api.patch(`/invoices/${invoice.id}/due-date`, {
                                      dueDate: editingDueDateValue || ''
                                    });
                                  } catch (err) {
                                    if (err.response?.status === 404) {
                                      await api.patch(`/invoices/${invoice.id}/created-at`, {
                                        dueDate: editingDueDateValue || ''
                                      });
                                    } else throw err;
                                  }
                                  toast.success(editingDueDateValue ? 'Termin płatności został zaktualizowany' : 'Termin płatności został usunięty');
                                  setAllInvoices(prev => prev.map(inv => inv.id === invoice.id ? { ...inv, dueDate: editingDueDateValue || null } : inv));
                                  setEditingDueDate(null);
                                  setEditingDueDateValue('');
                                } catch (error) {
                                  console.error('Błąd podczas aktualizacji terminu płatności:', error);
                                  const status = error.response?.status;
                                  if (status === 400) {
                                    toast.error('Nieprawidłowa data lub backend wymaga aktualizacji');
                                  } else if (status === 403) {
                                    toast.error('Brak uprawnień do edycji terminu płatności');
                                  } else {
                                    toast.error('Nie udało się zaktualizować terminu płatności');
                                  }
                                  setEditingDueDate(null);
                                  setEditingDueDateValue('');
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') e.target.blur();
                                else if (e.key === 'Escape') {
                                  setEditingDueDate(null);
                                  setEditingDueDateValue('');
                                }
                              }}
                              autoFocus
                            />
                          </div>
                        ) : (
                          <div
                            className={`text-xs sm:text-sm text-gray-700 whitespace-nowrap ${canEditDueDate ? 'cursor-pointer hover:text-rose-700' : ''}`}
                            onClick={() => {
                              if (canEditDueDate) {
                                setEditingDueDateValue(invoice.dueDate ? invoice.dueDate : '');
                                setEditingDueDate(invoice.id);
                              }
                            }}
                            title={canEditDueDate ? 'Kliknij, aby edytować termin płatności' : ''}
                          >
                            {formatDate(invoice.dueDate) ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-md bg-rose-50 text-rose-700 font-medium">
                                {formatDate(invoice.dueDate)}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap hidden lg:table-cell">
                        <div className="text-xs sm:text-sm text-gray-700">
                          {invoice.createdAt ? formatHistoryDate(invoice.createdAt) : '-'}
                        </div>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-right hidden sm:table-cell">
                        <div className="text-xs sm:text-sm text-gray-700">
                          {formatCurrency(invoice.amountNet, invoice.currency)}
                        </div>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-right">
                        <div className={`text-xs sm:text-sm font-semibold ${invoice.deleted ? 'line-through text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(invoice.amountGross, invoice.currency)}
                        </div>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <InvoiceStatusCell
                          status={invoice.status}
                          isDeleted={invoice.deleted}
                          isSuperAdmin={user?.role === 'SUPERADMIN'}
                          isModern={isModern}
                          isUpdating={statusUpdatingId === invoice.id}
                          onStatusChange={(newStatus) => handleStatusChange(invoice.id, newStatus)}
                        />
                      </td>
                      <td className="px-2 py-2 hidden lg:table-cell">
                        {invoice.assignedUserName ? (
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <div className="flex items-start gap-1 min-w-0">
                              <User className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                              <div className="flex flex-col min-w-0">
                                {isModern ? (
                                  <span className="block max-w-[150px] text-xs sm:text-sm text-gray-700 truncate" title={invoice.assignedUserName}>
                                    {invoice.assignedUserName}
                                  </span>
                                ) : (() => {
                                  const nameParts = invoice.assignedUserName.split(' ');
                                  const firstName = nameParts[0] || '';
                                  const lastName = nameParts.slice(1).join(' ') || '';
                                  return (
                                    <>
                                      <span className="text-xs sm:text-sm text-gray-700 break-words">{firstName}</span>
                                      {lastName && <span className="text-xs sm:text-sm text-gray-700 break-words">{lastName}</span>}
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                            {invoice.status === 'IN_PROGRESS' && (
                              <span className="text-[10px] sm:text-xs text-amber-600 italic ml-4">
                                (w realizacji)
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs sm:text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className={`px-0.5 sm:px-1 py-2 whitespace-nowrap sticky right-0 z-10 min-w-[140px] ${isSuspicious ? 'bg-orange-100' : 'bg-white'}`}>
                        <div className="flex items-center justify-center gap-0 flex-wrap">
                          {(invoice.filePath || invoice.ksefReference) && (
                            <button
                              onClick={() => handleViewPdf(invoice)}
                              className="p-0.5 sm:p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors duration-150 flex-shrink-0"
                              title={invoice.ksefReference ? "Podgląd faktury z KSeF" : "Podgląd faktury"}
                            >
                              <Eye className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            </button>
                          )}
                          {(invoice.canMarkSuspicious || invoice.canUnmarkSuspicious) && !isDeleted && (
                            <button
                              onClick={() => handleToggleSuspicious(invoice)}
                              className={`p-0.5 sm:p-1 rounded transition-colors duration-150 flex-shrink-0 ${
                                invoice.suspicious
                                  ? 'text-orange-700 hover:text-orange-800 hover:bg-orange-100'
                                  : 'text-gray-500 hover:text-orange-700 hover:bg-orange-50'
                              }`}
                              title={
                                invoice.canUnmarkSuspicious
                                  ? 'Usuń oznaczenie podejrzanej faktury'
                                  : 'Oznacz jako podejrzaną (do wyjaśnienia)'
                              }
                            >
                              <AlertTriangle className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${invoice.suspicious ? 'fill-orange-200' : ''}`} />
                            </button>
                          )}
                          {invoice.status === 'PENDING' && invoice.assignedUserId === user?.id && (
                            <button
                              onClick={() => handleMoveToInProgress(invoice)}
                              className="p-0.5 sm:p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors duration-150 flex-shrink-0"
                              title="Przenieś do realizacji"
                            >
                              <Play className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            </button>
                          )}
                          {invoice.status === 'ASSIGNED' && invoice.assignedUserId === user?.id && (
                            <>
                            <button
                              onClick={() => handleReturnClick(invoice)}
                              className="p-0.5 sm:p-1 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded transition-colors duration-150 flex-shrink-0"
                              title="Zwróć fakturę"
                            >
                              <CornerUpLeft className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            </button>
                            {invoice.newFromSecretariat && user?.role !== 'SUPERADMIN' && (
                              <button
                                onClick={() => handleAssignClick(invoice)}
                                className="p-0.5 sm:p-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded transition-colors duration-150 flex-shrink-0"
                                title="Przekaż dalej"
                              >
                                <Send className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                              </button>
                            )}
                            </>
                          )}
                          {invoice.status === 'IN_PROGRESS' && (
                            <>
                              <button
                                onClick={() => handleMoveToPending(invoice)}
                                className="p-0.5 sm:p-1 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded transition-colors duration-150 flex-shrink-0"
                                title="Przenieś do oczekujących"
                              >
                                <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                              </button>
                              {invoice.assignedUserId === user?.id && (
                                <>
                              <button
                                onClick={() => handleRevertClick(invoice)}
                                className="p-0.5 sm:p-1 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors duration-150 flex-shrink-0"
                                title="Cofnij do listy faktur"
                              >
                                <ArrowLeft className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                              </button>
                              <button
                                onClick={() => handleEditClick(invoice)}
                                className="p-0.5 sm:p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors duration-150 flex-shrink-0"
                                title="Opisz fakturę"
                              >
                                <Edit2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                              </button>
                              <button
                                onClick={() => handleAssignClick(invoice)}
                                className="p-0.5 sm:p-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded transition-colors duration-150 flex-shrink-0"
                                title="Przekaż dalej"
                              >
                                <Send className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                              </button>
                              {(user?.canAccountInvoice || user?.role === 'SUPERADMIN') && (
                                <button
                                  onClick={() => handleAccountInvoice(invoice)}
                                  className="p-0.5 sm:p-1 text-green-700 hover:text-green-800 hover:bg-green-100 rounded transition-colors duration-150 flex-shrink-0"
                                  title="Zaksięgowuj fakturę"
                                >
                                  <CheckCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                                </button>
                              )}
                                </>
                              )}
                            </>
                          )}
                          {/* Sekretariat może przekazywać faktury NEW (nieprzypisane) */}
                          {user?.role === 'SEKRETARIAT' && 
                           invoice.status === 'NEW' && 
                           !invoice.assignedUserId && (
                            <button
                              onClick={() => handleAssignClick(invoice)}
                              className="p-0.5 sm:p-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded transition-colors duration-150 flex-shrink-0"
                              title="Przekaż dalej"
                            >
                              <Send className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            </button>
                          )}
                          {/* Użytkownicy z uprawnieniem canViewSignedInvoices: Przyjmij do siebie + Przekaż dalej */}
                          {user?.canViewSignedInvoices && 
                           invoice.status === 'SIGNED' && (
                            <>
                              <button
                                onClick={() => handleAcceptToSelf(invoice)}
                                className="p-0.5 sm:p-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded transition-colors duration-150 flex-shrink-0"
                                title="Przyjmij do siebie"
                              >
                                <UserPlus className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                              </button>
                              <button
                                onClick={() => handleAssignClick(invoice)}
                                className="p-0.5 sm:p-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded transition-colors duration-150 flex-shrink-0"
                                title="Przekaż dalej"
                              >
                                <Send className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                              </button>
                            </>
                          )}
                          {/* Superadmin może przekazywać faktury niezależnie od statusu (oprócz DONE) */}
                          {user?.role === 'SUPERADMIN' && 
                           invoice.status !== 'DONE' && (
                            <button
                              onClick={() => handleAssignClick(invoice)}
                              className="p-0.5 sm:p-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded transition-colors duration-150 flex-shrink-0"
                              title="Przekaż dalej"
                            >
                              <Send className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            </button>
                          )}
                          {invoice.status === 'ASSIGNED' && (user?.canAccountInvoice || user?.role === 'SUPERADMIN') && (
                            <button
                              onClick={() => handleAccountInvoice(invoice)}
                              className="p-0.5 sm:p-1 text-green-700 hover:text-green-800 hover:bg-green-100 rounded transition-colors duration-150 flex-shrink-0"
                              title="Zaksięgowuj fakturę"
                            >
                              <CheckCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            </button>
                          )}
                          {user?.role === 'SUPERADMIN' && (
                            <button
                              onClick={() => handleDeleteInvoiceClick(invoice.id)}
                              className="p-0.5 sm:p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors duration-150 flex-shrink-0"
                              title="Usuń fakturę"
                            >
                              <Trash2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedInvoices.has(invoice.id) && (
                      <tr>
                        <td colSpan={11} className="px-3 sm:px-4 md:px-6 py-4 sm:py-5 md:py-6 bg-gray-50/50">
                          <div className="ml-2 sm:ml-4 md:ml-8 space-y-4 sm:space-y-5">
                            {/* Podpisy elektroniczne dokumentu - wyświetl wszystkie podpisy obok siebie ze strzałkami */}
                            {invoiceSignatures[invoice.id] && invoiceSignatures[invoice.id].length > 0 && (
                              <div className={isModern ? 'bg-white p-3 sm:p-4 rounded-xl border border-gray-100 shadow-sm' : 'bg-white p-2 sm:p-3 rounded-lg border border-gray-200 shadow-sm'}>
                                <h4 className={isModern ? 'text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2' : 'text-xs sm:text-sm font-normal text-gray-900 mb-2'}>
                                  {isModern && (
                                    <span className="h-6 w-6 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0">
                                      <PenTool className="h-3.5 w-3.5" />
                                    </span>
                                  )}
                                  Podpisy elektroniczne
                                </h4>
                                <div className={isModern ? 'flex flex-wrap items-start gap-2' : 'flex flex-wrap items-start gap-1.5 sm:gap-2'}>
                                  {(() => {
                                    // Grupuj podpisy po signatureGroupId (jeśli dostępny) lub po użytkowniku i zaokrąglonej dacie
                                    const uniqueSignatures = new Map();
                                    invoiceSignatures[invoice.id].forEach(sig => {
                                      if (sig.user && sig.signedAt) {
                                        let key;
                                        if (sig.signatureGroupId) {
                                          key = sig.signatureGroupId;
                                        } else {
                                          const roundedDate = new Date(sig.signedAt).toISOString().substring(0, 19);
                                          key = `${sig.user.id}_${roundedDate}`;
                                        }
                                        if (!uniqueSignatures.has(key)) {
                                          uniqueSignatures.set(key, sig);
                                        }
                                      }
                                    });
                                    
                                    // Sortuj po dacie (najstarsze pierwsze)
                                    const sortedSignatures = Array.from(uniqueSignatures.values()).sort((a, b) => {
                                      return new Date(a.signedAt) - new Date(b.signedAt);
                                    });
                                    
                                    return sortedSignatures.map((sig, idx) => {
                                      // Znajdź opis faktury dla tego podpisu
                                      // Najpierw szukaj po documentSignatureId, potem po userId
                                      let description = null;
                                      
                                      if (invoiceDescriptions[invoice.id]) {
                                        // Najpierw szukaj po documentSignatureId
                                        description = invoiceDescriptions[invoice.id].find(desc => {
                                          if (desc.documentSignatureId && sig.id) {
                                            return String(desc.documentSignatureId) === String(sig.id);
                                          }
                                          return false;
                                        });
                                        
                                        // Jeśli nie znaleziono po documentSignatureId, szukaj po userId i czasie (szerszy zakres dla starych faktur)
                                        if (!description && sig.user?.id) {
                                          description = invoiceDescriptions[invoice.id].find(desc => {
                                            if (!desc.userId || String(desc.userId) !== String(sig.user.id)) return false;
                                            // Nie szukaj jeśli desc ma już documentSignatureId powiązane z innym podpisem
                                            if (desc.documentSignatureId && String(desc.documentSignatureId) !== String(sig.id)) return false;
                                            const timeDiff = Math.abs(new Date(desc.createdAt || desc.updatedAt || 0) - new Date(sig.signedAt));
                                            return timeDiff < 900000; // 15 minut dla starych faktur
                                          });
                                        }
                                        
                                        // Jeśli nadal nie znaleziono, szukaj najbliższego opisu tego użytkownika (dla bardzo starych faktur)
                                        if (!description && sig.user?.id) {
                                          const userDescriptions = invoiceDescriptions[invoice.id].filter(desc => 
                                            desc.userId && String(desc.userId) === String(sig.user.id) &&
                                            (!desc.documentSignatureId || String(desc.documentSignatureId) === String(sig.id))
                                          );
                                          
                                          if (userDescriptions.length > 0) {
                                            // Znajdź najbliższy czasowo opis
                                            const sigTime = new Date(sig.signedAt).getTime();
                                            let closestDesc = null;
                                            let minTimeDiff = Infinity;
                                            
                                            userDescriptions.forEach(desc => {
                                              const descTime = new Date(desc.createdAt || desc.updatedAt || 0).getTime();
                                              const timeDiff = Math.abs(descTime - sigTime);
                                              // Maksymalnie 1 godzina różnicy
                                              if (timeDiff < 3600000 && timeDiff < minTimeDiff) {
                                                minTimeDiff = timeDiff;
                                                closestDesc = desc;
                                              }
                                            });
                                            
                                            if (closestDesc) {
                                              description = closestDesc;
                                            }
                                          }
                                        }
                                      }
                                      
                                      // Sprawdź czy to podpis załącznika
                                      const isAttachmentSignature = sig.attachmentId != null;
                                      const isCrossedOut = sig.crossedOut === true;
                                      const isRealizacjiOnly = description && description.zatwierdzonoDo === 'REALIZACJI' && !description.metricalChecked && !description.preliminaryCheckDone && !description.formalnoRachunkowoChecked && !isAttachmentSignature;
                                      const bgColorExpanded = isCrossedOut ? 'bg-orange-50' : (isAttachmentSignature ? 'bg-blue-50' : (isRealizacjiOnly ? 'bg-emerald-50' : 'bg-green-50'));
                                      const borderColorExpanded = isCrossedOut ? 'border-orange-300' : (isAttachmentSignature ? 'border-blue-200' : (isRealizacjiOnly ? 'border-emerald-300' : 'border-green-200'));
                                      const iconColorExpanded = isCrossedOut ? 'text-orange-600' : (isAttachmentSignature ? 'text-blue-600' : (isRealizacjiOnly ? 'text-emerald-600' : 'text-green-600'));
                                      const arrowColorExpanded = isCrossedOut ? 'text-orange-600' : (isAttachmentSignature ? 'text-blue-600' : (isRealizacjiOnly ? 'text-emerald-600' : 'text-green-600'));
                                      const borderTopColorExpanded = isCrossedOut ? 'border-orange-300' : (isAttachmentSignature ? 'border-blue-300' : (isRealizacjiOnly ? 'border-emerald-400' : 'border-green-300'));
                                      
                                      const signatureUserId = sig.user?.id || sig.userId;
                                      // Sprawdź uprawnienia - tylko użytkownicy z canDeleteDescriptionsAndSignatures === true lub SUPERADMIN
                                      const canDeleteSignature = user && (
                                        user.canDeleteDescriptionsAndSignatures === true || 
                                        user.role === 'SUPERADMIN'
                                      ) && !isCrossedOut;
                                      
                                      // Funkcja pomocnicza do formatowania daty w kompaktowy sposób
                                      const formatCompactDate = (date) => {
                                        const d = new Date(date);
                                        return `${d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: '2-digit' })}, ${d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`;
                                      };
                                      
                                      // Upewnij się, że mamy najaktualniejszy opis (ponownie sprawdź jeśli nie znaleziono wcześniej)
                                      let descToShow = description;
                                      
                                      // Jeśli nie ma opisu, spróbuj jeszcze raz znaleźć (dla starych faktur)
                                      if (!descToShow && invoiceDescriptions[invoice.id] && sig.user?.id) {
                                        descToShow = invoiceDescriptions[invoice.id].find(desc => {
                                          if (!desc.userId || String(desc.userId) !== String(sig.user.id)) return false;
                                          if (desc.documentSignatureId && String(desc.documentSignatureId) !== String(sig.id)) return false;
                                          const timeDiff = Math.abs(new Date(desc.createdAt || desc.updatedAt || 0) - new Date(sig.signedAt));
                                          return timeDiff < 3600000; // Do 1 godziny różnicy
                                        });
                                      }
                                      
                                      return (
                                        <React.Fragment key={sig.signatureGroupId || sig.id || `${signatureUserId}_${sig.signedAt}` || idx}>
                                          <div
                                            className={isModern
                                            ? `bg-white border border-gray-100 border-l-4 ${isCrossedOut ? 'border-l-orange-400 opacity-80' : (isAttachmentSignature ? 'border-l-blue-400' : (isRealizacjiOnly ? 'border-l-emerald-400' : 'border-l-green-400'))} rounded-lg p-2 shadow-sm hover:shadow-md transition-shadow flex-shrink-0 w-fit max-w-[200px] relative`
                                            : `${bgColorExpanded} border ${borderColorExpanded} rounded-md p-2 flex-shrink-0 min-w-[160px] sm:min-w-[180px] max-w-[220px] relative ${isCrossedOut ? 'opacity-80' : ''}`}
                                            title={isCrossedOut ? (sig.crossedOutReason || 'Podpis skreślony przy zwrocie faktury') : undefined}
                                          >
                                            {isCrossedOut && (
                                              <div className="absolute -top-1.5 left-1 px-1 py-0.5 text-[9px] font-bold text-orange-800 bg-orange-200 rounded z-10">
                                                SKREŚLONY
                                              </div>
                                            )}
                                            {canDeleteSignature && (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteSignatureClick(invoice.id, sig.id);
                                                }}
                                                className="absolute top-0.5 right-0.5 p-0.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors duration-150 flex-shrink-0 z-10 bg-white shadow-sm"
                                                title="Usuń podpis"
                                              >
                                                <Trash2 className="h-3 w-3" />
                                              </button>
                                            )}
                                            <div className={`flex items-start gap-1.5 ${isCrossedOut ? 'line-through decoration-orange-500' : ''}`}>
                                              <Lock className={`h-3 w-3 ${iconColorExpanded} mt-0.5 flex-shrink-0`} />
                                              <div className="flex-1 min-w-0">
                                                {/* SPRAWDZONO POD WZGLĘDEM MERYTORYCZNYM/FORMALNO-RACHUNKOWYM przez [imię nazwisko], [data], [godzina] - podpisano elektronicznie */}
                                                {(() => {
                                                  
                                                  const userName = sig.user ? `${sig.user.firstName} ${sig.user.lastName}` : 'nieznany';
                                                  const dateStr = formatCompactDate(sig.signedAt);
                                                  
                                                  return descToShow && descToShow.metricalChecked ? (
                                                    <div className="text-xs font-normal text-gray-900 mb-1 leading-tight">
                                                      <div className="font-normal text-green-700">SPRAWDZONO MERYTORYCZNYM</div>
                                                      <div className="text-gray-600 mt-0.5">{userName}</div>
                                                      <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                                                    </div>
                                                  ) : descToShow && descToShow.preliminaryCheckDone ? (
                                                    <div className="text-xs font-normal text-gray-900 mb-1 leading-tight">
                                                      <div className="font-normal text-blue-700">WSTĘPNA KONTROLA</div>
                                                      <div className="text-gray-600 mt-0.5">{userName}</div>
                                                      <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                                                    </div>
                                                  ) : descToShow && descToShow.formalnoRachunkowoChecked ? (
                                                    <div className="text-xs font-normal text-gray-900 mb-1 leading-tight">
                                                      <div className="font-normal text-purple-700">SPRAWDZONO FORMALNO-RACHUNKOWO</div>
                                                      <div className="text-gray-600 mt-0.5">{userName}</div>
                                                      <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                                                    </div>
                                                  ) : descToShow && descToShow.zatwierdzonoDo === 'REALIZACJI' ? (
                                                    <div className="text-xs font-normal text-gray-900 mb-1 leading-tight">
                                                      <div className="font-normal text-red-700">ZATWIERDZONO DO REALIZACJI</div>
                                                      <div className="text-gray-600 mt-0.5">{userName}</div>
                                                      <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                                                    </div>
                                                  ) : isAttachmentSignature ? (
                                                    <div className="text-xs font-normal text-gray-900 mb-1 leading-tight">
                                                      <div className="font-normal text-blue-700">ZAŁĄCZNIK</div>
                                                      <div className="text-gray-600 mt-0.5 truncate">{sig.attachmentFileName || 'załącznik'}</div>
                                                      <div className="text-gray-600 text-[10px] mt-0.5">{userName}</div>
                                                      <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                                                    </div>
                                                  ) : (
                                                    <div className="text-xs font-normal text-gray-900 mb-1 leading-tight">
                                                      <div className="font-normal text-gray-700">PODPISANO</div>
                                                      <div className="text-gray-600 mt-0.5">{userName}</div>
                                                      <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                                                    </div>
                                                  );
                                                })()}
                                                
                                                {/* ZATWIERDZONO DO REALIZACJI - pod kartą gdy jest też formalno-rachunkowo (dodatkowa linia) */}
                                                {(() => {
                                                  let descForRealiz = descToShow || description;
                                                  if (!descForRealiz && invoiceDescriptions[invoice.id] && sig.user?.id) {
                                                    descForRealiz = invoiceDescriptions[invoice.id].find(d => {
                                                      if (d.documentSignatureId && sig.id && String(d.documentSignatureId) === String(sig.id)) return true;
                                                      if (!d.documentSignatureId && d.userId && String(d.userId) === String(sig.user.id)) {
                                                        const timeDiff = Math.abs(new Date(d.createdAt || d.updatedAt || 0) - new Date(sig.signedAt));
                                                        return timeDiff < 3600000; // Do 1 godziny różnicy dla starych faktur
                                                      }
                                                      return false;
                                                    });
                                                  }
                                                  return descForRealiz && descForRealiz.formalnoRachunkowoChecked && descForRealiz.zatwierdzonoDo === 'REALIZACJI' ? (
                                                    <div className="mt-1.5 pt-1.5 border-t border-emerald-400">
                                                      <div className="text-[10px] font-normal text-purple-700">✓ ZATWIERDZONO DO REALIZACJI</div>
                                                    </div>
                                                  ) : null;
                                                })()}
                                                
                                                {/* Informacje księgowości – tylko dla podpisu faktury, nie załącznika */}
                                                {(() => {
                                                  if (isAttachmentSignature) return null;
                                                  // Użyj descToShow jeśli już jest znaleziony, w przeciwnym razie użyj description
                                                  let descForAccounting = descToShow || description;
                                                  if (!descForAccounting && invoiceDescriptions[invoice.id] && sig.user?.id) {
                                                    descForAccounting = invoiceDescriptions[invoice.id].find(desc => {
                                                      if (desc.documentSignatureId && sig.id && String(desc.documentSignatureId) === String(sig.id)) {
                                                        return true;
                                                      }
                                                      if (!desc.documentSignatureId && desc.userId && 
                                                          String(desc.userId) === String(sig.user.id)) {
                                                        const timeDiff = Math.abs(new Date(desc.createdAt || desc.updatedAt || 0) - new Date(sig.signedAt));
                                                        return timeDiff < 3600000; // Do 1 godziny różnicy dla starych faktur
                                                      }
                                                      return false;
                                                    });
                                                  }
                                                  
                                                  return descForAccounting && descForAccounting.formalnoRachunkowoChecked ? (
                                                    <div className={`mt-1.5 pt-1.5 border-t ${borderTopColorExpanded}`}>
                                                      {descForAccounting.zatwierdzonoDo && descForAccounting.zatwierdzonoDo !== 'REALIZACJI' && (
                                                        <div className="text-[10px] font-medium text-gray-700 mb-0.5">
                                                          DO: <strong className="text-purple-700">{descForAccounting.zatwierdzonoDo}</strong>
                                                        </div>
                                                      )}
                                                      {descForAccounting.kwota && (
                                                        <div className="text-[10px] font-medium text-gray-700">
                                                          <strong className="text-purple-700">{descForAccounting.kwota} PLN</strong>
                                                          {descForAccounting.kwotaSlownie && (
                                                            <span className="ml-1 text-gray-500 text-[9px]">({descForAccounting.kwotaSlownie.substring(0, 40)}...)</span>
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>
                                                  ) : null;
                                                })()}
                                                
                                                {/* Typ płatności – dla podpisów merytorycznych */}
                                                {!isAttachmentSignature && descToShow && descToShow.invoicePaymentType && (
                                                  <div className={`mt-1 pt-1 border-t ${borderTopColorExpanded} text-[10px] text-gray-700`}>
                                                    <span className="font-medium">Typ płatności: </span>
                                                    {descToShow.invoicePaymentType === 'CASH' ? 'Faktura gotówkowa' : descToShow.invoicePaymentType === 'TRANSFER' ? 'Przelew' : descToShow.invoicePaymentType}
                                                  </div>
                                                )}
                                                
                                                {/* Opis faktury – tylko dla podpisu faktury, NIE dla podpisu załącznika */}
                                                {(() => {
                                                  if (isAttachmentSignature) return null;
                                                  // Najpierw sprawdź czy jest opis powiązany z podpisem przez documentSignatureId
                                                  let descToShow = description;
                                                  
                                                  // Jeśli nie ma opisu powiązanego z podpisem, znajdź opis użytkownika
                                                  if (!descToShow && invoiceDescriptions[invoice.id] && sig.user?.id) {
                                                    descToShow = invoiceDescriptions[invoice.id].find(desc => {
                                                      if (!desc.description || !desc.description.trim()) return false;
                                                      // Porównaj userId jako stringi
                                                      if (desc.userId) {
                                                        return String(desc.userId) === String(sig.user.id);
                                                      }
                                                      return false;
                                                    });
                                                  }
                                                  
                                                  // Wyświetl opis jeśli istnieje
                                                  const canDeleteDesc = descToShow && user && (
                                                    user.canDeleteDescriptionsAndSignatures === true || 
                                                    user.role === 'SUPERADMIN'
                                                  );
                                                  
                                                  return descToShow && descToShow.description ? (
                                                    <div className={`mt-1.5 pt-1.5 border-t ${borderTopColorExpanded}`}>
                                                      <div className="flex items-start justify-between gap-1">
                                                        <div className="text-[10px] font-normal text-gray-700 flex-1 min-w-0">
                                                          <div className="truncate" title={descToShow.description}>
                                                            {descToShow.description.length > 50 ? `${descToShow.description.substring(0, 50)}...` : descToShow.description}
                                                          </div>
                                                          {descToShow.costCenter && (
                                                            <span className="block mt-0.5 font-normal text-yellow-700 text-[10px]">
                                                              OŚR. KOSZTÓW: {descToShow.costCenter}
                                                            </span>
                                                          )}
                                                        </div>
                                                        {canDeleteDesc && (
                                                          <button
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              handleDeleteDescriptionClick(invoice.id, descToShow.id);
                                                            }}
                                                            className="ml-1 p-0.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors duration-150 flex-shrink-0"
                                                            title="Usuń opis"
                                                          >
                                                            <Trash2 className="h-2.5 w-2.5" />
                                                          </button>
                                                        )}
                                                      </div>
                                                    </div>
                                                  ) : null;
                                                })()}
                                              </div>
                                            </div>
                                          </div>
                                          {idx < sortedSignatures.length - 1 && (
                                            <div className="flex items-center justify-center flex-shrink-0 px-0.5">
                                              <ArrowRight className={`h-3.5 w-3.5 ${isModern ? 'text-gray-300' : arrowColorExpanded}`} />
                                            </div>
                                          )}
                                        </React.Fragment>
                                      );
                                    });
                                  })()}
                                </div>
                              </div>
                            )}
                            
                            {/* Załączniki faktury */}
                            <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200 shadow-sm mt-4">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-normal text-gray-900 flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-gray-600" />
                                  Załączniki {invoiceAttachments[invoice.id] && invoiceAttachments[invoice.id].length > 0 && `(${invoiceAttachments[invoice.id].length})`}
                                </h4>
                                {/* Przycisk dodawania załącznika - dostępny dla faktur z KSeF lub z pliku */}
                                {(invoice.ksefReference || invoice.filePath) && (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="file"
                                      id={`attachment-upload-${invoice.id}`}
                                      accept=".pdf,.png,.jpg,.jpeg"
                                      onChange={(e) => handleAttachmentUploadFromList(e, invoice.id)} // eslint-disable-line no-undef
                                      className="hidden"
                                    />
                                    <label
                                      htmlFor={`attachment-upload-${invoice.id}`}
                                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors cursor-pointer bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                                      title="Dodaj załącznik"
                                    >
                                      <Upload className="h-3.5 w-3.5" />
                                      Dodaj załącznik
                                    </label>
                                  </div>
                                )}
                              </div>
                              {invoiceAttachments[invoice.id] && invoiceAttachments[invoice.id].length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                  {invoiceAttachments[invoice.id].map((attachment) => {
                                    // Sprawdź czy załącznik jest podpisany elektronicznie (nieskreślony podpis)
                                    const signaturesForInvoice = invoiceSignatures[invoice.id] || [];
                                    const isSigned = signaturesForInvoice.some(
                                      sig => {
                                        if (sig.crossedOut === true) {
                                          return false;
                                        }
                                        // Porównaj jako liczby, aby uniknąć problemów z typami
                                        const sigAttachmentId = sig.attachmentId != null ? Number(sig.attachmentId) : null;
                                        const attId = attachment.id != null ? Number(attachment.id) : null;
                                        const matches = sigAttachmentId !== null && attId !== null && sigAttachmentId === attId;
                                        if (matches) {
                                          console.log(`✅ Załącznik ${attachment.id} (${attachment.originalFileName}) jest podpisany!`, {
                                            signatureId: sig.id,
                                            attachmentId: sig.attachmentId,
                                            attachmentFileName: sig.attachmentFileName
                                          });
                                        }
                                        return matches;
                                      }
                                    ) || false;
                                    
                                    // Debug: loguj dla pierwszego załącznika pierwszej faktury
                                    if (signaturesForInvoice.length > 0 && attachment.id === invoiceAttachments[invoice.id]?.[0]?.id && invoice.id === allInvoices[0]?.id) {
                                      console.log(`📋 Podpisy dla faktury ${invoice.id}:`, signaturesForInvoice.map(s => ({
                                        id: s.id,
                                        attachmentId: s.attachmentId,
                                        attachmentFileName: s.attachmentFileName
                                      })));
                                      console.log(`📎 Załączniki dla faktury ${invoice.id}:`, invoiceAttachments[invoice.id].map(a => ({
                                        id: a.id,
                                        originalFileName: a.originalFileName
                                      })));
                                      console.log(`🔍 Sprawdzanie załącznika ${attachment.id}:`, {
                                        attachmentId: attachment.id,
                                        isSigned: isSigned,
                                        matchingSignatures: signaturesForInvoice.filter(s => Number(s.attachmentId) === Number(attachment.id))
                                      });
                                    }
                                    
                                    return (
                                    <div
                                      key={attachment.id}
                                      className={`flex items-center justify-between p-2 border rounded-lg transition-colors ${
                                        isSigned 
                                          ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' 
                                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                      }`}
                                    >
                                      <div className="flex flex-col flex-1 min-w-0">
                                        <div className="flex items-center gap-2 min-w-0">
                                          <FileText className={`h-4 w-4 flex-shrink-0 ${isSigned ? 'text-blue-600' : 'text-gray-500'}`} />
                                          <span className={`text-xs truncate ${isSigned ? 'text-blue-700 font-normal' : 'text-gray-700'}`} title={attachment.originalFileName}>
                                            {attachment.originalFileName}
                                          </span>
                                          {attachment.source === 'KSEF' && (
                                            <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 bg-amber-100 rounded" title="Załącznik z faktury KSeF">KSeF</span>
                                          )}
                                        </div>
                                        {(attachment.addedByFirstName != null || attachment.addedByLastName != null) && (
                                          <span className="text-[10px] text-gray-500 mt-0.5 ml-6">
                                            dodane przez: {[attachment.addedByFirstName, attachment.addedByLastName].filter(Boolean).join(' ')}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => {
                                            // Otwórz podgląd załącznika w nowym oknie
                                            window.open(`/app/attachments/${attachment.id}/preview`, '_blank');
                                          }}
                                          className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                                          title="Podgląd załącznika"
                                        >
                                          <Eye className="h-3 w-3" />
                                        </button>
                                        {canSignAttachment(attachment, invoice, user, activeSubstitutions) && (
                                          <button
                                            onClick={() => {
                                              setSigningInvoiceId(invoice.id);
                                              setSigningAttachmentId(attachment.id);
                                              setShowSignModal(true);
                                            }}
                                            className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors flex-shrink-0"
                                            title="Podpisz załącznik"
                                          >
                                            <PenTool className="h-3 w-3" />
                                          </button>
                                        )}
                                        {canReplaceAttachment && isSigned && (
                                          <button
                                            onClick={() => handleOpenReplaceModal(attachment, invoice.id, invoiceSignatures[invoice.id])}
                                            className="flex items-center gap-1 px-2 py-1 text-orange-700 hover:text-orange-800 hover:bg-orange-50 rounded-lg border border-orange-200 bg-orange-50/50 transition-colors flex-shrink-0 text-[10px] font-medium"
                                            title="Podmień załącznik (admin)"
                                          >
                                            <RefreshCw className="h-3 w-3" />
                                            Podmień
                                          </button>
                                        )}
                                        {!isSigned && !user?.isPreviewMode && (attachment.userId === user?.id || user?.role === 'SEKRETARIAT' || user?.role === 'SUPERADMIN') && (
                                          <button
                                            onClick={() => handleDeleteAttachmentClick(attachment.id, invoice.id)}
                                            className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                                            title="Usuń załącznik"
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-500 italic">Brak załączników</p>
                              )}
                            </div>
                            
                            {/* Historia faktury - wyświetl jako małe kwadraciki obok siebie ze strzałkami */}
                            {invoiceHistory[invoice.id] && invoiceHistory[invoice.id].length > 0 && (
                              <div className={isModern ? 'bg-white p-3 sm:p-4 rounded-xl border border-gray-100 shadow-sm mt-4' : 'bg-white p-3 sm:p-4 rounded-lg border border-gray-200 shadow-sm mt-4'}>
                                <button
                                  onClick={() => {
                                    const newExpanded = new Set(expandedHistory);
                                    if (newExpanded.has(invoice.id)) {
                                      newExpanded.delete(invoice.id);
                                    } else {
                                      newExpanded.add(invoice.id);
                                    }
                                    setExpandedHistory(newExpanded);
                                  }}
                                  className="w-full text-left"
                                >
                                  <h4 className={isModern ? 'text-sm font-semibold text-gray-800 mb-1 flex items-center justify-between gap-2' : 'text-sm font-normal text-gray-900 mb-3 flex items-center justify-between gap-2'}>
                                    <span className="flex items-center gap-2">
                                      {isModern ? (
                                        <span className="h-6 w-6 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
                                          <History className="h-3.5 w-3.5" />
                                        </span>
                                      ) : (
                                        <History className="h-4 w-4 text-gray-600" />
                                      )}
                                      Historia faktury
                                      <span className="text-xs text-gray-500 font-normal">
                                        {(() => {
                                          // Filtruj zmiany statusu - nie pokazuj ich w historii (są zbędne)
                                          // ALE pokazuj wpisy o zakończeniu faktury
                                          const filteredHistory = invoiceHistory[invoice.id].filter(item => 
                                            item.actionType !== 'STATUS_CHANGED' || 
                                            (item.actionDescription && item.actionDescription.toLowerCase().includes('zakończono'))
                                          );
                                          const count = filteredHistory.length;
                                          return `(${count} ${count === 1 ? 'wydarzenie' : 'wydarzeń'})`;
                                        })()}
                                      </span>
                                    </span>
                                    {expandedHistory.has(invoice.id) ? (
                                      <ChevronUp className="h-4 w-4 text-gray-600" />
                                    ) : (
                                      <ChevronDown className="h-4 w-4 text-gray-600" />
                                    )}
                                  </h4>
                                </button>
                                {expandedHistory.has(invoice.id) && (
                                <div className={isModern ? 'flex flex-wrap items-start gap-2 mt-3' : 'flex flex-wrap items-center gap-2 sm:gap-3'}>
                                  {(() => {
                                    // Odwróć kolejność - najstarsze pierwsze (od lewej do prawej)
                                    // Filtruj zmiany statusu - nie pokazuj ich w historii (są zbędne)
                                    // ALE pokazuj wpisy o zakończeniu faktury
                                    const filteredHistory = invoiceHistory[invoice.id].filter(item => 
                                      item.actionType !== 'STATUS_CHANGED' || 
                                      (item.actionDescription && item.actionDescription.toLowerCase().includes('zakończono'))
                                    );
                                    const reversedHistory = [...filteredHistory].reverse();
                                    return reversedHistory.map((historyItem, idx) => {
                                      const isLast = idx === reversedHistory.length - 1;
                                    const actionType = historyItem.actionType;
                                    
                                    // Określ ikonę i kolory na podstawie typu akcji
                                    let icon, bgColor, borderColor, textColor, iconColor;
                                    
                                    if (actionType === 'CREATED') {
                                      icon = FilePlus;
                                      bgColor = 'bg-blue-50';
                                      borderColor = 'border-blue-200';
                                      textColor = 'text-blue-900';
                                      iconColor = 'text-blue-600';
                                    } else if (actionType === 'ASSIGNED') {
                                      icon = Send;
                                      bgColor = 'bg-purple-50';
                                      borderColor = 'border-purple-200';
                                      textColor = 'text-purple-900';
                                      iconColor = 'text-purple-600';
                                    } else if (actionType === 'RETURNED') {
                                      icon = CornerUpLeft;
                                      bgColor = 'bg-orange-50';
                                      borderColor = 'border-orange-200';
                                      textColor = 'text-orange-900';
                                      iconColor = 'text-orange-600';
                                    } else if (actionType === 'MARKED_SUSPICIOUS') {
                                      icon = AlertTriangle;
                                      bgColor = 'bg-orange-50';
                                      borderColor = 'border-orange-300';
                                      textColor = 'text-orange-900';
                                      iconColor = 'text-orange-700';
                                    } else if (actionType === 'UNMARKED_SUSPICIOUS') {
                                      icon = AlertTriangle;
                                      bgColor = 'bg-gray-50';
                                      borderColor = 'border-gray-200';
                                      textColor = 'text-gray-900';
                                      iconColor = 'text-gray-500';
                                    } else if (actionType === 'ATTACHMENT_SIGN_WAIVED') {
                                      icon = UserPlus;
                                      bgColor = 'bg-purple-50';
                                      borderColor = 'border-purple-200';
                                      textColor = 'text-purple-900';
                                      iconColor = 'text-purple-600';
                                    } else if ((actionType === 'UPDATED' || (actionType && actionType.toUpperCase() === 'UPDATED')) && historyItem.actionDescription && (historyItem.actionDescription.includes('załącznik') || historyItem.actionDescription.toLowerCase().includes('zalacznik'))) {
                                      icon = PenTool;
                                      bgColor = 'bg-blue-50';
                                      borderColor = 'border-blue-200';
                                      textColor = 'text-blue-900';
                                      iconColor = 'text-blue-600';
                                    } else if (actionType === 'UPDATED' && historyItem.actionDescription && historyItem.actionDescription.includes('opisał i podpisał')) {
                                      icon = PenTool;
                                      bgColor = 'bg-green-50';
                                      borderColor = 'border-green-200';
                                      textColor = 'text-green-900';
                                      iconColor = 'text-green-600';
                                    } else if (actionType === 'STATUS_CHANGED') {
                                      // Sprawdź czy to zakończenie faktury
                                      if (historyItem.actionDescription && historyItem.actionDescription.toLowerCase().includes('zakończono')) {
                                        icon = CheckCircle;
                                        bgColor = 'bg-green-50';
                                        borderColor = 'border-green-200';
                                        textColor = 'text-green-900';
                                        iconColor = 'text-green-600';
                                      } else {
                                        icon = UserCheck;
                                        bgColor = 'bg-amber-50';
                                        borderColor = 'border-amber-200';
                                        textColor = 'text-amber-900';
                                        iconColor = 'text-amber-600';
                                      }
                                    } else {
                                      icon = History;
                                      bgColor = 'bg-gray-50';
                                      borderColor = 'border-gray-200';
                                      textColor = 'text-gray-900';
                                      iconColor = 'text-gray-600';
                                    }
                                    
                                    const IconComponent = icon;
                                    const modernAccent = borderColor.replace('border-', 'border-l-').replace('-200', '-400');
                                    
                                    return (
                                      <React.Fragment key={historyItem.id}>
                                        <div className={isModern
                                          ? `bg-white border border-gray-100 border-l-4 ${modernAccent} rounded-lg p-2.5 shadow-sm hover:shadow-md transition-shadow flex-shrink-0 w-fit max-w-[240px]`
                                          : `${bgColor} ${borderColor} border rounded-lg p-3 flex-shrink-0 min-w-[200px] sm:min-w-[220px] max-w-[280px]`}>
                                          <div className="flex flex-col">
                                            <div className={`flex items-center ${iconColor} mb-2`}>
                                              <IconComponent className="h-4 w-4 mr-2" />
                                              <div className={`text-xs font-normal ${textColor}`}>
                                                  {actionType === 'CREATED' ? 'Utworzenie' :
                                                 actionType === 'ASSIGNED' ? 'Przekazanie' :
                                                 actionType === 'RETURNED' ? 'Zwrot' :
                                                 actionType === 'MARKED_SUSPICIOUS' ? 'Oznaczenie podejrzanej' :
                                                 actionType === 'UNMARKED_SUSPICIOUS' ? 'Usunięcie oznaczenia' :
                                                 actionType === 'ATTACHMENT_SIGN_WAIVED' ? 'Wskazanie podpisującego' :
                                                 (actionType === 'UPDATED' && historyItem.actionDescription && historyItem.actionDescription.includes('podpisał załącznik')) ? 'Podpisanie załącznika' :
                                                 actionType === 'UPDATED' ? 'Opisanie i podpisanie' :
                                                 actionType === 'UNASSIGNED' ? 'Cofnięcie' : 'Akcja'}
                                              </div>
                                            </div>
                                            <div className={`text-xs ${textColor} mb-2 leading-relaxed`}>
                                              {historyItem.actionDescription}
                                            </div>
                                            {historyItem.performedByUserName && (
                                              <div className={`text-xs ${textColor} opacity-75 mb-1 flex items-center`}>
                                                <User className="h-3 w-3 mr-1" />
                                                {historyItem.performedByUserName}
                                              </div>
                                            )}
                                            {historyItem.assignedToUserName && (
                                              <div className={`text-xs ${textColor} opacity-75 mb-1 flex items-center`}>
                                                <ArrowRight className="h-3 w-3 mr-1" />
                                                {actionType === 'RETURNED' ? 'Zwrócono do:' : 'Przekazano do:'} {historyItem.assignedToUserName}
                                              </div>
                                            )}
                                            <div className={`text-xs ${textColor} opacity-60 mt-auto pt-1`}>
                                              {formatHistoryDate(historyItem.createdAt)}
                                            </div>
                                          </div>
                                        </div>
                                        {!isLast && (
                                          <div className="flex items-center justify-center flex-shrink-0">
                                            <ArrowRight className="h-4 w-4 text-gray-400" />
                                          </div>
                                        )}
                                      </React.Fragment>
                                    );
                                  });
                                  })()}
                                </div>
                                )}
                              </div>
                            )}
                            
                            {/* Przycisk do otwierania modalu opisów */}
                            <div className="flex flex-wrap gap-3 mt-4">
                              {(invoiceDescriptions[invoice.id] && invoiceDescriptions[invoice.id].length > 0) && (
                                <>
                                <button
                                  onClick={() => {
                                    setModalInvoiceId(invoice.id);
                                    setShowDescriptionsModal(true);
                                  }}
                                  className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg border border-blue-200 transition-colors duration-150"
                                >
                                  <FileCheck className="h-4 w-4" />
                                  <span className="text-sm font-medium">Opisy i podpisy ({invoiceDescriptions[invoice.id].length})</span>
                                </button>
                                  <button
                                    onClick={async () => {
                                      // Pobierz świeże dane przed otwarciem – po migracji attachment_id mogły się zmienić
                                      try {
                                        const [sigRes, descRes] = await Promise.all([
                                          api.get(`/documents/invoices/${invoice.id}/signatures`),
                                          api.get(`/invoices/${invoice.id}/descriptions`)
                                        ]);
                                        setInvoiceSignatures(prev => ({ ...prev, [invoice.id]: sigRes.data || [] }));
                                        setInvoiceDescriptions(prev => ({ ...prev, [invoice.id]: Array.isArray(descRes.data) ? descRes.data : [] }));
                                      } catch (e) {
                                        if (e.response?.status !== 404 && e.response?.status !== 403) console.error('A4 refetch error:', e);
                                      }
                                      setA4PreviewInvoiceId(invoice.id);
                                      setShowA4PreviewModal(true);
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg border border-green-200 transition-colors duration-150"
                                    title="Podgląd A4 - pełna ścieżka podpisów i opisów"
                                  >
                                    <Printer className="h-4 w-4" />
                                    <span className="text-sm font-medium">Podgląd A4</span>
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            
            {/* Paginacja */}
            {totalPages > 1 && (
              <div className="px-4 py-4 bg-white border-t border-gray-200 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">
                    Strona {currentPage + 1} z {totalPages} ({totalElements} {totalElements === 1 ? 'faktura' : totalElements < 5 ? 'faktury' : 'faktur'})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      if (currentPage > 0) {
                        setCurrentPage(currentPage - 1);
                      }
                    }}
                    disabled={currentPage === 0 || loading}
                    className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>Poprzednia</span>
                  </button>
                  <button
                    onClick={() => {
                      if (currentPage < totalPages - 1) {
                        setCurrentPage(currentPage + 1);
                      }
                    }}
                    disabled={currentPage >= totalPages - 1 || loading}
                    className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span>Następna</span>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
          );
        })()}
      </div>

      {/* Modal przekazywania faktury (pojedynczej lub zbiorczej) */}
      {(() => {
        const shouldShow = showAssignModal && (selectedInvoice || assignBatchIds.length > 0);
        console.log('🔍 [DEBUG InvoicesByStatus] Renderowanie modala przypisania:', {
          showAssignModal,
          selectedInvoice: selectedInvoice ? { id: selectedInvoice.id, number: selectedInvoice.number } : null,
          shouldShow
        });
        return shouldShow;
      })() && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] backdrop-blur-sm p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  {assignBatchIds.length > 0 ? 'Przekaż faktury' : 'Przekaż fakturę'}
                </h3>
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedInvoice(null);
                    setAssignBatchIds([]);
                    setSelectedUserId('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-4 sm:mb-6">
                {assignBatchIds.length > 0 ? (
                  <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 p-2 sm:p-3 bg-teal-50 rounded-lg border border-teal-100">
                    Przekaż <strong className="font-semibold text-gray-900">{assignBatchIds.length}</strong> {assignBatchIds.length === 1 ? 'fakturę' : assignBatchIds.length < 5 ? 'faktury' : 'faktur'} do wybranego użytkownika.
                  </p>
                ) : (
                  <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 p-2 sm:p-3 bg-gray-50 rounded-lg">
                    Faktura: <strong className="font-semibold text-gray-900">{selectedInvoice?.number}</strong>
                  </p>
                )}
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Wybierz użytkownika:
                </label>
                {substitutionInfo && (
                  <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs sm:text-sm text-yellow-800">
                      <strong>Uwaga:</strong> Użytkownik <strong>{substitutionInfo.managerName}</strong> jest na urlopie.
                    </p>
                    <p className="text-xs sm:text-sm text-yellow-800 mt-1">
                      Faktura zostanie przekazana do <strong>{substitutionInfo.substituteName}</strong> w zastępstwie.
                    </p>
                  </div>
                )}
                {/* Pole wyszukiwania */}
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Wyszukaj użytkownika..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                {/* Lista użytkowników z możliwością wyszukiwania */}
                <div className="border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
                  {(() => {
                    const filteredUsers = users.filter(user => {
                      if (user.active === false) return false;
                      if (!userSearchQuery) return true;
                      const query = userSearchQuery.toLowerCase();
                      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
                      const email = (user.email || '').toLowerCase();
                      return fullName.includes(query) || email.includes(query);
                    });
                    
                    if (filteredUsers.length === 0) {
                      return (
                        <div className="px-3 sm:px-4 py-4 text-sm text-gray-500 text-center">
                          {users.length === 0 
                            ? 'Brak użytkowników do wyboru' 
                            : 'Brak użytkowników pasujących do wyszukiwania'}
                        </div>
                      );
                    }
                    
                    return filteredUsers.map((user) => {
                      const substitution = activeSubstitutions.find(s => s.managerId === user.id);
                      const isSelected = selectedUserId === user.id.toString();
                      return (
                        <div
                          key={user.id}
                          onClick={() => handleUserSelectChange(user.id.toString())}
                          className={`px-3 sm:px-4 py-2 sm:py-3 cursor-pointer hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                            isSelected ? 'bg-blue-100' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.firstName && user.lastName 
                                    ? `${user.firstName} ${user.lastName}`
                                    : user.email}
                                </div>
                                {substitution && (
                                  <span className="px-2 py-0.5 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full border border-yellow-200">
                                    🏖️ Urlop
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {user.email}
                              </div>
                              {substitution && (
                                <div className="text-xs text-yellow-700 mt-1 italic">
                                  Zastępuje: {substitution.substituteName}
                                </div>
                              )}
                            </div>
                            {isSelected && (
                              <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    if (isAssigning) return;
                    setShowAssignModal(false);
                    setSelectedInvoice(null);
                    setAssignBatchIds([]);
                    setSelectedUserId('');
                    setSubstitutionInfo(null);
                    setUserSearchQuery('');
                  }}
                  disabled={isAssigning}
                  className="px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-150 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleAssignSubmit}
                  disabled={!selectedUserId || isAssigning}
                  className="px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors duration-150 flex items-center justify-center space-x-2 shadow-sm w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAssigning ? (
                    <>
                      <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-2 border-white border-t-transparent"></div>
                      <span>Przekazywanie...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      <span>Przekaż</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia cofnięcia do listy faktur */}
      {showRevertModal && revertInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full border border-gray-200">
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Cofnij fakturę do listy</h3>
                  <p className="text-sm text-gray-600">
                    Czy na pewno chcesz cofnąć tę fakturę do listy faktur? Faktura zostanie usunięta z realizacji.
                  </p>
                  {revertInvoice.number && (
                    <p className="mt-2 text-sm font-medium text-gray-700">
                      Faktura: <span className="text-teal-600">{revertInvoice.number}</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowRevertModal(false);
                    setRevertInvoice(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleRevertConfirm}
                  className="px-4 py-2 text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors font-medium flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Tak, cofnij
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia usunięcia załącznika/opisu/podpisu/faktury */}
      <ConfirmModal
        isOpen={confirmModal.show}
        onClose={() => setConfirmModal(prev => ({ ...prev, show: false, pendingAction: null }))}
        onConfirm={handleConfirmModalConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        variant="danger"
      />

      <AttachmentReplaceModal
        isOpen={showReplaceModal}
        onClose={() => {
          setShowReplaceModal(false);
          setReplaceAttachment(null);
          setReplaceInvoiceId(null);
          setReplaceDefaultSignerId(null);
        }}
        attachment={replaceAttachment}
        invoiceId={replaceInvoiceId}
        users={users}
        defaultSignerUserId={replaceDefaultSignerId}
        onSuccess={handleAttachmentReplaceSuccess}
      />

      {/* Modal zwrotu faktury */}
      {showReturnModal && returnInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Zwróć fakturę</h3>
                <button
                  onClick={() => {
                    setShowReturnModal(false);
                    setReturnInvoice(null);
                    setReturnReason('');
                    setReturnToUserId('');
                    setReturnUserSearchQuery('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-4 sm:mb-6 space-y-4">
                <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 p-2 sm:p-3 bg-gray-50 rounded-lg">
                  Faktura: <strong className="font-semibold text-gray-900">{returnInvoice?.number}</strong>
                </p>
                
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    Zwróć do użytkownika <span className="text-red-500">*</span>
                  </label>
                  <div className="relative mb-2">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={returnUserSearchQuery}
                      onChange={(e) => setReturnUserSearchQuery(e.target.value)}
                      placeholder="Wyszukaj użytkownika..."
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                    {users
                      .filter(u => {
                        if (!returnUserSearchQuery) return true;
                        const query = returnUserSearchQuery.toLowerCase();
                        const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
                        const email = (u.email || '').toLowerCase();
                        return fullName.includes(query) || email.includes(query);
                      })
                      .filter(u => {
                        if (u.active === false) return false;
                        if (u.id === user?.id) return false; // Wyklucz zalogowanego użytkownika
                        return true;
                      })
                      .map((u) => (
                        <div
                          key={u.id}
                          onClick={() => setReturnToUserId(u.id.toString())}
                          className={`px-3 py-2 cursor-pointer hover:bg-orange-50 transition-colors ${
                            returnToUserId === u.id.toString() ? 'bg-orange-100 border-l-4 border-orange-500' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {u.firstName && u.lastName
                                  ? `${u.firstName} ${u.lastName}`
                                  : u.email}
                              </p>
                              {u.email && (
                                <p className="text-xs text-gray-500">{u.email}</p>
                              )}
                            </div>
                            {returnToUserId === u.id.toString() && (
                              <CheckCircle className="h-5 w-5 text-orange-600" />
                            )}
                          </div>
                        </div>
                      ))}
                    {users.filter(u => {
                      if (!returnUserSearchQuery) return true;
                      const query = returnUserSearchQuery.toLowerCase();
                      const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
                      const email = (u.email || '').toLowerCase();
                      return fullName.includes(query) || email.includes(query);
                    }).filter(u => {
                      if (u.active === false) return false;
                      if (u.id === user?.id) return false;
                      return true;
                    }).length === 0 && (
                      <div className="px-3 py-4 text-center text-sm text-gray-500">
                        Brak użytkowników do wyboru
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    Powód zwrotu <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    rows={4}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                    placeholder="Opisz, co wymaga poprawy..."
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowReturnModal(false);
                    setReturnInvoice(null);
                    setReturnReason('');
                    setReturnToUserId('');
                    setReturnUserSearchQuery('');
                  }}
                  className="px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-150 w-full sm:w-auto"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleReturnSubmit}
                  className="px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors duration-150 flex items-center justify-center space-x-2 shadow-sm w-full sm:w-auto"
                >
                  <CornerUpLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span>Zwróć</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal opisu faktury */}
      {showDescriptionModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg shadow-xl w-[98vw] max-w-[1800px] h-[98vh] max-h-[98vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Opisz fakturę</h3>
              <button
                onClick={handleCancelEdit}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 flex overflow-hidden">
              {/* Lewa strona - Formularz opisu faktury */}
              <div className="w-[55%] border-r border-gray-200 overflow-y-auto p-6 space-y-5">
              {/* Podpis elektroniczny dokumentu – na górze */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Podpis elektroniczny dokumentu
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsSignaturesCollapsed(!isSignaturesCollapsed)}
                    className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                    title={isSignaturesCollapsed ? 'Rozwiń' : 'Zwiń'}
                  >
                    {isSignaturesCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                  </button>
                </div>
                {!isSignaturesCollapsed && (
                <div className="w-full space-y-3">
                  {(() => {
                    const signatures = filterActiveSignatures(invoiceSignatures[selectedInvoice?.id]);
                    if (signatures.length > 0) {
                      return (
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          {(() => {
                            const uniqueSignatures = new Map();
                            signatures.forEach(sig => {
                              if (sig.user && sig.signedAt) {
                                let key = sig.signatureGroupId || `${sig.user.id}_${new Date(sig.signedAt).toISOString().substring(0, 19)}`;
                                if (!uniqueSignatures.has(key)) uniqueSignatures.set(key, sig);
                              }
                            });
                            const sortedSignatures = Array.from(uniqueSignatures.values()).sort((a, b) => new Date(a.signedAt) - new Date(b.signedAt));
                            return sortedSignatures.map((sig, idx) => {
                              const description = invoiceDescriptions[selectedInvoice?.id]?.find(
                                desc => (desc.documentSignatureId && sig.id && desc.documentSignatureId === sig.id) ||
                                  (!desc.documentSignatureId && desc.userId && sig.user?.id && String(desc.userId) === String(sig.user.id))
                              );
                              const isAttachmentSignature = sig.attachmentId != null;
                              const isCrossedOut = sig.crossedOut === true;
                              const bgColor = isCrossedOut ? 'bg-orange-50' : (isAttachmentSignature ? 'bg-blue-50' : 'bg-green-50');
                              const borderColor = isCrossedOut ? 'border-orange-300' : (isAttachmentSignature ? 'border-blue-200' : 'border-green-200');
                              const iconColor = isCrossedOut ? 'text-orange-600' : (isAttachmentSignature ? 'text-blue-600' : 'text-green-600');
                              const arrowColor = isCrossedOut ? 'text-orange-600' : (isAttachmentSignature ? 'text-blue-600' : 'text-green-600');
                              const borderTopColor = isCrossedOut ? 'border-orange-300' : (isAttachmentSignature ? 'border-blue-300' : 'border-green-300');
                              return (
                                <React.Fragment key={sig.signatureGroupId || sig.id || `${sig.user?.id}_${sig.signedAt}` || idx}>
                                  <div
                                    className={`${bgColor} border ${borderColor} rounded-lg p-4 flex-shrink-0 min-w-[280px] max-w-[320px] shadow-sm relative ${isCrossedOut ? 'opacity-80' : ''}`}
                                    title={isCrossedOut ? (sig.crossedOutReason || 'Podpis skreślony przy zwrocie faktury') : undefined}
                                  >
                                    {isCrossedOut && (
                                      <div className="absolute -top-2 left-2 px-1.5 py-0.5 text-[10px] font-bold text-orange-800 bg-orange-200 rounded">
                                        SKREŚLONY
                                      </div>
                                    )}
                                    <div className={`flex items-start ${isCrossedOut ? 'line-through decoration-orange-500' : ''}`}>
                                      <Lock className={`h-5 w-5 ${iconColor} mr-3 mt-0.5 flex-shrink-0`} />
                                      <div className="flex-1 min-w-0">
                                        {isAttachmentSignature ? (
                                          <div className="text-sm font-semibold text-blue-700 mb-2">Podpis załącznika: {sig.attachmentFileName || 'załącznik'}</div>
                                        ) : description?.metricalChecked ? (
                                          <div className="text-sm font-semibold text-gray-900 mb-2">SPRAWDZONO POD WZGLĘDEM MERYTORYCZNYM przez {sig.user ? `${sig.user.firstName} ${sig.user.lastName}` : 'nieznany użytkownik'}, {new Date(sig.signedAt).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}, {new Date(sig.signedAt).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })} - podpisano elektronicznie</div>
                                        ) : description?.preliminaryCheckDone ? (
                                          <div className="text-sm font-semibold text-gray-900 mb-2">DOKONANO WSTĘPNEJ KONTROLI przez {sig.user ? `${sig.user.firstName} ${sig.user.lastName}` : 'nieznany użytkownik'}, {new Date(sig.signedAt).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}, {new Date(sig.signedAt).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })} - podpisano elektronicznie</div>
                                        ) : description?.formalnoRachunkowoChecked ? (
                                          <div className="text-sm font-semibold text-gray-900 mb-2">SPRAWDZONO POD WZGLĘDEM FORMALNO-RACHUNKOWYM przez {sig.user ? `${sig.user.firstName} ${sig.user.lastName}` : 'nieznany użytkownik'}, {new Date(sig.signedAt).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}, {new Date(sig.signedAt).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })} - podpisano elektronicznie</div>
                                        ) : description?.zatwierdzonoDo === 'REALIZACJI' ? (
                                          <div className="text-sm font-semibold text-red-700 mb-2">ZATWIERDZONO DO REALIZACJI przez {sig.user ? `${sig.user.firstName} ${sig.user.lastName}` : 'nieznany użytkownik'}, {new Date(sig.signedAt).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}, {new Date(sig.signedAt).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })} - podpisano elektronicznie</div>
                                        ) : (
                                          <div className="text-sm font-semibold text-gray-900 mb-2">Podpis wewnętrzny</div>
                                        )}
                                        <div className="text-xs text-gray-600 space-y-1">
                                          {sig.user && <div className="flex items-start"><User className="h-3.5 w-3.5 mr-1.5 mt-0.5 flex-shrink-0" /><span className="break-words">{sig.user.firstName} {sig.user.lastName}</span></div>}
                                          {sig.user && <div className="flex items-start text-gray-500"><span className="ml-5 break-all text-[11px]">{sig.user.email}</span></div>}
                                          <div className="flex items-center"><Calendar className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" /><span>{new Date(sig.signedAt).toLocaleString('pl-PL')}</span></div>
                                        </div>
                                        {description?.zatwierdzonoDo === 'REALIZACJI' && (
                                          <div className="mt-3 pt-3 border-t border-emerald-400"><div className="text-xs font-semibold text-purple-700">✓ ZATWIERDZONO DO REALIZACJI</div></div>
                                        )}
                                        {description?.formalnoRachunkowoChecked && (
                                          <div className={`mt-3 pt-3 border-t ${borderTopColor}`}>
                                            {description.zatwierdzonoDo && description.zatwierdzonoDo !== 'REALIZACJI' && <div className="text-xs font-normal text-gray-700 mb-1.5">ZATWIERDZONO DO: <strong>{description.zatwierdzonoDo}</strong></div>}
                                            {description.kwota && <div className="text-xs font-normal text-gray-700 mb-1.5">KWOTĘ: <strong>{description.kwota} PLN</strong>{description.kwotaSlownie && <span className="ml-2 text-gray-600">({description.kwotaSlownie})</span>}</div>}
                                          </div>
                                        )}
                                        {description?.description && (
                                          <div className={`mt-3 pt-3 border-t ${borderTopColor}`}>
                                            <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />Opis faktury:</div>
                                            <div className="text-xs text-gray-700 bg-white rounded p-2.5 border border-gray-200 whitespace-pre-wrap leading-relaxed max-h-[150px] overflow-y-auto">{description.description}</div>
                                            {description.signedByPosition && <div className="text-xs text-gray-600 mt-2 flex items-center gap-1.5"><User className="h-3.5 w-3.5" />{description.signedByPosition}</div>}
                                          </div>
                                        )}
                                        {description?.invoicePaymentType && (description.invoicePaymentType === 'CASH' || description.invoicePaymentType === 'TRANSFER') && (
                                          <div className={`mt-2 pt-2 border-t ${borderTopColor} text-xs text-gray-700`}>
                                            Typ płatności: <strong>{description.invoicePaymentType === 'CASH' ? 'Faktura gotówkowa' : 'Przelew'}</strong>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  {idx < sortedSignatures.length - 1 && (
                                    <div className="flex items-center justify-center flex-shrink-0 px-2"><ArrowRight className={`h-6 w-6 ${arrowColor}`} /></div>
                                  )}
                                </React.Fragment>
                              );
                            });
                          })()}
                        </div>
                      );
                    }
                    return (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="text-sm text-yellow-800">Brak podpisów do wyświetlenia. Podpisy mogą być pobierane...</div>
                      </div>
                    );
                  })()}
                </div>
                )}
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Opis faktury <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setIsDescriptionCollapsed(!isDescriptionCollapsed)}
                      className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                      title={isDescriptionCollapsed ? "Rozwiń opis" : "Zwiń opis"}
                    >
                      {isDescriptionCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowDictionaryManager(true)}
                    className="flex items-center space-x-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Otwórz słowniki"
                  >
                    <BookOpen className="h-4 w-4" />
                    <span>Słowniki</span>
                  </button>
                </div>
                {!isDescriptionCollapsed && (
                  <>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="np. Zgodnie z umową nr 123/2024..."
                    />
                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                      <span>Możesz zapisać opis roboczo i wrócić do niego później – nie podpisuje on faktury.</span>
                      <button
                        type="button"
                        onClick={handleSaveDraft}
                        disabled={saving}
                        className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-yellow-800 bg-yellow-50 border border-yellow-300 rounded-md hover:bg-yellow-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Zapisz to co wpisałeś, by wrócić w późniejszym terminie"
                      >
                        <Save className="h-3.5 w-3.5" />
                        {saving ? 'Zapisywanie...' : 'Zapisz roboczo i wróć później'}
                      </button>
                    </div>
                  </>
                )}
              </div>
              {/* Checkbox SPRAWDZONO POD WZGLĘDEM MERYTORYCZNYM - tylko dla kierowników z funkcją */}
              {user?.canCheckMerit && (
                <div className="space-y-3">
                  <div className="flex items-start bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 shadow-md">
                    <input
                      type="checkbox"
                      id="metricalChecked"
                      checked={editForm.metricalChecked || false}
                      onChange={(e) => {
                        console.log('Checkbox clicked:', e.target.checked);
                        setEditForm({ ...editForm, metricalChecked: e.target.checked });
                      }}
                      className="mt-0.5 h-6 w-6 text-blue-600 focus:ring-blue-500 border-gray-400 rounded cursor-pointer"
                      style={{ minWidth: '24px', minHeight: '24px', flexShrink: 0 }}
                    />
                    <label htmlFor="metricalChecked" className="ml-4 text-base font-bold text-gray-900 cursor-pointer select-none">
                      SPRAWDZONO POD WZGLĘDEM MERYTORYCZNYM
                    </label>
                  </div>
                  {/* Typ płatności - obowiązkowy dla użytkowników z weryfikacją merytoryczną przed podpisem */}
                  <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 shadow-md">
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Typ płatności faktury <span className="text-red-600">*</span>
                    </label>
                    <p className="text-xs text-amber-800 mb-3">Wymagane przed podpisaniem dokumentu</p>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="invoicePaymentType"
                          value="CASH"
                          checked={editForm.invoicePaymentType === 'CASH'}
                          onChange={(e) => setEditForm({ ...editForm, invoicePaymentType: e.target.value })}
                          className="h-4 w-4 text-amber-600 focus:ring-amber-500"
                        />
                        <span className="text-sm font-medium">Faktura gotówkowa</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="invoicePaymentType"
                          value="TRANSFER"
                          checked={editForm.invoicePaymentType === 'TRANSFER'}
                          onChange={(e) => setEditForm({ ...editForm, invoicePaymentType: e.target.value })}
                          className="h-4 w-4 text-amber-600 focus:ring-amber-500"
                        />
                        <span className="text-sm font-medium">Przelew</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
              {/* Checkbox DOKONANO WSTĘPNEJ KONTROLI - tylko dla użytkowników z uprawnieniem */}
              {user?.canDoPreliminaryCheck && (
                <div className="flex items-start bg-blue-50 border-2 border-blue-400 rounded-lg p-4 shadow-md">
                  <input
                    type="checkbox"
                    id="preliminaryCheckDone"
                    checked={editForm.preliminaryCheckDone || false}
                    onChange={(e) => {
                      setEditForm({ ...editForm, preliminaryCheckDone: e.target.checked });
                    }}
                    className="mt-0.5 h-6 w-6 text-blue-600 focus:ring-blue-500 border-gray-400 rounded cursor-pointer"
                    style={{ minWidth: '24px', minHeight: '24px', flexShrink: 0 }}
                  />
                  <label htmlFor="preliminaryCheckDone" className="ml-4 text-base font-bold text-gray-900 cursor-pointer select-none">
                    DOKONANO WSTĘPNEJ KONTROLI
                  </label>
                </div>
              )}
              {/* Checkbox ZATWIERDZONO DO REALIZACJI - tylko dla użytkowników z uprawnieniem "Zgoda na akceptację faktur" */}
              {user?.canAcceptInvoices && (
                <div className="flex items-start bg-emerald-50 border-2 border-emerald-500 rounded-lg p-4 shadow-md">
                  <input
                    type="checkbox"
                    id="zatwierdzonoDoRealizacji"
                    checked={editForm.zatwierdzonoDo === 'REALIZACJI'}
                    onChange={(e) => setEditForm({ ...editForm, zatwierdzonoDo: e.target.checked ? 'REALIZACJI' : '' })}
                    className="mt-0.5 h-6 w-6 text-emerald-600 focus:ring-emerald-500 border-gray-400 rounded cursor-pointer"
                    style={{ minWidth: '24px', minHeight: '24px', flexShrink: 0 }}
                  />
                  <label htmlFor="zatwierdzonoDoRealizacji" className="ml-4 text-base font-bold text-gray-900 cursor-pointer select-none">
                    ✓ ZATWIERDZONO DO REALIZACJI
                  </label>
                </div>
              )}
              {/* Formularz księgowości - tylko dla użytkowników z uprawnieniem */}
              {user?.canCheckFormalAccounting && (
                <div className="bg-green-50 border-2 border-green-400 rounded-lg p-4 shadow-md space-y-4">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="formalnoRachunkowoChecked"
                      checked={editForm.formalnoRachunkowoChecked || false}
                      onChange={(e) => setEditForm({ ...editForm, formalnoRachunkowoChecked: e.target.checked })}
                      className="mt-0.5 h-6 w-6 text-green-600 focus:ring-green-500 border-gray-400 rounded cursor-pointer"
                      style={{ minWidth: '24px', minHeight: '24px', flexShrink: 0 }}
                    />
                    <label htmlFor="formalnoRachunkowoChecked" className="ml-4 text-base font-bold text-gray-900 cursor-pointer select-none">
                      SPRAWDZONO POD WZGLĘDEM FORMALNO-RACHUNKOWYM
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ZATWIERDZONO DO: <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={editForm.zatwierdzonoDo}
                      onChange={(e) => setEditForm({ ...editForm, zatwierdzonoDo: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                    >
                      <option value="">Wybierz...</option>
                      <option value="WYPŁATY">WYPŁATY</option>
                      <option value="ZWROTU">ZWROTU</option>
                      <option value="REALIZACJI">REALIZACJI</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      KWOTĘ: <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editForm.kwota}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9,.]/g, '').replace(',', '.');
                        setEditForm({ ...editForm, kwota: value });
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                      placeholder="np. 1234.56"
                    />
                    {editForm.kwota && !isNaN(parseFloat(editForm.kwota.replace(',', '.'))) && (
                      <div className="mt-2 p-2 bg-white border border-gray-200 rounded text-sm text-gray-700">
                        <strong>Słownie:</strong> {numberToWordsPLN(parseFloat(editForm.kwota.replace(',', '.')))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Stanowisko osoby opisującej <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editForm.signedByPosition}
                  onChange={(e) => setEditForm({ ...editForm, signedByPosition: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="np. Kierownik działu"
                />
              </div>
              {/* Pole Ośrodek kosztów - tylko dla użytkowników z uprawnieniem */}
              {user?.canUseCostCenter && (
                <div className="flex items-start bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 shadow-md">
                  <div className="flex-1">
                    <label htmlFor="costCenter" className="block text-base font-bold text-gray-900 mb-2 cursor-pointer select-none">
                      OŚRODEK KOSZTÓW
                    </label>
                    <input
                      type="text"
                      id="costCenter"
                      value={editForm.costCenter || ''}
                      onChange={(e) => setEditForm({ ...editForm, costCenter: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-yellow-500 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-600 text-sm font-semibold bg-white"
                      placeholder="np. 1234"
                    />
                  </div>
                </div>
              )}

              {/* DOTACJA (dotyczy księgowości) - widoczne dla wszystkich */}
              <div className="flex items-start bg-purple-50 border-2 border-purple-400 rounded-lg p-4 shadow-md">
                <input
                  type="checkbox"
                  id="isDotacja"
                  checked={editForm.isDotacja || false}
                  onChange={(e) => setEditForm({ ...editForm, isDotacja: e.target.checked })}
                  className="mt-0.5 h-6 w-6 text-purple-600 focus:ring-purple-500 border-gray-400 rounded cursor-pointer"
                  style={{ minWidth: '24px', minHeight: '24px', flexShrink: 0 }}
                />
                <label htmlFor="isDotacja" className="ml-4 text-base font-bold text-gray-900 cursor-pointer select-none">
                  DOTACJA (dotyczy księgowości)
                </label>
              </div>
              {editForm.isDotacja && (
                <div className="bg-purple-50/50 border-2 border-purple-300 rounded-lg p-4 shadow-md space-y-3">
                  <div className="text-sm font-bold text-gray-900 underline">Źródła finansowania zadania:</div>
                  <div className="space-y-2 pl-4">
                    <div className="flex items-center justify-between gap-4">
                      <label className="text-sm text-gray-700 flex-shrink-0">- środki z Powiatu Białostockiego</label>
                      <input
                        type="text"
                        value={editForm.srodkiPowiat || ''}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9,.]/g, '').replace(/(\..*)\./g, '$1');
                          setEditForm({ ...editForm, srodkiPowiat: v });
                        }}
                        className="w-32 px-2 py-1.5 border border-gray-300 rounded text-sm text-right"
                        placeholder="0,00"
                      />
                      <span className="text-sm text-gray-600 w-12">zł</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <label className="text-sm text-gray-700 flex-shrink-0">- środki SP ZOZ w Łapach</label>
                      <input
                        type="text"
                        value={editForm.srodkiSpZoz || ''}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9,.]/g, '').replace(/(\..*)\./g, '$1');
                          setEditForm({ ...editForm, srodkiSpZoz: v });
                        }}
                        className="w-32 px-2 py-1.5 border border-gray-300 rounded text-sm text-right"
                        placeholder="0,00"
                      />
                      <span className="text-sm text-gray-600 w-12">zł</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4 pt-2 border-t-2 border-gray-300">
                    <span className="text-sm font-bold text-gray-900">Wartość wydatku</span>
                    <span className="text-sm font-bold text-gray-900">
                      {(() => {
                        const a = parseFloat((editForm.srodkiPowiat || '0').replace(',', '.')) || 0;
                        const b = parseFloat((editForm.srodkiSpZoz || '0').replace(',', '.')) || 0;
                        const sum = a + b;
                        return sum.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' zł';
                      })()}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Blok podpisywania – nad historią i załącznikami, pod stanowiskiem */}
              {(() => {
                const userSignatures = invoiceSignatures[selectedInvoice?.id]?.filter(
                  sig => sig.user && sig.user.id === user?.id
                ) || [];
                const hasUserSigned = userSignatures.length > 0;
                const currentInvoice = allInvoices.find(inv => inv.id === selectedInvoice?.id);
                const invoiceStatus = currentInvoice?.status || selectedInvoice?.status;
                const invoiceAssignedUserId = currentInvoice?.assignedUserId || selectedInvoice?.assignedUserId;
                const invoiceWasReturned = wasInvoiceReturned(selectedInvoice?.id);
                const isInProgress = invoiceStatus === 'IN_PROGRESS' || selectedInvoice?.status === 'IN_PROGRESS';
                const isAssignedToUser = invoiceAssignedUserId === user?.id || selectedInvoice?.assignedUserId === user?.id;
                const shouldShowSignButton = isInProgress && isAssignedToUser;
                const shouldShowSignButtonForReturned = invoiceWasReturned && isInProgress && isAssignedToUser;
                const finalShouldShowButton = shouldShowSignButton || shouldShowSignButtonForReturned;
                if (finalShouldShowButton) {
                  return (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          {hasUserSigned ? (
                            <div className="mb-2">
                              <div className="flex items-center text-sm text-green-800 mb-1">
                                <CheckCircle className="h-4 w-4 mr-2" />
                                <span>Masz już podpis na tej fakturze</span>
                              </div>
                              <span className="text-sm text-blue-800">
                                {invoiceWasReturned 
                                  ? 'Faktura ze zwrotu - możesz dodać nowy opis i podpis ponownie'
                                  : 'Możesz dodać nowy opis i podpis ponownie'}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <Lock className="h-5 w-5 text-blue-600 mr-2" />
                              <span className="text-sm text-blue-800">Dodaj swój podpis</span>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={async () => {
                            if (editForm.formalnoRachunkowoChecked) {
                              const kwotaStr = (editForm.kwota || '').trim().replace(',', '.');
                              if (!kwotaStr || isNaN(parseFloat(kwotaStr))) {
                                toast.error('Przy zaznaczeniu "Sprawdzono pod względem formalno-rachunkowym" pole KWOTĘ jest obowiązkowe. Wprowadź kwotę przed podpisem.');
                                return;
                              }
                            }
                            if (user?.canCheckMerit && !editForm.invoicePaymentType) {
                              toast.error('Oznacz typ płatności faktury (gotówkowa lub przelew) przed podpisaniem dokumentu.');
                              return;
                            }
                            try {
                              const unsignedNames = await fetchUnsignedAttachmentNames(api, selectedInvoice.id, user);
                              if (unsignedNames.length > 0) {
                                toast.error(unsignedAttachmentsToast(unsignedNames));
                                return;
                              }
                            } catch (err) {
                              console.error('Błąd weryfikacji podpisów załączników:', err);
                              toast.error('Nie udało się zweryfikować podpisów załączników.');
                              return;
                            }
                            setSigningInvoiceId(selectedInvoice.id);
                            setSigningAttachmentId(null);
                            setShowSignModal(true);
                          }}
                          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors flex items-center ml-4"
                        >
                          <Lock className="h-4 w-4 mr-2" />
                          {hasUserSigned ? 'Podpisz ponownie' : 'Podpisz dokument'}
                        </button>
                      </div>
                    </div>
                  );
                } else if (hasUserSigned && !finalShouldShowButton) {
                  return (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                        <span className="text-sm text-green-800">Dokument został podpisany przez Ciebie</span>
                      </div>
                    </div>
                  );
                } else if (!finalShouldShowButton) {
                  return (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <Lock className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600">
                          {!isInProgress
                            ? `Faktura nie jest w realizacji (status: ${invoiceStatus || selectedInvoice?.status || 'brak'})` 
                            : `Faktura nie jest przypisana do Ciebie (przypisana do: ${invoiceAssignedUserId || selectedInvoice?.assignedUserId}, Ty: ${user?.id})`}
                        </span>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Sekcja załączników */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Załączniki (PDF, PNG, JPG)
                  {(() => {
                    const attachmentsToShow = (attachments && attachments.length > 0) 
                      ? attachments 
                      : (invoiceAttachments[selectedInvoice?.id] || []);
                    return attachmentsToShow.length > 0 ? ` (${attachmentsToShow.length})` : '';
                  })()}
                </label>
                <div className="space-y-3">
                  <div
                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (!uploadingAttachment) setIsDraggingAttachment(true); }}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!uploadingAttachment) setIsDraggingAttachment(true); }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (e.currentTarget.contains(e.relatedTarget)) return;
                      setIsDraggingAttachment(false);
                    }}
                    onDrop={handleAttachmentDrop}
                    className={`flex flex-col items-center justify-center gap-2 px-4 py-5 rounded-lg border-2 border-dashed transition-colors ${
                      uploadingAttachment
                        ? 'bg-gray-100 border-gray-300'
                        : isDraggingAttachment
                          ? 'bg-blue-100 border-blue-500'
                          : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                    }`}
                  >
                    <input
                      type="file"
                      id="attachment-upload-modal"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={handleAttachmentUpload}
                      disabled={uploadingAttachment}
                      multiple
                      className="hidden"
                    />
                    <Upload className={`h-6 w-6 ${isDraggingAttachment ? 'text-blue-700' : 'text-blue-600'}`} />
                    <div className="text-sm text-blue-800 text-center">
                      {uploadingAttachment ? (
                        <span className="font-medium">Dodawanie...</span>
                      ) : isDraggingAttachment ? (
                        <span className="font-medium">Upuść pliki tutaj, aby dodać do faktury</span>
                      ) : (
                        <>
                          <span className="font-medium">Przeciągnij i upuść pliki</span>
                          <span className="text-blue-700"> lub </span>
                          <label
                            htmlFor="attachment-upload-modal"
                            className="font-medium underline cursor-pointer hover:text-blue-900"
                          >
                            wybierz z dysku
                          </label>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-blue-700/80">PDF, PNG, JPG · maks. 10MB / plik</div>
                  </div>
                  {/* Lista załączników - pokaż wszystkie załączniki faktury */}
                  {(() => {
                    // Użyj attachments jeśli są, w przeciwnym razie użyj invoiceAttachments
                    const attachmentsToShow = (attachments && attachments.length > 0) 
                      ? attachments 
                      : (invoiceAttachments[selectedInvoice?.id] || []);
                    
                    if (attachmentsToShow.length === 0) {
                      return (
                        <div className="text-sm text-gray-500 italic py-2">
                          Brak załączników dla tej faktury
                        </div>
                      );
                    }
                    
                    const signaturesForModal = invoiceSignatures[selectedInvoice?.id] || [];
                    const currentInv = allInvoices.find((inv) => inv.id === selectedInvoice?.id) || selectedInvoice;
                    const modalCanSignAttachment = (attachment) =>
                      canSignAttachment(attachment, currentInv || selectedInvoice, user, activeSubstitutions);

                    return (
                      <div className="space-y-2">
                        {attachmentsToShow.map((attachment) => {
                          const isSigned = isAttachmentSigned(signaturesForModal, attachment.id);
                          return (
                          <div
                            key={attachment.id}
                            className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${
                              isSigned
                                ? 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                            }`}
                          >
                            <div className="flex flex-col flex-1 min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className={`h-4 w-4 flex-shrink-0 ${isSigned ? 'text-blue-600' : 'text-gray-500'}`} />
                                <span
                                  className={`text-sm truncate ${isSigned ? 'text-blue-700 font-medium' : 'text-gray-700'}`}
                                  title={attachment.originalFileName}
                                >
                                  {attachment.originalFileName}
                                </span>
                                {isSigned && (
                                  <CheckCircle className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" title="Podpisany" />
                                )}
                                {attachment.source === 'KSEF' && (
                                  <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 bg-amber-100 rounded" title="Załącznik z faktury KSeF">KSeF</span>
                                )}
                                <span className={`text-xs ${isSigned ? 'text-blue-600' : 'text-gray-500'}`}>
                                  ({(attachment.fileSize / 1024).toFixed(1)} KB)
                                </span>
                              </div>
                              {(attachment.addedByFirstName != null || attachment.addedByLastName != null) && (
                                <span className="text-[10px] text-gray-500 mt-0.5 ml-6">
                                  dodane przez: {[attachment.addedByFirstName, attachment.addedByLastName].filter(Boolean).join(' ')}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <button
                                type="button"
                                onClick={async () => {
                                  setLoadingAttachmentPreview(true);
                                  setSelectedAttachmentPreview(attachment);
                                  try {
                                    const response = await api.get(`/invoices/attachments/${attachment.id}/download`, {
                                      responseType: 'blob'
                                    });
                                    const blob = new Blob([response.data], {
                                      type: response.headers['content-type'] || 'application/pdf'
                                    });
                                    const url = window.URL.createObjectURL(blob);
                                    setAttachmentPreviewUrl(url);
                                  } catch (error) {
                                    console.error('Błąd podczas pobierania załącznika:', error);
                                    toast.error('Nie udało się pobrać załącznika');
                                    setSelectedAttachmentPreview(null);
                                  } finally {
                                    setLoadingAttachmentPreview(false);
                                  }
                                }}
                                className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                title="Podgląd załącznika"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {modalCanSignAttachment(attachment) && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSigningInvoiceId(selectedInvoice.id);
                                    setSigningAttachmentId(attachment.id);
                                    setShowSignModal(true);
                                  }}
                                  className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                                  title={isSigned ? 'Podpisz załącznik ponownie' : 'Podpisz załącznik'}
                                >
                                  <PenTool className="h-4 w-4" />
                                </button>
                              )}
                              {canReplaceAttachment && isSigned && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenReplaceModal(attachment, selectedInvoice.id, signaturesForModal)}
                                  className="flex items-center gap-1 px-2 py-1 text-orange-700 hover:text-orange-800 hover:bg-orange-50 rounded-lg border border-orange-200 bg-orange-50/50 transition-colors text-xs font-medium"
                                  title="Podmień załącznik (admin)"
                                >
                                  <RefreshCw className="h-3.5 w-3.5" />
                                  Podmień
                                </button>
                              )}
                              {!isSigned &&
                                !user?.isPreviewMode &&
                                (attachment.userId === user?.id ||
                                  user?.role === 'SEKRETARIAT' ||
                                  user?.role === 'SUPERADMIN') && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteAttachmentClick(attachment.id, selectedInvoice.id)}
                                  className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                  title="Usuń załącznik"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>
              </div>
              
              {/* Prawa strona - Podgląd faktury/załączników */}
              <div className="w-[45%] overflow-hidden flex flex-col bg-gray-50">
                <div className="px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <Eye className="h-4 w-4 text-gray-600" />
                      {selectedAttachmentPreview ? 'Podgląd załącznika' : 'Podgląd faktury'}
                    </h4>
                    {selectedAttachmentPreview && (
                      <button
                        onClick={() => {
                          setSelectedAttachmentPreview(null);
                          if (attachmentPreviewUrl && attachmentPreviewUrl.startsWith('blob:')) {
                            window.URL.revokeObjectURL(attachmentPreviewUrl);
                          }
                          setAttachmentPreviewUrl(null);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        ← Powrót do faktury
                      </button>
                    )}
                  </div>
                  {/* Tabs dla załączników */}
                  {(() => {
                    // Użyj attachments jeśli są, w przeciwnym razie użyj invoiceAttachments
                    const attachmentsToShow = (attachments && attachments.length > 0) 
                      ? attachments 
                      : (invoiceAttachments[selectedInvoice?.id] || []);
                    
                    if (attachmentsToShow.length === 0 || selectedAttachmentPreview) {
                      return null;
                    }
                    
                    const tabSignatures = invoiceSignatures[selectedInvoice?.id] || [];
                    return (
                      <div className="mt-2 flex gap-2 overflow-x-auto">
                        {attachmentsToShow.map((attachment) => {
                          const tabSigned = isAttachmentSigned(tabSignatures, attachment.id);
                          return (
                          <button
                            key={attachment.id}
                            type="button"
                            onClick={async () => {
                              setLoadingAttachmentPreview(true);
                              setSelectedAttachmentPreview(attachment);
                              try {
                                const response = await api.get(`/invoices/attachments/${attachment.id}/download`, {
                                  responseType: 'blob'
                                });
                                const blob = new Blob([response.data], {
                                  type: response.headers['content-type'] || 'application/pdf'
                                });
                                const url = window.URL.createObjectURL(blob);
                                setAttachmentPreviewUrl(url);
                              } catch (error) {
                                console.error('Błąd podczas pobierania załącznika:', error);
                                toast.error('Nie udało się pobrać załącznika');
                                setSelectedAttachmentPreview(null);
                              } finally {
                                setLoadingAttachmentPreview(false);
                              }
                            }}
                            className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded border whitespace-nowrap ${
                              tabSigned
                                ? 'text-blue-800 bg-blue-50 border-blue-300 hover:bg-blue-100'
                                : 'text-gray-700 bg-gray-100 border-gray-300 hover:bg-gray-200'
                            }`}
                            title={attachment.originalFileName}
                          >
                            <FileText className={`h-3 w-3 ${tabSigned ? 'text-blue-600' : ''}`} />
                            <span className="truncate max-w-[120px]">{attachment.originalFileName}</span>
                            {attachment.source === 'KSEF' && (
                              <span className="flex-shrink-0 px-1 py-0.5 text-[9px] font-medium text-amber-800 bg-amber-100 rounded">KSeF</span>
                            )}
                          </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                <div className="flex-1 overflow-hidden">
                  {selectedAttachmentPreview ? (
                    // Podgląd załącznika
                    loadingAttachmentPreview ? (
                      <div className="flex items-center justify-center h-full">
                        <LoadingSpinner />
                      </div>
                    ) : attachmentPreviewUrl ? (
                      <iframe
                        src={attachmentPreviewUrl}
                        className="w-full h-full border-0"
                        title="Podgląd załącznika"
                        type="application/pdf"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full p-8">
                        <div className="text-center">
                          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">Nie udało się załadować podglądu załącznika</p>
                        </div>
                      </div>
                    )
                  ) : selectedInvoice && (selectedInvoice.filePath || selectedInvoice.ksefReference) ? (
                    // Podgląd faktury
                    loadingPreview ? (
                      <div className="flex items-center justify-center h-full">
                        <LoadingSpinner />
                      </div>
                    ) : invoicePreviewHtml ? (
                      <iframe
                        srcDoc={invoicePreviewHtml}
                        className="w-full h-full border-0"
                        title="Podgląd faktury"
                      />
                    ) : invoicePreviewUrl ? (
                      <iframe
                        src={invoicePreviewUrl}
                        className="w-full h-full border-0"
                        title="Podgląd faktury"
                        type="application/pdf"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full p-8">
                        <div className="text-center">
                          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">Nie udało się załadować podglądu faktury</p>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="flex items-center justify-center h-full p-8">
                      <div className="text-center">
                        <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">Brak podglądu dla tej faktury</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 flex-shrink-0">
              <button
                onClick={handleCancelEdit}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-150 flex items-center justify-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>Anuluj</span>
              </button>
              <button
                onClick={handleSaveDraft}
                disabled={saving}
                className="px-5 py-2.5 text-sm font-medium text-yellow-800 bg-yellow-50 border border-yellow-300 rounded-lg hover:bg-yellow-100 transition-colors duration-150 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zapisz to co wpisałeś, by wrócić w późniejszym terminie"
              >
                <Save className="h-4 w-4" />
                <span>{saving ? 'Zapisywanie...' : 'Zapisz roboczo'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Wskazanie osoby podpisującej załącznik przy przekazywaniu faktury */}
      <AttachmentSignWaiverModal
        isOpen={showWaiverModal}
        onClose={() => {
          setShowWaiverModal(false);
          setWaiverAttachments([]);
          setWaiverInvoice(null);
        }}
        invoiceId={waiverInvoice?.id}
        invoiceNumber={waiverInvoice?.number}
        attachments={waiverAttachments}
        users={users}
        currentUser={user}
        onSign={(attachment) => {
          setShowWaiverModal(false);
          setSigningInvoiceId(waiverInvoice?.id);
          setSigningAttachmentId(attachment.id);
          setShowSignModal(true);
        }}
        onSuccess={async (designatedSignerUserId) => {
          const invoice = waiverInvoice;
          setShowWaiverModal(false);
          setWaiverAttachments([]);
          setWaiverInvoice(null);
          if (!invoice) return;
          // Po wskazaniu osoby przekazujemy fakturę od razu do niej — bez dodatkowego okna.
          // Ewentualne zastępstwo wskazanej osoby rozwiązuje backend.
          try {
            await api.put(`/invoices/${invoice.id}`, {
              status: 'ASSIGNED',
              assignedUserId: designatedSignerUserId,
            });
            toast.success('Faktura przekazana do wskazanej osoby');
            await fetchInvoices();
            if (expandedInvoices.has(invoice.id)) {
              await fetchInvoiceHistory(invoice.id);
            }
          } catch (error) {
            const data = error?.response?.data;
            toast.error(data?.error || 'Błąd podczas przekazywania faktury');
            console.error('Error assigning invoice after waiver:', error);
          }
        }}
      />

      {/* Modal podpisywania dokumentu */}
      {showSignModal && signingInvoiceId && (
        <DocumentSignModal
          key={signingInvoiceId}
          isOpen={showSignModal}
          onClose={() => {
            setShowSignModal(false);
            setSigningInvoiceId(null);
            setSigningAttachmentId(null);
            setBatchSignQueue([]);
            setSelectedInvoiceIds([]);
          }}
          invoiceId={signingInvoiceId}
          attachmentId={signingAttachmentId}
          onSignAndAssign={batchSignQueue.length === 0 ? (async (signatureData) => {
              console.log('🚀 [DEBUG InvoicesByStatus] onSignAndAssign WYWOŁANY!', {
                signatureData,
                signingInvoiceId,
                hasSignatureData: !!signatureData,
                signatureId: signatureData?.signatureId
              });
              
              // Ta sama logika co onSuccess, ale dodatkowo otwieramy modal przypisania
              // Odśwież podpisy dla faktury
              try {
                const response = await api.get(`/documents/invoices/${signingInvoiceId}/signatures`);
                setInvoiceSignatures(prev => ({
                  ...prev,
                  [signingInvoiceId]: response.data || []
                }));
                
                if (signingAttachmentId) {
                  const attachmentsResponse = await api.get(`/invoices/${signingInvoiceId}/attachments`, {
                    timeout: 10000
                  });
                  const attData = attachmentsResponse.data || [];
                  setInvoiceAttachments(prev => ({
                    ...prev,
                    [signingInvoiceId]: attData
                  }));
                  if (showDescriptionModal && selectedInvoice?.id === signingInvoiceId) {
                    setAttachments(attData);
                  }
                }
              
                // Zapisz opis i documentSignatureId tylko przy podpisie FAKTURY (nie załącznika)
                if (signatureData && signatureData.signatureId && !signingAttachmentId) {
                  try {
                    const descResponse = await api.get(`/invoices/${signingInvoiceId}/descriptions`);
                    const userDesc = descResponse.data.find(d => d.userId === user?.id);
                    
                    const descriptionToSave = editForm.description || userDesc?.description || '';
                    const positionToSave = editForm.signedByPosition || userDesc?.signedByPosition || user?.position || '';
                    
                    const requestData = {
                      description: descriptionToSave,
                      signedByPosition: positionToSave,
                      documentSignatureId: signatureData.signatureId.toString()
                    };
                    
                    if (user?.canCheckFormalAccounting) {
                      requestData.formalnoRachunkowoChecked = editForm.formalnoRachunkowoChecked || false;
                      if (editForm.zatwierdzonoDo) {
                        requestData.zatwierdzonoDo = editForm.zatwierdzonoDo;
                      }
                      if (editForm.kwota) {
                        requestData.kwota = editForm.kwota.replace(',', '.');
                      }
                    }
                    // ZATWIERDZONO DO REALIZACJI - zapisuj zawsze gdy użytkownik zaznaczył (canAcceptInvoices), nie tylko przy canCheckFormalAccounting
                    if (editForm.zatwierdzonoDo) {
                      requestData.zatwierdzonoDo = editForm.zatwierdzonoDo;
                    }
                    
                    if (user?.canCheckMerit) {
                      requestData.metricalChecked = editForm.metricalChecked || false;
                      if (editForm.invoicePaymentType) {
                        requestData.invoicePaymentType = editForm.invoicePaymentType;
                      }
                    }
                    
                    if (user?.canDoPreliminaryCheck) {
                      requestData.preliminaryCheckDone = editForm.preliminaryCheckDone || false;
                    }
                    
                    // Dodaj pole ośrodka kosztów jeśli użytkownik ma uprawnienie
                    if (user?.canUseCostCenter) {
                      requestData.costCenter = editForm.costCenter ? editForm.costCenter.trim() : null;
                    }
                    requestData.isDotacja = editForm.isDotacja || false;
                    if (editForm.isDotacja) {
                      requestData.srodkiPowiat = editForm.srodkiPowiat ? editForm.srodkiPowiat.toString().replace(',', '.') : null;
                      requestData.srodkiSpZoz = editForm.srodkiSpZoz ? editForm.srodkiSpZoz.toString().replace(',', '.') : null;
                    }
                    
                    await api.patch(`/invoices/${signingInvoiceId}/describe`, requestData);
                    
                    // Odśwież opisy
                    const updatedDescResponse = await api.get(`/invoices/${signingInvoiceId}/descriptions`);
                    setInvoiceDescriptions(prev => ({
                      ...prev,
                      [signingInvoiceId]: updatedDescResponse.data || []
                    }));
                    
                    // Odśwież również faktury
                    await fetchInvoices();
                    
                    if (expandedInvoices.has(signingInvoiceId)) {
                      await fetchInvoiceHistory(signingInvoiceId);
                      const sigResponse = await api.get(`/documents/invoices/${signingInvoiceId}/signatures`);
                      setInvoiceSignatures(prev => ({
                        ...prev,
                        [signingInvoiceId]: sigResponse.data || []
                      }));
                    }
                  } catch (error) {
                    console.error('Błąd podczas zapisywania opisu:', error);
                  }
                }
                
                if (!signingAttachmentId) {
                  try {
                    const unsignedNames = await fetchUnsignedAttachmentNames(api, signingInvoiceId, user);
                    if (unsignedNames.length > 0) {
                      toast.error(unsignedAttachmentsToast(unsignedNames));
                      return;
                    }
                  } catch (checkErr) {
                    console.error('Błąd weryfikacji podpisów załączników:', checkErr);
                    toast.error('Nie udało się zweryfikować podpisów załączników.');
                    return;
                  }
                }

                // Znajdź fakturę i otwórz modal przypisania
                console.log('🔍 [DEBUG InvoicesByStatus] Próba otwarcia modala przypisania:', {
                  signingInvoiceId,
                  invoicesLength: allInvoices.length,
                  invoices: allInvoices.map(inv => ({ id: inv.id, number: inv.number }))
                });
                
                const invoice = allInvoices.find(inv => inv.id === signingInvoiceId);
                console.log('🔍 [DEBUG InvoicesByStatus] Znaleziona faktura:', invoice);
                
                if (invoice) {
                  console.log('✅ [DEBUG InvoicesByStatus] Otwieranie modala przypisania dla faktury:', invoice.id);
                  console.log('🔍 [DEBUG InvoicesByStatus] Przed setSelectedInvoice:', {
                    selectedInvoice: null,
                    showAssignModal: false
                  });
                  setSelectedInvoice(invoice);
                  setShowAssignModal(true);
                  console.log('🔍 [DEBUG InvoicesByStatus] Po setSelectedInvoice i setShowAssignModal');
                  
                  // Sprawdź stan po chwili
                  setTimeout(() => {
                    console.log('🔍 [DEBUG InvoicesByStatus] Stan po 200ms - sprawdzam czy modal się otworzył');
                  }, 200);
                } else {
                  console.warn('⚠️ [DEBUG InvoicesByStatus] Nie znaleziono faktury o ID:', signingInvoiceId);
                  // Spróbuj pobrać fakturę z API jeśli nie ma w liście
                  try {
                    const invoiceResponse = await api.get(`/invoices/${signingInvoiceId}`);
                    if (invoiceResponse.data) {
                      console.log('✅ [DEBUG InvoicesByStatus] Pobrano fakturę z API:', invoiceResponse.data.id);
                      setSelectedInvoice(invoiceResponse.data);
                      setShowAssignModal(true);
                    }
                  } catch (apiError) {
                    console.error('❌ [DEBUG InvoicesByStatus] Błąd podczas pobierania faktury z API:', apiError);
                    toast.error('Nie udało się otworzyć modala przypisania. Faktura została podpisana.');
                  }
                }
              } catch (error) {
                console.error('❌ [DEBUG InvoicesByStatus] Błąd podczas odświeżania podpisów:', error);
              }
            }) : undefined}
          onSuccess={async (signatureData) => {
              // Odśwież podpisy dla faktury - pobierz WSZYSTKIE podpisy
              try {
                const response = await api.get(`/documents/invoices/${signingInvoiceId}/signatures`);
                // Upewnij się, że zapisujemy wszystkie podpisy (nie tylko najnowszy)
                setInvoiceSignatures(prev => ({
                  ...prev,
                  [signingInvoiceId]: response.data || []
                }));
                
                // Jeśli podpisano załącznik, odśwież też załączniki
                if (signingAttachmentId) {
                  const attachmentsResponse = await api.get(`/invoices/${signingInvoiceId}/attachments`, {
                    timeout: 10000
                  });
                  const attData = attachmentsResponse.data || [];
                  setInvoiceAttachments(prev => ({
                    ...prev,
                    [signingInvoiceId]: attData
                  }));
                  if (showDescriptionModal && selectedInvoice?.id === signingInvoiceId) {
                    setAttachments(attData);
                  }
                }
              
              // Zapisz opis i documentSignatureId tylko przy podpisie FAKTURY (nie załącznika)
              if (signatureData && signatureData.signatureId && !signingAttachmentId) {
                try {
                  // Pobierz aktualny opis użytkownika (może być zapisany jako draft)
                  const descResponse = await api.get(`/invoices/${signingInvoiceId}/descriptions`);
                  const userDesc = descResponse.data.find(d => d.userId === user?.id);
                  
                  // Użyj istniejącego opisu jeśli jest, w przeciwnym razie użyj z formularza
                  const descriptionToSave = editForm.description || userDesc?.description || '';
                  const positionToSave = editForm.signedByPosition || userDesc?.signedByPosition || user?.position || '';
                  
                  // Zapisz opis i podpis - podpis jest jednoznaczny z zapisaniem
                  // Zawsze aktualizuj/utwórz opis z documentSignatureId, nawet jeśli opis był już zapisany jako draft
                  const requestData = {
                    description: descriptionToSave,
                    signedByPosition: positionToSave,
                    documentSignatureId: signatureData.signatureId.toString()
                  };
                  
                  // Dodaj pola księgowości jeśli użytkownik ma uprawnienie
                  if (user?.canCheckFormalAccounting) {
                    requestData.formalnoRachunkowoChecked = editForm.formalnoRachunkowoChecked || false;
                    if (editForm.zatwierdzonoDo) {
                      requestData.zatwierdzonoDo = editForm.zatwierdzonoDo;
                    }
                    if (editForm.kwota) {
                      requestData.kwota = editForm.kwota.replace(',', '.');
                    }
                  }
                  // ZATWIERDZONO DO REALIZACJI - zapisuj zawsze gdy użytkownik zaznaczył (canAcceptInvoices), nie tylko przy canCheckFormalAccounting
                  if (editForm.zatwierdzonoDo) {
                    requestData.zatwierdzonoDo = editForm.zatwierdzonoDo;
                  }
                  
                  // Dodaj pole merytoryczne jeśli użytkownik ma uprawnienia
                  if (user?.canCheckMerit) {
                    requestData.metricalChecked = editForm.metricalChecked || false;
                    if (editForm.invoicePaymentType) {
                      requestData.invoicePaymentType = editForm.invoicePaymentType;
                    }
                  }
                  
                  // Dodaj pole wstępnej kontroli jeśli użytkownik ma uprawnienia
                  if (user?.canDoPreliminaryCheck) {
                    requestData.preliminaryCheckDone = editForm.preliminaryCheckDone || false;
                  }
                  
                  // Dodaj pole ośrodka kosztów jeśli użytkownik ma uprawnienie - zawsze wysyłaj, nawet jeśli puste
                  console.log('🔍 [DEBUG podpisanie InvoicesByStatus] Sprawdzanie uprawnienia canUseCostCenter:', {
                    user: user,
                    canUseCostCenter: user?.canUseCostCenter,
                    userId: user?.id,
                    editFormCostCenter: editForm.costCenter
                  });
                  
                  if (user?.canUseCostCenter) {
                    requestData.costCenter = editForm.costCenter ? editForm.costCenter.trim() : null;
                    console.log('💰 [DEBUG podpisanie InvoicesByStatus] Wysyłanie costCenter:', requestData.costCenter);
                  } else {
                    console.warn('⚠️ [DEBUG podpisanie InvoicesByStatus] Użytkownik nie ma uprawnienia canUseCostCenter:', user?.canUseCostCenter);
                  }
                  requestData.isDotacja = editForm.isDotacja || false;
                  if (editForm.isDotacja) {
                    requestData.srodkiPowiat = editForm.srodkiPowiat ? editForm.srodkiPowiat.toString().replace(',', '.') : null;
                    requestData.srodkiSpZoz = editForm.srodkiSpZoz ? editForm.srodkiSpZoz.toString().replace(',', '.') : null;
                  }
                  
                  console.log('📤 [DEBUG podpisanie InvoicesByStatus] Wysyłane dane:', requestData);
                  await api.patch(`/invoices/${signingInvoiceId}/describe`, requestData);
                  
                  // Odśwież opisy
                  const updatedDescResponse = await api.get(`/invoices/${signingInvoiceId}/descriptions`);
                  setInvoiceDescriptions(prev => ({
                    ...prev,
                    [signingInvoiceId]: updatedDescResponse.data || []
                  }));
                  
                  // Odśwież również faktury, żeby zaktualizować widok
                  await fetchInvoices();
                  
                  // Jeśli faktura jest rozwinięta, odśwież historię i podpisy
                  if (expandedInvoices.has(signingInvoiceId)) {
                    await fetchInvoiceHistory(signingInvoiceId);
                    const sigResponse = await api.get(`/documents/invoices/${signingInvoiceId}/signatures`);
                    setInvoiceSignatures(prev => ({
                      ...prev,
                      [signingInvoiceId]: sigResponse.data || []
                    }));
                  }
                  
                  // Zamknij modal opisu faktury po udanym podpisaniu
                  setShowDescriptionModal(false);
                  setSelectedInvoice(null);
                  setEditForm({
                    description: '',
                    signedByPosition: '',
                    signatureImage: null
                  });
                  
                  toast.success('Faktura została podpisana i opis zapisany');
                } catch (error) {
                  console.error('Błąd podczas zapisywania opisu i documentSignatureId:', error);
                  toast.error('Błąd podczas zapisywania opisu');
                }
              }
              
              // Jeśli modal opisu faktury jest otwarty, odśwież podpisy w nim również
              if (selectedInvoice && selectedInvoice.id === signingInvoiceId) {
                try {
                  const refreshResponse = await api.get(`/documents/invoices/${signingInvoiceId}/signatures`);
                  setInvoiceSignatures(prev => ({
                    ...prev,
                    [signingInvoiceId]: refreshResponse.data || []
                  }));
                } catch (error) {
                  console.error('Błąd podczas odświeżania podpisów w modalu:', error);
                }
              }
              
              // Odśwież listę faktur
              await fetchInvoices();

              // Obsługa masowego podpisywania – jeśli w kolejce są kolejne faktury, otwórz następną
              setBatchSignQueue(prev => {
                const remaining = prev.filter(id => id !== signingInvoiceId);
                if (remaining.length > 0) {
                  setSigningInvoiceId(remaining[0]);
                  toast.success(`Podpisano. Zostało ${remaining.length} ${remaining.length === 1 ? 'faktura' : remaining.length < 5 ? 'faktury' : 'faktur'}`);
                  return remaining;
                }
                setShowSignModal(false);
                setSigningInvoiceId(null);
                setSelectedInvoiceIds([]);
                toast.success('Wszystkie zaznaczone faktury zostały podpisane');
                return [];
              });
            } catch (error) {
              console.error('Błąd podczas pobierania podpisów:', error);
            }
          }}
        />
      )}

      {/* Modal historii faktury */}
      {showHistoryModal && modalInvoiceId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full border border-gray-200 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex justify-between items-center">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                  <History className="h-5 w-5 text-gray-600" />
                  Historia faktury
                </h3>
                <button
                  onClick={() => {
                    setShowHistoryModal(false);
                    setModalInvoiceId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {invoiceHistory[modalInvoiceId] && invoiceHistory[modalInvoiceId].length > 0 ? (
                <div className="space-y-0">
                  {invoiceHistory[modalInvoiceId].map((historyItem, idx) => {
                    const isLast = idx === invoiceHistory[modalInvoiceId].length - 1;
                    const actionType = historyItem.actionType;
                    
                    // Określ ikonę i kolory na podstawie typu akcji
                    let icon, bgColor, borderColor, textColor, iconColor;
                    
                    if (actionType === 'CREATED') {
                      icon = FilePlus;
                      bgColor = 'bg-blue-50';
                      borderColor = 'border-blue-200';
                      textColor = 'text-blue-900';
                      iconColor = 'text-blue-600';
                    } else if (actionType === 'ASSIGNED') {
                      icon = Send;
                      bgColor = 'bg-purple-50';
                      borderColor = 'border-purple-200';
                      textColor = 'text-purple-900';
                      iconColor = 'text-purple-600';
                    } else if (actionType === 'RETURNED') {
                      icon = CornerUpLeft;
                      bgColor = 'bg-orange-50';
                      borderColor = 'border-orange-200';
                      textColor = 'text-orange-900';
                      iconColor = 'text-orange-600';
                    } else if (actionType === 'MARKED_SUSPICIOUS') {
                      icon = AlertTriangle;
                      bgColor = 'bg-orange-50';
                      borderColor = 'border-orange-300';
                      textColor = 'text-orange-900';
                      iconColor = 'text-orange-700';
                    } else if (actionType === 'UNMARKED_SUSPICIOUS') {
                      icon = AlertTriangle;
                      bgColor = 'bg-gray-50';
                      borderColor = 'border-gray-200';
                      textColor = 'text-gray-900';
                      iconColor = 'text-gray-500';
                    } else if ((actionType === 'UPDATED' || (actionType && actionType.toUpperCase() === 'UPDATED')) && historyItem.actionDescription && (historyItem.actionDescription.includes('załącznik') || historyItem.actionDescription.toLowerCase().includes('zalacznik'))) {
                      icon = PenTool;
                      bgColor = 'bg-blue-50';
                      borderColor = 'border-blue-200';
                      textColor = 'text-blue-900';
                      iconColor = 'text-blue-600';
                    } else if (actionType === 'UPDATED' && historyItem.actionDescription && historyItem.actionDescription.includes('opisał i podpisał')) {
                      icon = PenTool;
                      bgColor = 'bg-green-50';
                      borderColor = 'border-green-200';
                      textColor = 'text-green-900';
                      iconColor = 'text-green-600';
                    } else if (actionType === 'STATUS_CHANGED') {
                      icon = UserCheck;
                      bgColor = 'bg-amber-50';
                      borderColor = 'border-amber-200';
                      textColor = 'text-amber-900';
                      iconColor = 'text-amber-600';
                    } else {
                      icon = History;
                      bgColor = 'bg-gray-50';
                      borderColor = 'border-gray-200';
                      textColor = 'text-gray-900';
                      iconColor = 'text-gray-600';
                    }
                    
                    const IconComponent = icon;
                    
                    return (
                      <React.Fragment key={historyItem.id}>
                        <div className={`flex items-start p-4 rounded-lg border ${bgColor} ${borderColor}`}>
                          <div className={`flex-shrink-0 ${iconColor} mr-3 mt-0.5`}>
                            <IconComponent className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium break-words ${textColor}`}>
                              {historyItem.actionDescription}
                            </div>
                            {historyItem.performedByUserName && (
                              <div className={`text-xs mt-1 flex items-center ${textColor} opacity-75`}>
                                <User className="h-3 w-3 mr-1" />
                                {historyItem.performedByUserName}
                              </div>
                            )}
                            {historyItem.assignedToUserName && (
                              <div className={`text-xs mt-1 flex items-center ${textColor} opacity-75`}>
                                <ArrowRight className="h-3 w-3 mr-1" />
                                {actionType === 'RETURNED' ? 'Zwrócono do:' : 'Przekazano do:'} {historyItem.assignedToUserName}
                              </div>
                            )}
                            <div className={`text-xs mt-1.5 ${textColor} opacity-60`}>
                              {formatHistoryDate(historyItem.createdAt)}
                            </div>
                          </div>
                        </div>
                        {!isLast && (
                          <div className="flex items-center justify-center py-1">
                            <ArrowDown className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <History className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Brak historii dla tej faktury</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal opisów i podpisów użytkowników */}
      {showDescriptionsModal && modalInvoiceId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full border border-gray-200 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex justify-between items-center">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                  <FileCheck className="h-5 w-5 text-blue-600" />
                  Opisy i podpisy użytkowników
                </h3>
                <button
                  onClick={() => {
                    setShowDescriptionsModal(false);
                    setModalInvoiceId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {invoiceDescriptions[modalInvoiceId] && invoiceDescriptions[modalInvoiceId].length > 0 ? (
                <div className="space-y-4">
                  {invoiceDescriptions[modalInvoiceId].map((desc) => {
                    // Znajdź podpis powiązany z tym opisem
                    const signature = invoiceSignatures[modalInvoiceId]?.find(sig => 
                      sig.id === desc.documentSignatureId || 
                      (sig.user?.id && String(sig.user.id) === String(desc.userId) &&
                       Math.abs(new Date(sig.signedAt) - new Date(desc.createdAt)) < 60000)
                    );
                    
                    return (
                      <div key={desc.id} className="border-l-4 border-blue-500 pl-4 py-3 bg-gray-50 rounded-lg">
                        {signature ? (
                          <>
                            <div className="text-sm font-normal text-gray-900 mb-2">
                              {desc.metricalChecked
                                ? 'SPRAWDZONO POD WZGLĘDEM MERYTORYCZNYM'
                                : desc.formalnoRachunkowoChecked
                                  ? 'SPRAWDZONO POD WZGLĘDEM FORMALNO-RACHUNKOWYM'
                                  : desc.preliminaryCheckDone
                                    ? 'DOKONANO WSTĘPNEJ KONTROLI'
                                    : desc.zatwierdzonoDo === 'REALIZACJI'
                                      ? 'ZATWIERDZONO DO REALIZACJI'
                                      : 'Podpisano'}{' '}
                              przez{' '}
                              {signature.user ? `${signature.user.firstName} ${signature.user.lastName}` : desc.userName || desc.userEmail},{' '}
                              {new Date(signature.signedAt).toLocaleDateString('pl-PL', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric'
                              })},{' '}
                              {new Date(signature.signedAt).toLocaleTimeString('pl-PL', { 
                                hour: '2-digit',
                                minute: '2-digit'
                              })}{' '}
                              - podpisano elektronicznie
                            </div>
                            {/* Typ płatności */}
                            {(desc.invoicePaymentType === 'CASH' || desc.invoicePaymentType === 'TRANSFER') && (
                              <div className="text-sm font-normal text-gray-700 mt-2">
                                Typ płatności: <strong>{desc.invoicePaymentType === 'CASH' ? 'Faktura gotówkowa' : 'Przelew'}</strong>
                              </div>
                            )}
                            {/* Opis faktury */}
                            {desc.description && (
                              <div className="text-sm font-normal text-gray-700 mt-2">
                                Opis: {desc.description}
                                {desc.costCenter && (
                                  <span className="block mt-1 font-semibold text-yellow-700">
                                    Ośrodek kosztów: {desc.costCenter}
                                  </span>
                                )}
                              </div>
                            )}
                            <DotacjaDisplay desc={desc} variant="modal" />
                          </>
                        ) : (
                          <>
                            {/* Jeśli nie ma podpisu, wyświetl typ płatności i opis */}
                            {(desc.invoicePaymentType === 'CASH' || desc.invoicePaymentType === 'TRANSFER') && (
                              <div className="text-sm font-normal text-gray-700">
                                Typ płatności: <strong>{desc.invoicePaymentType === 'CASH' ? 'Faktura gotówkowa' : 'Przelew'}</strong>
                              </div>
                            )}
                            {desc.description && (
                              <div className="text-sm font-normal text-gray-700 mt-2">
                                Opis: {desc.description}
                                {desc.costCenter && (
                                  <span className="block mt-1 font-semibold text-yellow-700">
                                    Ośrodek kosztów: {desc.costCenter}
                                  </span>
                                )}
                              </div>
                            )}
                            <DotacjaDisplay desc={desc} variant="modal" />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Brak opisów i podpisów dla tej faktury</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal podglądu faktury XML */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full border border-gray-200 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex justify-between items-center">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-gray-600" />
                  {previewTitle}
                </h3>
                <button
                  onClick={() => {
                    setShowPreviewModal(false);
                    setPreviewContent('');
                    setPreviewTitle('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-0">
              <iframe
                srcDoc={previewContent}
                className="w-full h-full min-h-[600px] border-0"
                title="Podgląd faktury"
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal podglądu A4 - pełna ścieżka podpisów i opisów */}
      {showA4PreviewModal && a4PreviewInvoiceId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-1 sm:p-2 md:p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-full sm:max-w-[95vw] md:max-w-[210mm] border border-gray-200 overflow-hidden flex flex-col my-2 sm:my-4" style={{ maxHeight: 'calc(100vh - 2rem)', minHeight: 'auto' }}>
            <div className="p-3 sm:p-4 md:p-6 border-b border-gray-200 flex-shrink-0 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2 flex-shrink-0">
                  <Printer className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                  <span className="break-words">Podgląd A4 - Pełna ścieżka podpisów i opisów</span>
                </h3>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => window.print()}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center gap-1.5 sm:gap-2 flex-shrink-0"
                  >
                    <Printer className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                    <span className="hidden sm:inline">Drukuj</span>
                    <span className="sm:hidden">Drukuj</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowA4PreviewModal(false);
                      setA4PreviewInvoiceId(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 p-1"
                    title="Zamknij"
                  >
                    <X className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 print:p-8" style={{ minHeight: 'auto' }}>
              {(() => {
                const invoiceId = a4PreviewInvoiceId;
                const signatures = invoiceSignatures[invoiceId] || [];
                const descriptions = invoiceDescriptions[invoiceId] || [];
                
                // Opis-centric: iterujemy po opisach (jak "Opisy i podpisy użytkowników"), zachowując styl A4
                const combinedItems = [];
                descriptions.forEach(desc => {
                  const signature = signatures.find(sig =>
                    (sig.id && desc.documentSignatureId && String(desc.documentSignatureId) === String(sig.id)) ||
                    (sig.user?.id && desc.userId && String(desc.userId) === String(sig.user.id) &&
                     Math.abs(new Date(desc.createdAt) - new Date(sig.signedAt)) < 60000)
                  );
                  const timestamp = signature ? new Date(signature.signedAt) : new Date(desc.createdAt);
                  combinedItems.push({
                    type: signature ? 'signature' : 'description',
                    data: signature || desc,
                    description: desc,
                    signature: signature,
                    timestamp
                  });
                });
                combinedItems.sort((a, b) => a.timestamp - b.timestamp);
                
                if (combinedItems.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <FileCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">Brak podpisów i opisów dla tej faktury</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-6">
                    {/* Nagłówek faktury */}
                    {allInvoices.find(inv => inv.id === invoiceId) && (
                      <div className="mb-4 pb-3 border-b border-gray-300 print:mb-2 print:pb-1 print:border-b print:border-gray-400">
                        <h2 className="text-lg font-bold text-gray-900 mb-1 print:text-sm print:mb-0.5 print:font-bold">
                          Faktura: {allInvoices.find(inv => inv.id === invoiceId)?.number}
                        </h2>
                        {allInvoices.find(inv => inv.id === invoiceId)?.supplierName && (
                          <p className="text-sm text-gray-600 print:text-[10px] print:text-gray-700">
                            Dostawca: {allInvoices.find(inv => inv.id === invoiceId)?.supplierName}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Podpisy elektroniczne - kompaktowy layout pionowy */}
                    <div className="mb-6 print:mb-2 print:break-inside-avoid">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 print:mb-1 print:text-sm print:font-bold">Podpisy elektroniczne</h3>
                      <div className="space-y-2 print:space-y-1">
                        {combinedItems.map((item, idx) => {
                          if (item.type === 'signature') {
                            const sig = item.data;
                            const desc = item.description;
                            return (
                              <div key={`sig-${sig.id || idx}`} className="border-l-4 border-green-500 bg-gray-50 print:bg-white print:border-l-2 rounded-r print:rounded-none p-2 print:p-1.5 print:border print:border-gray-300">
                                <div className="flex items-start gap-2 print:gap-1">
                                  {/* Numer kolejności */}
                                  <div className="flex-shrink-0 w-6 h-6 print:w-5 print:h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs print:text-[10px] font-bold print:border print:border-gray-400">
                                    {idx + 1}
                                  </div>
                                  
                                  {/* Strzałka do następnego */}
                                  {idx < combinedItems.length - 1 && (
                                    <div className="flex-shrink-0 flex items-center print:hidden">
                                      <ArrowRight className="h-4 w-4 text-green-600" />
                                    </div>
                                  )}
                                  
                                  {/* Zawartość podpisu */}
                                  <div className="flex-1 min-w-0">
                                    {/* SPRAWDZONO POD WZGLĘDEM MERYTORYCZNYM przez [imię nazwisko], [data], [godzina] - podpisano elektronicznie */}
                                    {desc && desc.metricalChecked ? (
                                      <div className="text-sm print:text-xs font-normal text-gray-900 mb-1 print:mb-0.5">
                                        SPRAWDZONO POD WZGLĘDEM MERYTORYCZNYM przez{' '}
                                        {sig.user ? `${sig.user.firstName} ${sig.user.lastName}` : 'nieznany użytkownik'},{' '}
                                        {new Date(sig.signedAt).toLocaleDateString('pl-PL', { 
                                          day: '2-digit', 
                                          month: '2-digit', 
                                          year: 'numeric'
                                        })},{' '}
                                        {new Date(sig.signedAt).toLocaleTimeString('pl-PL', { 
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}{' '}
                                        - podpisano elektronicznie
                                      </div>
                                    ) : desc && desc.preliminaryCheckDone ? (
                                      <div className="text-sm print:text-xs font-normal text-gray-900 mb-1 print:mb-0.5">
                                        DOKONANO WSTĘPNEJ KONTROLI przez{' '}
                                        {sig.user ? `${sig.user.firstName} ${sig.user.lastName}` : 'nieznany użytkownik'},{' '}
                                        {new Date(sig.signedAt).toLocaleDateString('pl-PL', { 
                                          day: '2-digit', 
                                          month: '2-digit', 
                                          year: 'numeric'
                                        })},{' '}
                                        {new Date(sig.signedAt).toLocaleTimeString('pl-PL', { 
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}{' '}
                                        - podpisano elektronicznie
                                      </div>
                                    ) : desc && desc.formalnoRachunkowoChecked ? (
                                      <div className="text-sm print:text-xs font-normal text-gray-900 mb-1 print:mb-0.5">
                                        SPRAWDZONO POD WZGLĘDEM FORMALNO-RACHUNKOWYM przez{' '}
                                        {sig.user ? `${sig.user.firstName} ${sig.user.lastName}` : 'nieznany użytkownik'},{' '}
                                        {new Date(sig.signedAt).toLocaleDateString('pl-PL', { 
                                          day: '2-digit', 
                                          month: '2-digit', 
                                          year: 'numeric'
                                        })},{' '}
                                        {new Date(sig.signedAt).toLocaleTimeString('pl-PL', { 
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}{' '}
                                        - podpisano elektronicznie
                                      </div>
                                    ) : desc && desc.zatwierdzonoDo === 'REALIZACJI' ? (
                                      <div className="text-sm print:text-xs font-normal text-red-700 mb-1 print:mb-0.5">
                                        ZATWIERDZONO DO REALIZACJI przez{' '}
                                        {sig.user ? `${sig.user.firstName} ${sig.user.lastName}` : 'nieznany użytkownik'},{' '}
                                        {new Date(sig.signedAt).toLocaleDateString('pl-PL', { 
                                          day: '2-digit', 
                                          month: '2-digit', 
                                          year: 'numeric'
                                        })},{' '}
                                        {new Date(sig.signedAt).toLocaleTimeString('pl-PL', { 
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}{' '}
                                        - podpisano elektronicznie
                                      </div>
                                    ) : sig.attachmentFileName ? (
                                      <div className="text-sm print:text-xs font-normal text-gray-900 mb-1 print:mb-0.5">
                                        Podpisano załącznik „{sig.attachmentFileName}” przez{' '}
                                        {sig.user ? `${sig.user.firstName} ${sig.user.lastName}` : 'nieznany użytkownik'},{' '}
                                        {new Date(sig.signedAt).toLocaleDateString('pl-PL', { 
                                          day: '2-digit', 
                                          month: '2-digit', 
                                          year: 'numeric'
                                        })},{' '}
                                        {new Date(sig.signedAt).toLocaleTimeString('pl-PL', { 
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}{' '}
                                        - podpisano elektronicznie
                                      </div>
                                    ) : (
                                      <div className="text-sm print:text-xs font-normal text-gray-900 mb-1 print:mb-0.5">
                                        Podpisano przez{' '}
                                        {sig.user ? `${sig.user.firstName} ${sig.user.lastName}` : 'nieznany użytkownik'},{' '}
                                        {new Date(sig.signedAt).toLocaleDateString('pl-PL', { 
                                          day: '2-digit', 
                                          month: '2-digit', 
                                          year: 'numeric'
                                        })},{' '}
                                        {new Date(sig.signedAt).toLocaleTimeString('pl-PL', { 
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}{' '}
                                        - podpisano elektronicznie
                                      </div>
                                    )}
                                    
                                    {/* ZATWIERDZONO DO REALIZACJI - pod podpisem (także bez formalno) */}
                                    {desc && desc.zatwierdzonoDo === 'REALIZACJI' && (
                                      <div className="mt-2 print:mt-1 pt-2 print:pt-1 border-t border-emerald-400 print:border-emerald-500">
                                        <div className="text-xs print:text-[10px] font-semibold text-purple-700 print:text-purple-800">✓ ZATWIERDZONO DO REALIZACJI</div>
                                      </div>
                                    )}
                                    
                                    {/* Informacje księgowości */}
                                    {desc && desc.formalnoRachunkowoChecked && (
                                      <div className="mt-2 print:mt-1 pt-2 print:pt-1 border-t border-green-300 print:border-t print:border-gray-200">
                                        {desc.zatwierdzonoDo && desc.zatwierdzonoDo !== 'REALIZACJI' && (
                                          <div className="text-xs print:text-[10px] text-gray-700 font-normal mb-1 print:mb-0.5">
                                            ZATWIERDZONO DO: <strong>{desc.zatwierdzonoDo}</strong>
                                          </div>
                                        )}
                                        {desc.kwota && (
                                          <div className="text-xs print:text-[10px] text-gray-700 font-normal mb-1 print:mb-0.5">
                                            KWOTĘ: <strong>{desc.kwota} PLN</strong>
                                            {desc.kwotaSlownie && (
                                              <span className="ml-2 text-gray-600">({desc.kwotaSlownie})</span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Typ płatności – dla podpisów merytorycznych */}
                                    {desc && (desc.invoicePaymentType === 'CASH' || desc.invoicePaymentType === 'TRANSFER') && (
                                      <div className="mt-2 print:mt-1 pt-2 print:pt-1 border-t border-gray-300 print:border-t print:border-gray-200">
                                        <div className="text-xs print:text-[10px] text-gray-700 font-normal">
                                          Typ płatności: <strong>{desc.invoicePaymentType === 'CASH' ? 'Faktura gotówkowa' : 'Przelew'}</strong>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Opis faktury */}
                                    {desc && desc.description && (
                                      <div className="mt-2 print:mt-1 pt-2 print:pt-1 border-t border-gray-300 print:border-t print:border-gray-200">
                                        <div className="text-xs print:text-[10px] text-gray-700 font-normal break-words">
                                          Opis: {desc.description}
                                          {desc.costCenter && (
                                            <span className="block mt-1 print:mt-0.5 font-semibold text-yellow-700 print:text-yellow-800">
                                              Ośrodek kosztów: {desc.costCenter}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    <DotacjaDisplay desc={desc} variant="print" />
                                  </div>
                                </div>
                              </div>
                            );
                          } else if (item.type === 'description') {
                            const desc = item.data;
                            return (
                              <div key={`desc-${desc.id || idx}`} className="border-l-4 border-blue-500 bg-gray-50 print:bg-white print:border-l-2 rounded-r print:rounded-none p-2 print:p-1.5 print:border print:border-gray-300">
                                <div className="flex items-start gap-2 print:gap-1">
                                  <div className="flex-shrink-0 w-6 h-6 print:w-5 print:h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs print:text-[10px] font-bold print:border print:border-gray-400">
                                    {idx + 1}
                                  </div>
                                  
                                  {idx < combinedItems.length - 1 && (
                                    <div className="flex-shrink-0 flex items-center print:hidden">
                                      <ArrowRight className="h-4 w-4 text-blue-600" />
                                    </div>
                                  )}
                                  
                                  <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-baseline gap-2 print:gap-1 print:flex-col print:items-start">
                                      {desc.userName && (
                                        <div className="font-semibold text-sm print:text-xs print:font-bold text-gray-900">
                                          {desc.userName}
                                        </div>
                                      )}
                                      <div className="text-xs print:text-[10px] text-gray-600">
                                        {new Date(desc.createdAt).toLocaleString('pl-PL', { 
                                          day: '2-digit', 
                                          month: '2-digit', 
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </div>
                                    </div>
                                    
                                    {/* ZATWIERDZONO DO REALIZACJI - pod podpisem (także bez formalno) */}
                                    {desc.zatwierdzonoDo === 'REALIZACJI' && (
                                      <div className="mt-1.5 print:mt-1 pt-1.5 print:pt-1 border-t border-emerald-400 print:border-emerald-500">
                                        <div className="text-xs print:text-[10px] font-semibold text-purple-700 print:text-purple-800">✓ ZATWIERDZONO DO REALIZACJI</div>
                                      </div>
                                    )}
                                    
                                    {/* Informacje księgowości */}
                                    {desc.formalnoRachunkowoChecked && (
                                      <div className="mt-1.5 print:mt-1 pt-1.5 print:pt-1 border-t border-green-300 print:border-t print:border-gray-200">
                                        <div className="text-xs print:text-[10px] text-gray-700 font-semibold mb-1 print:mb-0.5">
                                          SPRAWDZONO POD WZGLĘDEM FORMALNO-RACHUNKOWYM
                                        </div>
                                        {desc.zatwierdzonoDo && desc.zatwierdzonoDo !== 'REALIZACJI' && (
                                          <div className="text-xs print:text-[10px] text-gray-700 font-normal mb-0.5">
                                            ZATWIERDZONO DO: <strong>{desc.zatwierdzonoDo}</strong>
                                          </div>
                                        )}
                                        {desc.kwota && (
                                          <div className="text-xs print:text-[10px] text-gray-700 font-normal mb-0.5">
                                            KWOTĘ: <strong>{desc.kwota} PLN</strong>
                                            {desc.kwotaSlownie && (
                                              <span className="ml-2 text-gray-600">({desc.kwotaSlownie})</span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Informacje merytoryczne */}
                                    {desc.metricalChecked && (
                                      <div className="mt-1.5 print:mt-1 pt-1.5 print:pt-1 border-t border-yellow-300 print:border-t print:border-gray-200">
                                        <div className="text-xs print:text-[10px] text-gray-700 font-semibold mb-0.5">
                                          SPRAWDZONO POD WZGLĘDEM MERYTORYCZNYM
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Informacje wstępnej kontroli */}
                                    {desc.preliminaryCheckDone && (
                                      <div className="mt-1.5 print:mt-1 pt-1.5 print:pt-1 border-t border-blue-300 print:border-t print:border-gray-200">
                                        <div className="text-xs print:text-[10px] text-gray-700 font-semibold mb-0.5">
                                          DOKONANO WSTĘPNEJ KONTROLI
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Typ płatności */}
                                    {(desc.invoicePaymentType === 'CASH' || desc.invoicePaymentType === 'TRANSFER') && (
                                      <div className="mt-1.5 print:mt-1 pt-1.5 print:pt-1 border-t border-gray-300 print:border-t print:border-gray-200">
                                        <div className="text-xs print:text-[10px] text-gray-700 font-normal">
                                          Typ płatności: <strong>{desc.invoicePaymentType === 'CASH' ? 'Faktura gotówkowa' : 'Przelew'}</strong>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {desc.description && (
                                      <div className="mt-1.5 print:mt-1 pt-1.5 print:pt-1 border-t border-gray-300 print:border-t print:border-gray-200">
                                        <div className="text-xs print:text-[10px] text-gray-700 font-medium print:font-semibold mb-0.5">
                                          Opis:
                                        </div>
                                        <div className="text-xs print:text-[10px] text-gray-600 leading-tight print:leading-snug break-words">
                                          {desc.description}
                                          {desc.costCenter && (
                                            <span className="block mt-1 print:mt-0.5 font-semibold text-yellow-700 print:text-yellow-800">
                                              Ośrodek kosztów: {desc.costCenter}
                                            </span>
                                          )}
                                        </div>
                                        {desc.signedByPosition && (
                                          <div className="text-xs print:text-[10px] text-gray-500 mt-0.5 print:mt-0">
                                            {desc.signedByPosition}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    <DotacjaDisplay desc={desc} variant="print" />
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Dictionary Manager Modal */}
      <DictionaryManager
        isOpen={showDictionaryManager}
        onClose={() => setShowDictionaryManager(false)}
        onSelectPhrase={(phrase) => {
          setEditForm({ ...editForm, description: editForm.description + (editForm.description ? ' ' : '') + phrase });
          setShowDictionaryManager(false);
          toast.success('Frazę dodano do opisu');
        }}
      />
    </div>
  );
};

export default InvoicesByStatus;
